// Molecules — feature components built from atoms in components.tsx.

import React, { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SlotStageMap } from "./SlotStageMap";
import {
  BrandMark,
  CtxBar,
  Empty,
  fmtTokenCount,
  Icon,
  LivePill,
  Panel,
  PillSelect,
  PROVIDER_DISPLAY,
  ProviderBadge,
  StatusBadge,
  Tabs,
  TokenUsageBadge,
  Toggle,
  type PillOption,
} from "./components";
// Lazy: these two are reused unchanged inside popovers below (FleetKpisMenu/
// FleetGitMenu) that only mount them once the operator opens the popover — see
// ao_control_plane_read_models_and_request_path_2026_09_04.md [UI] P0. A plain static
// import here would pin both pages (and FleetKpis's recharts usage) into the initial
// bundle regardless of App.tsx's own route-level split, since layout.tsx is itself
// eagerly loaded (TopBar).
const FleetGit = React.lazy(() => import("./FleetGit").then((m) => ({ default: m.FleetGit })));
const FleetKpis = React.lazy(() => import("./FleetKpis").then((m) => ({ default: m.FleetKpis })));
import { fmtCeiling, fmtTpm } from "./GeminiCapacityPanel";
import { isBenignRecycle, isCompletionRecycle } from "./deathRecycle";
import { MatchedSignatureChips, parseTranscriptExcerpt, TranscriptExcerptBlocks } from "./TranscriptExcerptView";
import { fmtRatioOrDash, fmtTokenAvgOrDash, fmtTokenCountOrDash, fmtUsdOrDash } from "./TaskUsageWindows";
import {
  fmtAgo,
  fmtClock,
  fmtDate,
  fmtDuration,
  fmtDurationBetween,
  fmtIn,
  formatBytes,
  humanOperatorDisplayName,
  humanOperatorSlotId,
  modelBadgeClass,
  sortRolesForDisplay,
  summarizeRegistry,
  ctxArithmetic,
} from "./utils";
// Scoped single-escalation path builder — the escalation cards link to the
// per-record view (`/escalation/{id}`), the same surface the Slack alert links to.
import { scopedEscalationPath } from "./EscalationDetail";
// Frontend version is read from package.json (Vite/TS resolveJsonModule) so the
// top-bar pill self-updates instead of drifting from a hardcoded literal.
import pkg from "../package.json";
import { ACCOUNT_TIERS } from "./types";
import type {
  AccountStatus,
  AccountView,
  ActivityEntry,
  AgentKind,
  AgentRole,
  AgentView,
  AuthedUser,
  BackendConfig,
  BacklogCollisionView,
  BacklogSummary as BacklogSummaryData,
  BlockedRuling,
  BlockedView,
  ChatMessage,
  ContextPressure,
  DeepSeekUsageSummary,
  DispatchPauseStatusView,
  EscalationView,
  FleetFreezeStatusView,
  PrerequisiteView,
  FromRole,
  LastRollbackView,
  Mode,
  NodeHealthView,
  PmCloneView,
  RepoStatus,
  RoleView,
  SlotGitStatus,
  SlotView,
  ScheduledDispatchStatusView,
  ScheduledJobRunView,
  StateResponse,
} from "./types";

// ─── git-status badge (per-slot drift indicator) ─────────────────────────

// Single "how long red before it's real" threshold, shared with the Slack
// pager (worker_liveness/_git_alerts.GIT_RED_SUSTAIN_S) via StateResponse.
// Default mirrors the backend's own default; setGitRedSustainMs (called from
// App.tsx on every /api/state poll) keeps it in sync so this badge never
// drifts out of step with what actually pages again (operator 2026-07-24 —
// this file previously had its own 60min/10min literals, and a THIRD,
// unconditional-red branch for ahead/diverged with no threshold at all).
let GIT_RED_SUSTAIN_MS = 50 * 60 * 1000;
export function setGitRedSustainMs(ms: number): void {
  if (Number.isFinite(ms) && ms > 0) GIT_RED_SUSTAIN_MS = ms;
}
const STALE_REPORT_THRESHOLD_MS = 10 * 60 * 1000; // snapshot older than 10 min → stale (gray)

// "behind" is a PASSIVE, self-healing state — the next slot-cron-ff-pull.sh tick fast-forwards
// it away with no one having to act — unlike dirty/ahead/diverged, which need someone to
// commit/push/rebase and never resolve on their own. Before this threshold existed, "yellow"
// fired the INSTANT any repo read behind>0, with zero debounce, so a slot lit up "warn" for
// what is normal, expected steady-state churn: unified-trading-pm alone ships every 20-90s but
// its own FF-pull is deliberately throttled to a 900s (15 min) cadence
// (unified-trading-pm/scripts/dev/cron-repo-min-interval.txt — "widen-pm-cron-cadence"), so a
// slot idling between ticks reads "behind" for a large fraction of every cycle. Measured live
// 2026-08-19: 3 fleet slots showing "1-2 warn" on the main table were ALL dirty=0, behind=1-2,
// not_clean_since stamped at that exact report tick — pure cadence noise, not a real problem.
// 15 min covers PM's own widened cadence (the dominant source); keep this in step with that
// file's threshold if it ever changes. dirty/ahead/diverged are untouched — those stay instant.
const GIT_BEHIND_WARN_SUSTAIN_MS = 15 * 60 * 1000;

type GitTone = "green" | "yellow" | "red" | "gray";

interface GitBadgeSummary {
  tone: GitTone;
  label: string;
  title: string;
  problems: number;
}

/** A repo not on its integration branch — and not a legit tab/<op>/<N> slot
 * branch — is the silent spawn-blocker (FM5/FM7): e.g. left on `staging` or a
 * `_conflict_analysis_tmp` / `temp-conflict-test` scratch branch by a killed
 * conflict-resolver. Otherwise-clean, so it was invisible (classed green) until
 * it jammed every spawn onto the slot (incident 2026-06-10). */
export function isOffExpectedBranch(r: RepoStatus): boolean {
  return !!r.branch && !!r.integration_branch && r.branch !== r.integration_branch && !r.branch.startsWith("tab/");
}

export function classifyRepoTone(r: RepoStatus, now: number): GitTone {
  // detached/fetch-fail are genuine breakage (not "still pushing" transience) —
  // stays instant red, unlike the sustain-gated states below.
  if (r.state === "detached" || r.state === "fetch-fail") return "red";
  if (r.state === "ahead" || r.state === "diverged") {
    // Ground-truth commit-graph age — the oldest unpushed commit's own timestamp,
    // recomputed fresh every reporter tick, not a server-propagated "first observed
    // non-clean" stamp (not_clean_since, retired here for this branch — same class of
    // fix dirty already had via dirty_oldest_mtime).
    if (r.ahead_oldest_commit_ts) {
      const since = new Date(r.ahead_oldest_commit_ts).getTime();
      if (now - since > GIT_RED_SUSTAIN_MS) return "red";
    }
    return "yellow"; // unpushed, but not sustained past the pager's own threshold yet
  }
  if (isOffExpectedBranch(r)) return "yellow"; // wrong-branch worktree = warning (silent spawn-blocker)
  if (r.state === "dirty") {
    if (r.dirty_oldest_mtime) {
      const dirtySince = new Date(r.dirty_oldest_mtime).getTime();
      if (now - dirtySince > GIT_RED_SUSTAIN_MS) return "red";
    }
    return "yellow";
  }
  if (r.state === "behind") {
    if (r.behind_oldest_commit_ts) {
      const since = new Date(r.behind_oldest_commit_ts).getTime();
      const age = now - since;
      if (age > GIT_RED_SUSTAIN_MS) return "red";
      if (age > GIT_BEHIND_WARN_SUSTAIN_MS) return "yellow";
      return "green"; // fresh — within one FF-pull cycle, self-resolving, not yet worth a warn
    }
    return "yellow"; // no behind_oldest_commit_ts to gate on (legacy/partial report shape) — safe immediate fallback
  }
  return "green";
}

function summarizeGitStatus(git: SlotGitStatus | null): GitBadgeSummary {
  if (!git)
    return { tone: "gray", label: "git: no data", title: "No git-status report from this slot yet.", problems: 0 };
  const now = Date.now();
  const reportedAt = new Date(git.reported_at).getTime();
  if (now - reportedAt > STALE_REPORT_THRESHOLD_MS) {
    const mins = Math.round((now - reportedAt) / 60000);
    return {
      tone: "gray",
      label: `git: stale (${mins}m)`,
      title: `Last report ${mins} min ago — reporter cron may be down on host ${git.host ?? "?"}.`,
      problems: 0,
    };
  }
  const perRepo = git.repos.map((r) => ({ r, tone: classifyRepoTone(r, now) }));
  const reds = perRepo.filter((x) => x.tone === "red").length;
  const yellows = perRepo.filter((x) => x.tone === "yellow").length;
  const tone: GitTone = reds > 0 ? "red" : yellows > 0 ? "yellow" : "green";
  const parts: string[] = [];
  if (reds > 0) parts.push(`${reds} red`);
  if (yellows > 0) parts.push(`${yellows} warn`);
  if (parts.length === 0) parts.push("clean");
  const label = `git: ${parts.join(" · ")}`;
  const titleLines = [
    `Host: ${git.host ?? "?"}`,
    `Reported: ${new Date(git.reported_at).toLocaleTimeString()}`,
    "",
    ...perRepo.map(({ r, tone: t }) => {
      const dot = t === "red" ? "●" : t === "yellow" ? "◐" : "○";
      const extras: string[] = [];
      if (isOffExpectedBranch(r)) extras.push(`on '${r.branch}' ≠ ${r.integration_branch}`);
      if (r.dirty_files > 0) extras.push(`${r.dirty_files} dirty`);
      if (r.ahead > 0) extras.push(`+${r.ahead}`);
      if (r.behind > 0) extras.push(`-${r.behind}`);
      return `${dot} ${r.name} [${r.state}]${extras.length ? " " + extras.join(" ") : ""}`;
    }),
  ];
  return { tone, label, title: titleLines.join("\n"), problems: reds + yellows };
}

function gitToneColor(tone: GitTone): string {
  switch (tone) {
    case "red":
      return "var(--status-blocked, #d4564a)";
    case "yellow":
      return "var(--status-stale, #d8a534)";
    case "green":
      return "var(--status-ok, #4ea672)";
    default:
      return "var(--fg-2, #888)";
  }
}

export function GitStatusBadge({ git }: { git: SlotGitStatus | null }) {
  const summary = summarizeGitStatus(git);
  return (
    <span
      className="git-status-badge"
      title={summary.title}
      style={{
        fontSize: 10.5,
        padding: "2px 6px",
        borderRadius: 4,
        background: gitToneColor(summary.tone),
        color: "#fff",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      {summary.label}
    </span>
  );
}

/** Structural capacity-reservation badge (2026-08-15) — same inline-pill shape as
 * GitStatusBadge. Only rendered for "ci_escalation"/"scheduled_task": those are the
 * two non-default reserves worth calling out on a fleet card; "backlog" is the
 * common case for most slots and would just add noise to every card. `reserved_for`
 * is computed server-side each request from the LIVE roster (never a stored per-slot
 * value), so this badge moves automatically as the fleet grows/shrinks.
 *
 * Operator FAQ baked into the tooltip (2026-08-17, ao_review_slot_hard_rule_and_
 * diagnostics_2026_08_17): these two reserves pick the HIGHEST-numbered non-review
 * slots (config.py's `sorted(non_review_slot_ids)[-n:]`), the opposite of review's
 * fixed low slot 2 — so a reserve badge showing up on a high slot number (e.g. #30
 * on a larger fleet) is expected, not a misconfiguration. A reserved slot showing
 * active work (WORKING, a real task id) is also expected: the reserve exists so its
 * dispatcher (escalation.py / plan_health.py) always has somewhere to run, it is not
 * an idle-only pool. */
export function ReservedBadge({ reservedFor }: { reservedFor: SlotView["reserved_for"] }) {
  if (reservedFor !== "ci_escalation" && reservedFor !== "scheduled_task") return null;
  const label = reservedFor === "ci_escalation" ? "CI reserve" : "sched reserve";
  const background = reservedFor === "ci_escalation" ? "#b45309" : "#0369a1";
  return (
    <span
      className="reserved-badge"
      title={
        reservedFor === "ci_escalation"
          ? "Structurally reserved for CI/CD escalation dispatch (config.ci_escalation_reserved_slot_ids) — " +
            "one of the highest-numbered non-review slots on purpose (unlike review's fixed low slot 2), so " +
            "it can sit at a high slot # and still show active work — that's the reserve doing its job, not a leak."
          : "Structurally reserved for scheduled/cron dispatch (config.scheduled_task_reserved_slot_ids) — " +
            "one of the highest-numbered non-review slots on purpose (unlike review's fixed low slot 2), so " +
            "it can sit at a high slot # and still show active work (e.g. na_eligibility_auditor) — that's the " +
            "reserve doing its job, not a leak."
      }
      style={{
        fontSize: 10.5,
        padding: "2px 6px",
        borderRadius: 4,
        background,
        color: "#fff",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      {label}
    </span>
  );
}

/** Verifies a "review"/"main" role agent (the two singleton roles shown as
 * AgentsPanel tabs) is genuinely bound to its ONE configured reserved slot, not a
 * masking self-registration with no real slot behind it — the exact ambiguity
 * `ao_review_slot_hard_rule_and_diagnostics_2026_08_17` found: an unrelated
 * session self-registered role="review" with no `tmux_session` at all, and the
 * Agents panel read it as "review: healthy" for 23+ hours while the real,
 * configured review slot sat wedged on a hijacked task. Cross-references the
 * holder's `tmux_session` against the live slot roster's server-computed `kind`
 * field — both already flow through the dashboard, so this needs no new backend
 * field. Kept pure (no JSX) so the branches are unit-testable independent of the
 * badge's rendering. */
export function reservedSlotVerification(
  role: AgentRole,
  tmuxSession: string | null,
  slots: SlotView[],
): { applicable: boolean; verified: boolean; slotId: number | null } {
  if (role !== "review" && role !== "main") return { applicable: false, verified: false, slotId: null };
  const boundSlot = tmuxSession ? slots.find((s) => s.tmux_session === tmuxSession) : undefined;
  const verified = !!boundSlot && boundSlot.kind === role;
  return { applicable: true, verified, slotId: boundSlot?.slot_id ?? null };
}

/** Agents-panel counterpart to `ReservedBadge` (2026-08-17,
 * ao_review_slot_hard_rule_and_diagnostics_2026_08_17 Todo 3 — "the tags in the
 * fleet overview and agents overview make this clear"). `ReservedBadge` tags the
 * Fleet card side (ci_escalation/scheduled_task); this tags the Agents-panel side
 * (review/main) using `reservedSlotVerification` above. Same inline-pill shape as
 * `ReservedBadge`/`GitStatusBadge` for visual consistency across panels. */
export function ReservedRoleBadge({
  role,
  tmuxSession,
  slots,
}: {
  role: AgentRole;
  tmuxSession: string | null;
  slots: SlotView[];
}) {
  const v = reservedSlotVerification(role, tmuxSession, slots);
  if (!v.applicable) return null;
  return (
    <span
      className="reserved-role-badge"
      data-testid={`reserved-role-badge-${role}`}
      title={
        v.verified
          ? `Verified: bound to slot #${v.slotId}, the configured ${role} slot.`
          : `NOT bound to a configured ${role} slot (tmux_session=${tmuxSession ?? "none"}) — this may be a ` +
            `masking self-registration rather than the real ${role} agent. See ` +
            `ao_review_slot_hard_rule_and_diagnostics_2026_08_17.md.`
      }
      style={{
        fontSize: 10.5,
        padding: "2px 6px",
        borderRadius: 4,
        background: v.verified ? "var(--status-ok, #15803d)" : "var(--status-failed, #b91c1c)",
        color: "#fff",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      {v.verified ? `✓ slot #${v.slotId}` : "⚠ unbound"}
    </span>
  );
}

/** The "Task / Plan / Task id" lines that lead the Done tooltip; empty string when the outcome
 * carries none of them. Task and plan are absent on events recorded before the server stamped them. */
export function doneTaskContextLines(outcome: Pick<SlotDoneOutcome, "taskId" | "taskTitle" | "planRef">): string {
  return [
    outcome.taskTitle && `Task: ${outcome.taskTitle}`,
    outcome.planRef && `Plan: ${outcome.planRef}`,
    outcome.taskId && `Task id: ${outcome.taskId}`,
  ]
    .filter(Boolean)
    .join("\n");
}

/** One-line rendering of a death's classified terminal reason for the Fleet badge, in
 * `gemini_resource_exhausted (gemini · rpd)` form. `undefined` when there is nothing to show — a death
 * recorded before the 2026-10-16 stamp, or a row the server did not classify — so the caller's `&&` guard
 * degrades to the pre-change rendering rather than printing "undefined".
 *
 * Provider and limit axis are appended only when the `transcript` evidence channel supplied them; a
 * `kill_reason`/`death_class` reason is a bare fact with no such detail, which is why they are optional
 * rather than rendered as empty placeholders. */
export function terminalReasonLine(outcome: SlotDoneOutcome): string | undefined {
  if (!outcome.terminalReason) return undefined;
  const detail = outcome.terminalReasonDetail;
  const bits = [detail?.provider, detail?.limit_axis].filter((v): v is string => typeof v === "string" && v.length > 0);
  return bits.length > 0 ? `${outcome.terminalReason} (${bits.join(" · ")})` : outcome.terminalReason;
}

/** Fleet "Done" badge — renders the slot's most recent completion outcome
 * (see latestDoneOutcomeBySlot). Same inline-pill shape as GitStatusBadge for
 * visual consistency in the same table. */
export function DoneBadge({ outcome }: { outcome: SlotDoneOutcome | undefined }) {
  if (!outcome) return <span style={{ color: "var(--fg-3)" }}>—</span>;
  // died-mid-task (fleet_wide_deepseek_crash_loop_undetected_2026_08_11 follow-up): a
  // tmux_session_lost more recent than the last real slot_done/rejection is a THIRD outcome,
  // not a variant of "done" — without this branch the badge silently froze on a stale "✓ done"
  // through any number of subsequent mid-task deaths (measured live: slot 27 showed a green
  // "✓ done" through 6 consecutive tmux_session_lost deaths over 45 minutes with zero real
  // completions in between).
  // Confirmed-intentional teardown (2026-08-24, the same operator ask that made
  // `outcome.detail` visible in the first place): the server's own classifier
  // already distinguishes a WORKING-AS-DESIGNED kill (one-task-per-session
  // recycle, a resumed context wedge, ...) from a genuinely unattributed loss —
  // showing both under the same red "died mid-task" alarm buried that signal the
  // moment the detail line finally surfaced it. `death_class="unexplained"` (the
  // common, ambiguous case) still renders as died — an absent signal is not
  // confirmation of anything benign, see the fleet_wide_mid_task_death_root_
  // cause_2026_08_21.md todo this same investigation added.
  const background =
    outcome.died && outcome.intentional
      ? "var(--status-idle, #6b7280)"
      : outcome.died
        ? "var(--status-stale, #d8a534)"
        : outcome.passed
          ? "var(--status-ok, #4ea672)"
          : "var(--status-blocked, #d4564a)";
  const label =
    outcome.died && outcome.intentional
      ? "↻ recycled"
      : outcome.died
        ? "⚠ died mid-task"
        : outcome.passed
          ? "✓ done"
          : `✗ ${outcome.label.replace(/^done failed · /, "")}`;
  // (account, model) in play when this outcome was recorded — the operator's
  // died-mid-task ↔ model correlation signal (2026-08-24). Rendered as a compact
  // sub-line so the correlation is scannable down the column without hovering.
  const binding = [outcome.accountId, outcome.model].filter(Boolean).join(" · ");
  // WHY the session ended (2026-10-16) — leads the generic detail line below, because the cause is what
  // the eye should land on; absent on pre-stamp deaths, where the detail line renders alone as before.
  const reasonLine = terminalReasonLine(outcome);
  // What that task was — title + parent plan lead the tooltip so the hover answers "what did the
  // previous agent do", then the existing sha/files/verification detail follows.
  const taskLines = doneTaskContextLines(outcome);
  return (
    <span style={{ display: "inline-block", maxWidth: "100%" }}>
      <span
        // The Cards view renders this badge with no column header to disambiguate it, so the
        // badge has to carry its own meaning: a "✓ done" next to a live WORKING slot means the
        // slot's PREVIOUS task passed, not that the current one is finished.
        title={`Most recently COMPLETED (or died mid-task) attempt for this slot — not the task currently in flight.${
          taskLines ? `\n\n${taskLines}` : ""
        }${binding ? `\nRan as: ${binding}` : ""}\n\n${outcome.detail}`}
        style={{
          fontSize: 10.5,
          padding: "2px 6px",
          borderRadius: 4,
          background,
          color: "#fff",
          whiteSpace: "nowrap",
          cursor: "help",
        }}
      >
        {label}
      </span>
      {outcome.contextReset && (
        // Disposition, not outcome: the task ended as the badge says; this only records that its session was
        // torn down afterwards, so the next task starts with no carried context. Neutral on purpose.
        <span
          title="This task's session was torn down after it finished — the next task starts with a fresh context."
          style={{
            marginLeft: 4,
            fontSize: 9.5,
            padding: "1px 5px",
            borderRadius: 4,
            border: "1px solid var(--fg-3)",
            color: "var(--fg-2)",
            whiteSpace: "nowrap",
            cursor: "help",
          }}
        >
          ↻ fresh context
        </span>
      )}
      {binding && (
        <span
          title={binding}
          style={{
            display: "block",
            fontSize: 9,
            color: "var(--fg-3)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {binding}
        </span>
      )}
      {/* Reason detail (2026-08-24 operator ask): previously only reachable via the
          `title` tooltip above — a died-mid-task row showed a fixed "died mid-task"
          badge with literally nothing else visible. Rendered next to the existing
          account/model line, same dim/small treatment, so both are scannable down
          the column without hovering. */}
      {/* Terminal reason (operator ask 2026-10-16) — `terminal_reason` as stamped on the death row by
          tmux_pruner/terminal_outcome. Rendered in the stale/amber tone so it reads as a cause rather than
          a completion, and the tooltip names the EVIDENCE CHANNEL (`source`), since a `transcript` reason
          shares that signature scan's self-match caveat while a `kill_reason` is a recorded fact. */}
      {reasonLine && (
        <span
          title={`Terminal reason: ${reasonLine}\nEvidence: ${outcome.terminalReasonSource ?? "unclassified"}`}
          style={{
            display: "block",
            fontSize: 9,
            // The stale tone is red in this theme (styles.css `--status-stale`): a cause line on a benign or
            // completed row must not read as an error.
            color: outcome.intentional || outcome.contextReset ? "var(--fg-3)" : "var(--status-stale, #d8a534)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {reasonLine}
        </span>
      )}
      {outcome.detail && (
        <span
          title={outcome.detail}
          style={{
            display: "block",
            fontSize: 9,
            color: "var(--fg-3)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {outcome.detail}
        </span>
      )}
    </span>
  );
}

export function GitStatusDetails({ git }: { git: SlotGitStatus | null }) {
  if (!git || git.repos.length === 0) return null;
  const now = Date.now();
  const reportedAt = new Date(git.reported_at).getTime();
  const stale = now - reportedAt > STALE_REPORT_THRESHOLD_MS;
  const perRepo = git.repos.map((r) => ({ r, tone: classifyRepoTone(r, now) }));
  const interesting = perRepo.filter((x) => x.tone !== "green");
  if (interesting.length === 0) return null;
  return (
    <details style={{ marginTop: 4, fontSize: 11.5 }}>
      <summary style={{ cursor: "pointer", color: "var(--fg-1)" }}>
        {interesting.length} repo{interesting.length === 1 ? "" : "s"} need attention
        {stale && <span style={{ marginLeft: 6, color: gitToneColor("gray") }}>· stale</span>}
        <span style={{ marginLeft: 6, color: "var(--fg-2)" }}>· {git.host ?? "?"}</span>
      </summary>
      <ul style={{ margin: "4px 0 0 0", padding: "0 0 0 14px", lineHeight: 1.5 }}>
        {interesting.map(({ r, tone }) => {
          const extras: string[] = [];
          if (r.dirty_files > 0) extras.push(`${r.dirty_files} file${r.dirty_files === 1 ? "" : "s"} dirty`);
          if (r.ahead > 0) extras.push(`ahead ${r.ahead}`);
          if (r.behind > 0) extras.push(`behind ${r.behind}`);
          let age = "";
          if (r.state === "dirty" && r.dirty_oldest_mtime) {
            const mins = Math.round((now - new Date(r.dirty_oldest_mtime).getTime()) / 60000);
            age = `${mins}m`;
          } else if (r.not_clean_since) {
            const mins = Math.round((now - new Date(r.not_clean_since).getTime()) / 60000);
            age = `${mins}m`;
          }
          const offBranch = isOffExpectedBranch(r);
          return (
            <li key={r.name} style={{ color: gitToneColor(tone) }}>
              <span style={{ fontWeight: 600 }}>{r.name}</span>
              <span style={{ color: "var(--fg-1)" }}> · {r.state}</span>
              {offBranch && (
                <span style={{ color: gitToneColor("red") }}>
                  {" "}
                  · on <code>{r.branch}</code> <span style={{ color: "var(--fg-2)" }}>(≠ {r.integration_branch})</span>
                </span>
              )}
              {extras.length > 0 && <span style={{ color: "var(--fg-2)" }}> · {extras.join(", ")}</span>}
              {age && <span style={{ color: "var(--fg-2)" }}> · {age}</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

// ─── derived: fleet health summary ───────────────────────────────────────

/** Slots that belong in the worker FLEET. Excludes the singleton main (slot 0)
 * and review agents — those run the main / review agents shown in the Agents
 * panel, not the fleet, so they must never appear as fleet slot rows or in fleet
 * counts (a review agent leaking in as "slot 1" on the planning VM was the
 * reported bug, 2026-06-27). Fail-safe: a slot whose `kind` is absent (legacy /
 * partial response) is treated as a worker and stays visible.
 *
 * Also excludes human-kind slots (`config.human_slot_ids()` — operator identity slots
 * 9001/9002, backend `kind` classifies them "worker" since there is no dedicated human
 * literal, so `kind` alone can't distinguish them). ao_human_fleet_integration_2026_08_15
 * wired human slots into the dedicated HumanFleet.tsx page (same `slot_id >= 9000` range
 * check as HumanFleet.tsx's own `buildHumanFleetRows`), but this Fleet table's generic
 * per-slot role badge never got the matching exclusion — a human operator's own session
 * showed up here as an ordinary worker row (ao_satellite_ao_dispatch_batch25 item 4). */
export function fleetSlots(slots: SlotView[]): SlotView[] {
  return slots.filter((s) => s.kind !== "main" && s.kind !== "review" && s.slot_id < 9000);
}

/** Dead-row display gate (ao_worker_lifecycle 2026-07-10 Phase D, operator
 * screenshot): worker-lived fields — TASK · PLAN · LAST MESSAGE · CONTEXT ·
 * PING — die with the worker. A killed slot, or an idle one with neither a
 * live tmux session nor a heartbeating process, must render those columns
 * blank even if a backend field lags, so a dead row can never read as live.
 * Belt-and-suspenders over the backend's reset_slot_worker_state. */
export function slotRowIsDead(s: SlotView): boolean {
  if (s.status === "killed") return true;
  return s.status === "idle" && !s.tmux_alive && !s.worker_alive;
}

/** What the Fleet TASK cell renders for a row (table + card share it). "unknown" ("task unknown") is an
 * honest-absence marker for a NODE row that is WORKING with no in-flight task recorded — never for an idle
 * node row, where "no task" is the truth and a bare "—" is correct (an idle node row with a fresh ping read
 * "task unknown", the 2026-09-26 screenshot symptom). Central rows never render "unknown". */
export function taskCellKind(s: SlotView, isCentral: boolean, dead: boolean): "task" | "role" | "unknown" | "empty" {
  if (!dead && s.current_task) return "task";
  if (!dead && s.spawn_base_role && s.spawn_base_role !== "worker") return "role";
  if (!dead && !isCentral && s.status === "working") return "unknown";
  return "empty";
}

/** UI-only threshold (2026-09-27 operator complaint: slots 11/12/14/15/17 read a fresh
 * `last_ping` while genuinely stalled — no dashboard signal told the operator "pinging"
 * from "actually doing something" apart). Independent of any backend liveness/kill
 * threshold (`_WORKER_ALIVE_WINDOW` etc.) — purely how long a "working" slot may go with
 * no GENUINE task-scoped signal (`last_real_activity_at`) before the row visibly warns. */
export const REAL_ACTIVITY_STALE_SECONDS = 20 * 60;

/** Is this "working" slot's ping fresh while its real work looks stalled? "unknown" means
 * the backend has never logged a genuine task-scoped event for this slot (a node-hosted
 * row — see SlotView.last_real_activity_at's own docstring — or a fresh slot before its
 * first `/progress`/`/done`): never rendered as a false "stale", since there's nothing to
 * compare against. Only a "working" row is judged — an idle/paused/stale/killed slot's
 * ping/activity gap is either already covered by its own status badge or not load-bearing
 * (matches `slotRowIsDead`'s own "only worker-lived fields" scoping). */
export function activityStalenessKind(s: SlotView, now: number = Date.now()): "fresh" | "stale" | "unknown" {
  if (s.status !== "working") return "fresh";
  if (!s.last_real_activity_at) return "unknown";
  const gapSecs = (now - new Date(s.last_real_activity_at).getTime()) / 1000;
  return gapSecs > REAL_ACTIVITY_STALE_SECONDS ? "stale" : "fresh";
}

/** UI-only threshold (PC8, ao_node_worker_parity_followups_2026_10_13): how long a NODE-hosted
 * "working" row's OWN worker heartbeat (`worker_last_heartbeat_at`) may go unrefreshed before the
 * row visibly warns. Independent of every backend liveness/kill threshold — the backend's own
 * `_WORKER_ALIVE_WINDOW` (180 s) still governs kill decisions and is untouched by this; this is
 * only how long the dashboard lets the divergence go unshown. */
export const WORKER_HEARTBEAT_STALE_SECONDS = 10 * 60;

/** Is this row's PING a NODE TICK while the worker itself has gone quiet? (PC8,
 * ao_node_worker_parity_followups_2026_10_13; source issue doc
 * ao_node_worker_parity_click_through_program_2026_09_26, archived.)
 *
 * A NODE-hosted row's `last_ping` is bumped to the node's own tick on every `session_state="busy"`
 * observation (`server/routes/nodes.py` — deliberate kill-safety, Section D todo 6 of
 * ao_liveness_unified_design_and_contradiction_closeout_2026_09_16: it stops `node_stale_requeue`
 * requeueing a worker sitting in one long tool call). The cost is that a WEDGED worker reads a
 * permanently fresh ping — measured 10:58Z, all 5 working rows served `last_ping` 10:57:48.6Z while
 * the SAME batch's `last_worker_heartbeat_at` was 2 h 19 m old for slot 8. The server carries that
 * honest signal separately (`SlotView.worker_last_heartbeat_at`); this is the UI half that surfaces
 * the divergence, next to the PING cell whose value it explains.
 *
 * "unknown" — never rendered as a warning — means there is nothing to compare: a CENTRAL row
 * (whose `last_ping` already IS the worker's own heartbeat, since a central worker's `/progress`
 * reaches central directly, so a second signal would be a duplicate rather than a divergence), or a
 * node whose observation carried no worker heartbeat (an older node). `isCentral` is passed
 * explicitly rather than inferred from the payload for exactly that reason: a central row must not
 * warn on a field it does not own, whatever a given payload happens to carry — the same reason
 * `taskCellKind` takes it. Only a "working" row is judged, matching `activityStalenessKind`'s own
 * scoping (an idle/paused/stale/killed row's ping gap is already covered by its own status badge). */
export function workerHeartbeatStalenessKind(
  s: SlotView,
  isCentral: boolean,
  now: number = Date.now(),
): "fresh" | "stale" | "unknown" {
  if (isCentral || s.status !== "working") return "fresh";
  if (!s.worker_last_heartbeat_at) return "unknown";
  const gapSecs = (now - new Date(s.worker_last_heartbeat_at).getTime()) / 1000;
  return gapSecs > WORKER_HEARTBEAT_STALE_SECONDS ? "stale" : "fresh";
}

/** PING-cell text — the Fleet table row and the card view share this one decision.
 * A dead row, or a paused one (no polling worker: the stored `last_ping` is whoever
 * booted there last), has no meaningful ping and reads "—".
 * `phase: "pre_boot"` is the case this exists for. The backend computes pre_boot as
 * `last_ping < last_spawned_at` (server/state_store/slots.py::compute_slot_phase) — the
 * session is spawned but has not yet sent its STEP-0 heartbeat, so the stored `last_ping`
 * belongs to the PREVIOUS occupant. Rendering it as an age put "55m ago" beside PRE_BOOT
 * for a session seconds old, which is how slots #18/#20 looked stuck for an hour
 * (2026-09-26 operator screenshot, parity item centr#7). Render the honest
 * "no ping since spawn" instead. "booting" is deliberately NOT special-cased: by then a
 * post-spawn ping exists, so its age is real. */
export function pingCellText(s: SlotView, dead: boolean): string {
  if (dead || s.status === "paused") return "—";
  if (s.phase === "pre_boot") return "no ping since spawn";
  return fmtAgo(s.last_ping);
}

/** The Fleet row/card Delete affordance (PC-5,
 * ao_node_worker_parity_click_through_program_2026_09_26.md) — pure, so vitest pins it
 * without a browser.
 *
 * A node-hosted delete is relayed asynchronously and stays `pending_delete` until the node
 * confirms, so a row sits in that state whenever the node is slow — or gone. PC-5 made the
 * DELETE route idempotent precisely so the operator's retry is another DELETE, and the
 * heartbeat finalizer reconciles from central's DB; this is the third leg, the UI: the button
 * must stay ENABLED on a `pending_delete` row and say what a click now does, instead of
 * showing a disabled "Delete pending…" with no lever at all. `current_task` still disables it —
 * the server refuses that call, so the button must not offer it. */
export function deleteSlotAffordance(s: SlotView): { disabled: boolean; label: string; title: string } {
  if (s.current_task) {
    return {
      disabled: true,
      label: "Delete slot",
      title: "Reassign the task first, then delete the slot",
    };
  }
  if (s.status === "pending_delete") {
    return {
      disabled: false,
      label: "Retry delete",
      title:
        "Delete already requested — the node has not confirmed it yet. Click to re-send the delete command; " +
        "the row is also removed automatically once the node reports the session gone.",
    };
  }
  return {
    disabled: false,
    label: "Delete slot",
    title: "Remove this slot row + kill its tmux session",
  };
}

// ─── multi-host fleet grouping (ao_v2_worker_runtime_and_liveness_2026_09_01.md
// Phase 5 [UI] P1) ──────────────────────────────────────────────────────────
//
// Backend fix (server/routes/state.py::_node_slots_for_state) now merges every
// registered node's slots into /api/state's slot list, read-time only, each
// carrying its real `host`. The gap this section closes is entirely on the read
// side: slot_id COLLIDES across hosts BY DESIGN — central can run slots 21-33
// while a node independently runs its own 21-30 — so every per-row React key,
// Map lookup (doneBySlot/workBySlot, both keyed by bare slot_id from
// CENTRAL-only forensics), and REST action (`/api/slots/{slot_id}/...`, which
// only ever resolves against CENTRAL's own SlotRow table) that assumed a bare
// slot_id was a safe identity is a latent cross-host collision the moment a
// node's slots are visible: e.g. clicking "Delete slot" on a rendered node row
// whose slot_id happens to match a central slot would delete CENTRAL's slot,
// not the node's. `slotKey` and `groupSlotsByHost` below exist to make that
// collision structurally impossible in the render path, not just avoided by
// convention.

/** Composite (host, slot_id) identity — the ONLY safe React key or Map key once a
 * node's slots are merged into a fleet-wide list. Bare `slot_id` is safe ONLY
 * within one host's own rows. */
export function slotKey(s: SlotView): string {
  return `${s.host ?? "central"}:${s.slot_id}`;
}

/** Per-slot CPU/RAM/disk/IO telemetry (ao_fleet_slot_resource_ui_and_scale_controls_2026_09_05.md
 * todo 1) — pure, jsdom-free mapper (vitest-covered) so the Fleet table and cards render this
 * identically. Every field null means genuinely NOT MEASURED THIS TICK (no per-slot sampler has
 * ticked yet, or this is a central-hosted slot, which has no per-slot sampler at all) — renders as
 * an explicit "not measured", never a fabricated zero, the same honesty convention
 * `WorkerSlotObservation`'s own docstring establishes server-side. `resource_reading_stale` is
 * computed server-side (clock-skew safe, mirrors `context_reading_stale`) against the sample's own
 * freshness window — a genuinely measured-but-old reading is a THIRD, distinct state from both
 * "fresh" and "never measured". */
export function formatSlotResources(s: SlotView): { text: string; title: string; stale: boolean } {
  const measured =
    s.cpu_pct != null ||
    s.rss_bytes != null ||
    s.disk_bytes != null ||
    s.io_read_bytes != null ||
    s.io_write_bytes != null;
  if (!measured) {
    return { text: "not measured", title: "No per-slot resource sample yet for this slot.", stale: false };
  }
  const cpu = s.cpu_pct != null ? `${s.cpu_pct.toFixed(0)}%` : "cpu —";
  const ram = s.rss_bytes != null ? formatBytes(s.rss_bytes) : "ram —";
  const disk = s.disk_bytes != null ? formatBytes(s.disk_bytes) : "disk —";
  const ioRead = s.io_read_bytes != null ? formatBytes(s.io_read_bytes) : "—";
  const ioWrite = s.io_write_bytes != null ? formatBytes(s.io_write_bytes) : "—";
  const stale = !!s.resource_reading_stale;
  const sampledText = stale
    ? `STALE — last sampled ${fmtAgo(s.resource_sample_at)}`
    : `sampled ${fmtAgo(s.resource_sample_at)}`;
  return {
    text: `${cpu} · ${ram}${stale ? " ⚠" : ""}`,
    title: `CPU ${cpu} · RAM ${ram} · Disk ${disk} · IO read ${ioRead} · IO write ${ioWrite} — ${sampledText}`,
    stale,
  };
}

/** Shared TokenUsageBadge prop builder for the Fleet table AND cards
 * (ao_v2_fleet_visibility_and_reconciliation_2026_09_03 Phase 5 UI P2, ~197) — pure
 * function (jsdom-free vitest, same rationale as `tokenUsageBadgeTitle`) so both
 * render sites can never independently drift on the fallback rule below.
 *
 * A node-hosted slot never carries `current_task_*` usage — the node wire only ever
 * reports SESSION-scoped totals (`session_input_tokens`/`session_output_tokens`/
 * `session_turn_count`, forwarded by `_node_slot_to_view`), never a per-task
 * breakdown (Phase 5 BACKEND P2, ~189). Falls back to session-scoped numbers ONLY
 * when BOTH current_task_input_tokens and current_task_output_tokens are null (the
 * genuine "no task-scoped data available" case — a real central slot with a
 * measured-zero current task, e.g. `current_task_input_tokens: 0`, is NOT null and
 * is never overridden by this fallback). The title changes to say "session" rather
 * than falsely claiming "current task" scope when the fallback is in effect —
 * mislabeling session totals as task-scoped would misreport spend/usage against a
 * task that may not even be the one currently running. `turnCount` has no
 * current_task-scoped counterpart anywhere in the system (see SlotView.
 * session_turn_count's own docstring), so it is always session-scoped. */
export function slotTokenUsageBadgeProps(s: SlotView): {
  inputTokens: number | null;
  outputTokens: number | null;
  cacheCreationTokens?: number | null;
  cacheReadTokens?: number | null;
  spendUsd?: number | null;
  reasoningTokens?: number | null;
  turnCount?: number | null;
  title: string;
} {
  const usingSessionFallback = s.current_task_input_tokens == null && s.current_task_output_tokens == null;
  return {
    inputTokens: (usingSessionFallback ? s.session_input_tokens : s.current_task_input_tokens) ?? null,
    outputTokens: (usingSessionFallback ? s.session_output_tokens : s.current_task_output_tokens) ?? null,
    cacheCreationTokens: usingSessionFallback
      ? s.session_cache_creation_input_tokens
      : s.current_task_cache_creation_input_tokens,
    cacheReadTokens: usingSessionFallback ? s.session_cache_read_input_tokens : s.current_task_cache_read_input_tokens,
    spendUsd: usingSessionFallback ? s.session_spend_usd : s.current_task_spend_usd,
    reasoningTokens: usingSessionFallback ? s.session_reasoning_tokens : s.current_task_reasoning_tokens,
    turnCount: s.session_turn_count,
    title: usingSessionFallback
      ? "Session tokens (per-task breakdown isn't available for this host yet)"
      : "Tokens for the CURRENT task (since assigned)",
  };
}

/** The control plane's own host id, resolved from the always-present "main" (slot 0)
 * row — the one slot classification guaranteed to be central's, never a node's
 * (`state_store.classify_slot_kind`'s own docstring: slot 0 is always the main agent
 * unconditionally; `_node_slots_for_state` explicitly skips a NodeRow matching
 * central's own host_id, so a node can never masquerade as "main"). `null` only when
 * no main-kind row is present at all (a bare unit-test fixture with no slot 0) —
 * callers treat that as "cannot distinguish hosts, assume central" rather than
 * disabling actions/hiding forensics on every row, which would be a regression for
 * every pre-existing single-host deployment and every existing fixture. */
export function resolveCentralHostId(allSlots: SlotView[]): string | null {
  return allSlots.find((s) => s.kind === "main")?.host ?? null;
}

/** Human label for ONE slot row, host-QUALIFIED the moment the fleet can hold two rows
 * with the same `slot_id`. `slot_id` collides across hosts by design (see `SlotView.host`'s
 * own docstring), so a bare `#N` inside an action modal — whose whole point is "act on THIS
 * row" — is ambiguous as soon as a node is in the list, and in the worst case names the WRONG
 * machine (PC-10, `ao_node_worker_parity_click_through_program_2026_09_26.md`). Central rows
 * stay UNQUALIFIED (`#N`) — the operator's existing vocabulary, and what every pre-node spec
 * asserts. A node row reads `#N @ <host>`. `centralHostId == null` (no main row: a bare
 * fixture or a pre-node deployment) means "cannot distinguish hosts", so every row reads
 * unqualified — the same fail-safe `groupSlotsByHost.isCentral` takes. */
export function slotLabel(s: Pick<SlotView, "slot_id" | "host">, centralHostId: string | null): string {
  const base = `#${s.slot_id}`;
  if (centralHostId == null || s.host === centralHostId) return base;
  return `${base} @ ${s.host ?? "(unknown host)"}`;
}

/** Whether this row's own host can be the target of an attach command the dashboard BUILDS.
 * The tmux session named on a row lives on that row's OWN host, but the dashboard can only
 * build a command for the host it is itself served from — `Copy ssh+attach` interpolates
 * `window.location.hostname`, which is the DASHBOARD's host, never a node's. Pairing that
 * with a node's session name emits a command that attaches to the wrong machine or to
 * nothing at all: exactly the wrong-host attach PC-10 removes. No node's own ssh address is
 * available to the dashboard today (`data/config/node_registry.json` carries no ssh field;
 * the tree's only `ssh_host` is the orchestrator VM's own, in orchestrator_vm_registry.yaml),
 * so the node branch is closed until such a field exists — at which point this predicate is
 * the ONE place to reopen it. */
export function slotAttachAllowed(s: Pick<SlotView, "slot_id" | "host">, centralHostId: string | null): boolean {
  return centralHostId == null || s.host === centralHostId;
}

export interface HostSlotGroup {
  host: string;
  /** False for a registered node's own group — gates every action button and every
   * central-only forensic lookup (doneBySlot/workBySlot) in SlotTable/SlotCards, since
   * none of those are safe to evaluate against a node's slot_id (see this section's own
   * header comment). */
  isCentral: boolean;
  slots: SlotView[];
}

/** Groups fleet slots by host for the ONE-page multi-host Fleet render — the
 * operator's own complaint this todo closes: "when these slots started working then
 * why dont i see them in the AO UI?? i dont see the host column as well." Central's
 * group sorts first (the operator's home base, and the only group that existed before
 * nodes did); the rest sort alphabetically for a stable render across polls. A slot
 * with `host` absent/null groups under a labeled fallback bucket — `SlotView.host`'s
 * own docstring says this is never true for a live-queried row, so this is
 * belt-and-suspenders, not an expected bucket. */
export function groupSlotsByHost(slots: SlotView[], centralHostId: string | null): HostSlotGroup[] {
  const byHost = new Map<string, SlotView[]>();
  for (const s of slots) {
    const h = s.host ?? "(unknown host)";
    const bucket = byHost.get(h);
    if (bucket) bucket.push(s);
    else byHost.set(h, [s]);
  }
  const groups: HostSlotGroup[] = Array.from(byHost.entries()).map(([host, hostSlots]) => ({
    host,
    isCentral: centralHostId == null || host === centralHostId,
    slots: hostSlots,
  }));
  groups.sort((a, b) => {
    if (a.isCentral !== b.isCentral) return a.isCentral ? -1 : 1;
    return a.host.localeCompare(b.host);
  });
  return groups;
}

// ─── planning-box function labels (ao_planning_box_profile_and_reserved_slots_
// first_2026_09_05.md, R20) — aggregate per-function counts, feeds
// HostGroupHeader's own profile summary line (slot-18, landed first) ────────

export type SlotFunction = "main" | "review" | "ci_escalation" | "scheduled" | "general";

/** Unifies `kind` + `reserved_for` into ONE function label per slot — the vocabulary
 * R20 labels every slot with: main / review / CI escalation / scheduled / general.
 * Derived client-side from the two backend-owned fields already on the wire (both
 * are already server-computed truth, see their own docstrings above) rather than
 * waiting on a unified backend field — this only relabels them into one combined
 * display vocabulary. */
export function slotFunction(s: SlotView): SlotFunction {
  if (s.kind === "main") return "main";
  if (s.kind === "review") return "review";
  if (s.reserved_for === "ci_escalation") return "ci_escalation";
  if (s.reserved_for === "scheduled_task") return "scheduled";
  return "general";
}

export interface PlanningBoxProfile {
  main: number;
  review: number;
  ci_escalation: number;
  scheduled: number;
  general: number;
}

/** Full-roster function breakdown for the planning/central host — R20: "the
 * main/planning box runs an explicit host profile whose roster is {main, review, 7
 * reserved-function} + a configurable general count." Computed from the FULL
 * unfiltered slot list, NOT `fleetSlots()`'s worker-only view: `fleetSlots()` must
 * stay worker-only (`summarise()`/`criticalHealth()` depend on that exclusion — see
 * `fleetSlots`'s own docstring), but the planning box's own profile needs to count
 * main/review too. Reuses `groupSlotsByHost` rather than re-deriving "which slots
 * belong to the central host" a second, drift-prone way. */
export function planningBoxProfile(allSlots: SlotView[], centralHostId: string | null): PlanningBoxProfile {
  const groups = groupSlotsByHost(
    allSlots.filter((s) => s.slot_id < 9000), // human identity/tab slots are never part of any function roster
    centralHostId,
  );
  const counts: PlanningBoxProfile = { main: 0, review: 0, ci_escalation: 0, scheduled: 0, general: 0 };
  const central = groups.find((g) => g.isCentral);
  for (const s of central?.slots ?? []) {
    counts[slotFunction(s)] += 1;
  }
  return counts;
}

// ─── planning-box roster (ao_planning_box_profile_and_reserved_slots_first_2026_09_05.md
// R20 [UI] P0) — the main/planning host's full roster (main + review + the 7
// reserved-function slots + general backlog workers) as ONE function-labeled
// section, distinct from the per-node groups above. Complements (not replaces)
// slotFunction()/planningBoxProfile() above: those feed HostGroupHeader's
// aggregate-count summary line; this feeds PlanningBox.tsx's own per-SLOT table,
// which is what actually satisfies R20's "each slot labelled by function" —
// an aggregate count alone can't show which specific slot has which function.
// Independent types/names throughout to avoid coupling two independently-landed
// implementations of the same doc's vocabulary. ───────────────────────────────

export type PlanningBoxFunction = "main" | "review" | "ci_escalation" | "scheduled_task" | "general";

export const PLANNING_BOX_FUNCTION_LABEL: Record<PlanningBoxFunction, string> = {
  main: "main",
  review: "review",
  ci_escalation: "CI escalation",
  scheduled_task: "scheduled",
  general: "general",
};

/** Every slot's function within the planning/main host's roster — derived from the
 * SAME `kind`/`reserved_for` fields `ReservedBadge` already reads (never a separate
 * guess): main/review come from `kind`, the two structural reserves from
 * `reserved_for`, and "general" is everything else (an ordinary backlog-pool
 * worker). R20's own vocabulary (ao_planning_box_profile_and_reserved_slots_first_
 * 2026_09_05.md). */
export function planningBoxFunction(s: SlotView): PlanningBoxFunction {
  if (s.kind === "main") return "main";
  if (s.kind === "review") return "review";
  if (s.reserved_for === "ci_escalation") return "ci_escalation";
  if (s.reserved_for === "scheduled_task") return "scheduled_task";
  return "general";
}

const _PLANNING_BOX_FUNCTION_ORDER: Record<PlanningBoxFunction, number> = {
  main: 0,
  review: 1,
  ci_escalation: 2,
  scheduled_task: 3,
  general: 4,
};

export interface PlanningBoxRow {
  key: string;
  slotId: number;
  fn: PlanningBoxFunction;
  status: SlotView["status"];
  currentTask: string | null;
}

/** The planning/main host's full roster, function-labeled and profile-ordered
 * (main, review, CI escalation, scheduled, then general by slot id) — every slot
 * ON the central host, never a node's (those keep `groupSlotsByHost`'s existing
 * per-host rendering untouched — same `isCentral` test this function mirrors).
 * Excludes human-identity slots (`slot_id >= 9000`), the same range check
 * `fleetSlots`/`HumanFleet.tsx`'s own `buildHumanFleetRows` already use. */
export function buildPlanningBoxRoster(slots: SlotView[], centralHostId: string | null): PlanningBoxRow[] {
  return slots
    .filter((s) => s.slot_id < 9000)
    .filter((s) => centralHostId == null || (s.host ?? "(unknown host)") === centralHostId)
    .map((s) => ({
      key: slotKey(s),
      slotId: s.slot_id,
      fn: planningBoxFunction(s),
      status: s.status,
      currentTask: s.current_task,
    }))
    .sort((a, b) => _PLANNING_BOX_FUNCTION_ORDER[a.fn] - _PLANNING_BOX_FUNCTION_ORDER[b.fn] || a.slotId - b.slotId);
}

export interface PlanningBoxCapacity {
  general: number;
  reserved: number;
  total: number;
}

/** "capacity figures exclude reserved-function slots from general capacity" (R20's
 * own done-when) — `general` is what's actually left for ordinary backlog
 * dispatch; `reserved` covers main + review + both structural reserves. */
export function planningBoxCapacity(rows: PlanningBoxRow[]): PlanningBoxCapacity {
  const general = rows.filter((r) => r.fn === "general").length;
  return { general, reserved: rows.length - general, total: rows.length };
}

export interface NodeHealthChip {
  /** "none" = render nothing (central group, or an older backend with no verdict). */
  tone: "none" | "ok" | "warn" | "bad";
  text: string;
  title: string;
}

/** Pure verdict-chip decision for HostGroupHeader (PC-4): the per-host
 * `node_health_status` verdict `state.nodes` carries, reduced to a chip. Kept
 * pure (like taskCellKind) so vitest can pin it without rendering. */
export function nodeHealthChip(nodeHealth: NodeHealthView | null | undefined, isCentral: boolean): NodeHealthChip {
  if (isCentral || nodeHealth == null) return { tone: "none", text: "", title: "" };
  const age =
    nodeHealth.heartbeat_age_s == null ? "age unknown" : `${Math.max(0, Math.round(nodeHealth.heartbeat_age_s))}s ago`;
  const title =
    `node ${nodeHealth.host_id}: ${nodeHealth.status} — ${nodeHealth.reason} ` +
    `(heartbeat ${age}, ${nodeHealth.slots_total} slots)`;
  if (nodeHealth.status === "healthy") return { tone: "ok", text: "● healthy", title };
  if (nodeHealth.status === "degraded") return { tone: "warn", text: "⚠ degraded", title };
  return { tone: "bad", text: `⚠ ${nodeHealth.status}`, title };
}

/** One host's section header inside the grouped Fleet render (table or cards) — see
 * `groupSlotsByHost`. Every slot belonging to a genuinely-offline/unknown node shares
 * the IDENTICAL node-level `stale_reason` (`_node_slot_to_view` stamps
 * `f"node {host_id!r}: {reason}"` onto every one of that node's slots when the node
 * itself is judged stale/unknown) — surfaced once here instead of repeating the same
 * text on every one of the node's rows. */
export function HostGroupHeader({
  group,
  profile,
  nodeHealth,
}: {
  group: HostSlotGroup;
  profile?: PlanningBoxProfile;
  nodeHealth?: NodeHealthView | null;
}) {
  const working = group.slots.filter((s) => s.status === "working").length;
  const idle = group.slots.filter((s) => s.status === "idle").length;
  const staleCount = group.slots.filter((s) => s.status === "stale").length;
  const nodeDown = !group.isCentral && group.slots.length > 0 && staleCount === group.slots.length;
  const nodeDownReason = nodeDown ? (group.slots[0]?.stale_reason ?? "node unreachable") : null;
  // R20 (ao_planning_box_profile_and_reserved_slots_first_2026_09_05.md): the planning
  // box renders as its OWN clearly-labelled section. Both additions below are gated on
  // `group.isCentral` — a node group renders neither, so "node hosts keep today's
  // rendering" holds unconditionally (verified by this file's pre-existing node tests).
  const generalSlots = group.slots.filter((s) => slotFunction(s) === "general");
  const generalIdle = generalSlots.filter((s) => s.status === "idle").length;
  // PC-4: the node's own health verdict, rendered once per host group instead of
  // repeated on every row (`_node_slot_to_view` stamps the same reason per slot).
  const health = nodeHealthChip(nodeHealth, group.isCentral);
  return (
    <div className="host-group-header" data-testid={`host-group-${group.host}`}>
      <span className="host-group-name mono">{group.host}</span>
      <span className={`host-group-badge ${group.isCentral ? "central" : "node"}`}>
        {group.isCentral ? "control plane" : "node"}
      </span>
      {health.tone !== "none" && (
        <span
          className={`host-group-node-health ${health.tone}`}
          title={health.title}
          data-testid="host-group-node-health"
        >
          {health.text}
        </span>
      )}
      {group.isCentral && profile && (
        <span
          className="planning-box-profile"
          data-testid="planning-box-profile"
          title="This host's planning-box roster — reserved-function slots are never counted toward general backlog capacity."
        >
          planning box · {profile.main} main · {profile.review} review · {profile.ci_escalation} CI escalation ·{" "}
          {profile.scheduled} scheduled · {profile.general} general
        </span>
      )}
      {nodeDownReason ? (
        <span className="host-group-node-down" title={nodeDownReason} data-testid="host-group-node-down">
          ⚠ unreachable — {nodeDownReason}
        </span>
      ) : (
        <span className="host-group-counts">
          {group.slots.length} slot{group.slots.length === 1 ? "" : "s"} · {working} working · {idle} idle
          {staleCount > 0 ? ` · ${staleCount} stale` : ""}
          {group.isCentral && (
            <span
              className="general-capacity"
              data-testid="general-capacity"
              title="Backlog-eligible general capacity — excludes main/review/CI-escalation/scheduled reserved-function slots."
            >
              {" "}
              · {generalIdle}/{generalSlots.length} general idle
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export type HealthLevel = "ok" | "warn" | "crit";

export interface HealthSummary {
  level: HealthLevel;
  counts: Record<SlotView["status"], number>;
  pendingBlocked: number;
  highCtx: number;
  thrashing: number;
  total: number;
  avgCtxByStatus: Partial<Record<SlotView["status"], number>>;
  /** Durable auto-park's fleet-wide count (surface_parked_tasks_2026_07_25) —
   * mirrors `state.backlog_summary.auto_parked`, defaulted to 0 when the field is
   * absent (older backend). */
  parkedCount: number;
  /** WorkerLivenessWatchdog dormant — daily kill cap hit, self-healing off until UTC
   * midnight (mirrors `state.watchdog_dormant`, defaulted false on older backends). */
  watchdogDormant: boolean;
  /** tmux SERVER itself is down, taking every slot's session with it at once
   * (mirrors `state.tmux_server_down`, defaulted false on older backends) —
   * ao_tmux_session_loss_mid_task_root_cause_2026_08_10 breakthrough, 2026-08-11. */
  tmuxServerDown: boolean;
  /** Slots whose stored status is "idle" but whose backend-computed `.phase` is
   * "pre_boot" (session spawned, no boot heartbeat yet) — split out so "idle"
   * means literally available-and-unused, not "idle or mid-launch". */
  preBootCount: number;
  /** Same split for `.phase === "booting"` (boot heartbeat seen, `/boot` not done). */
  bootingCount: number;
  /** `counts.idle` minus preBootCount/bootingCount — a slot that is genuinely not
   * paused, not booting, not working, and not stale. This is what "Idle" tiles
   * should render, not the raw status-only count. */
  idleAvailable: number;
  /** Count of `orphan_process_reaped` activity rows in the trailing hour
   * (ao_fleet_regression_triad_2026_08_16 Finding 2) — "agents dying mid-task",
   * which previously had zero HealthStrip signal at all. Mirrors `state.
   * recent_orphan_reap_count`, defaulted to 0 when the field is absent (older
   * backend). */
  recentOrphanReapCount: number;
  /** ao-self-pull.sh auto-rolled a bad deploy back (P0 incident 2026-08-26) —
   * mirrors `state.last_rollback`. `null` is the common case (no rollback, or
   * a later deploy came up healthy and the script cleared the marker itself);
   * ALWAYS worth flagging loudly when present, whether or not it recovered. */
  lastRollback: LastRollbackView | null;
  /** ao-self-pull.sh is REFUSING to deploy (dirty tree in the server clone) —
   * mirrors `state.deploy_stale`. `null` = deploys flowing (or older backend).
   * The Slack wedge page is drift-gated (>=10 commits behind), so this strip is
   * the surface that catches a small-drift freeze (2026-08-26: 2 behind,
   * ~100 min, zero pages). */
  deployStale: NonNullable<StateResponse["deploy_stale"]> | null;
  /** `manual-release`'s running process is behind the checkout HEAD it should be
   * running (todo 1/2 of ao_manual_release_deploy_currency_has_no_dashboard_
   * surface_2026_09_07.md) — mirrors `state.deploy_currency`. Unlike deployStale
   * this is NOT a dirty-tree block, so a small gap is routine (a human hasn't
   * pressed the release button yet) and must render as WARN, escalating only past
   * an age — never CRIT outright, which was the exact always-red mistake this
   * issue was filed alongside. `null` = running process is current (or an older
   * backend that predates the field). */
  deployCurrency: NonNullable<StateResponse["deploy_currency"]> | null;
  /** The "N tasks queued, M idle slots" puzzle aggregate (ao_fleet_slot_state_
   * reason_tooltips_2026_08_27 Track F) — mirrors `state.idle_fleet_blockers`.
   * `null` = no idle slots or nothing queued to classify (the common case when
   * the fleet is busy or the backlog is empty). */
  idleFleetBlockers: NonNullable<StateResponse["idle_fleet_blockers"]> | null;
}

export function summarise(state: StateResponse): HealthSummary {
  // Fleet health = the WORKER fleet only; main / review have their own status in
  // the Agents panel (and dedicated keepers/watchdogs), so a stale review agent
  // must not inflate the fleet's counts / total (keeps HealthStrip consistent
  // with the filtered Fleet list).
  const slots = fleetSlots(state.slots || []);
  const counts: Record<SlotView["status"], number> = {
    working: 0,
    blocked: 0,
    stale: 0,
    idle: 0,
    paused: 0,
    killed: 0,
    // offline (fleet_wide_dispatch_stall_35_idle_1888_queued_2026_09_04.md): a node
    // fleet health has already given up on — same "not a genuine fleet-health bucket"
    // reasoning as pending_delete below, kept at 0 purely for Record exhaustiveness.
    offline: 0,
    // pending_delete (ao_operator_actions_ionos_node_slot_parity_2026_09_09): a transient
    // node-slot-delete-in-flight state, not a genuine fleet-health bucket — never
    // incremented below, kept at 0 so this Record stays exhaustive over SlotStatus.
    pending_delete: 0,
    // ambiguous (ao_liveness_unified_design_and_contradiction_closeout_2026_09_16.md
    // Section E): reserved for the future liveness oracle's AMBIGUOUS verdict — not yet
    // SET by any code path, kept at 0 purely for Record exhaustiveness.
    ambiguous: 0,
  };
  const sumCtx: Partial<Record<SlotView["status"], number>> = {};
  const nCtx: Partial<Record<SlotView["status"], number>> = {};
  let highCtx = 0;
  let thrashing = 0;
  let preBootCount = 0;
  let bootingCount = 0;
  slots.forEach((s) => {
    counts[s.status] = (counts[s.status] || 0) + 1;
    if (s.context_used_pct >= 80) highCtx++;
    if (s.context_pressure === "thrashing") thrashing++;
    sumCtx[s.status] = (sumCtx[s.status] || 0) + s.context_used_pct;
    nCtx[s.status] = (nCtx[s.status] || 0) + 1;
    // `phase` is only meaningful while the backend still reports the slot as
    // "idle" (see server/models/slots.py) — a working/blocked/stale slot's phase
    // is stale/irrelevant, so this split must not fire outside the idle bucket.
    if (s.status === "idle") {
      if (s.phase === "pre_boot") preBootCount++;
      else if (s.phase === "booting") bootingCount++;
    }
  });
  const pendingBlocked = (state.blocked_queue || []).filter((b) => !b.answered_at).length;
  // Durable auto-park count (surface_parked_tasks_2026_07_25) — the orchestrator's
  // own ruled-autonomous blockers, previously invisible here even when they'd sat
  // for ages silently gating dozens of downstream tasks. `?? 0` covers an older
  // backend that predates this field, never reads as "unknown".
  const parkedCount = state.backlog_summary?.auto_parked ?? 0;
  // Watchdog dormancy — the fleet's self-healing being switched off is a fleet-wide
  // condition, not a per-slot one, so no slot count can ever surface it. `?? false`
  // covers an older backend that predates the field; absent must never read as red.
  const watchdogDormant = state.watchdog_dormant ?? false;
  // tmux server down is a fleet-wide condition (like watchdogDormant) — no slot
  // count can surface it, and `?? false` covers an older backend that predates
  // the field, never reading absent as red.
  const tmuxServerDown = state.tmux_server_down ?? false;
  // Orphan-reap count is fleet-wide, not per-slot, like watchdogDormant/tmuxServerDown
  // above — `?? 0` covers an older backend that predates the field (ao_fleet_
  // regression_triad_2026_08_16 Finding 2).
  const recentOrphanReapCount = state.recent_orphan_reap_count ?? 0;
  // Auto-rollback marker (P0 incident 2026-08-26) — `?? null` covers an older
  // backend that predates the field, never reading absent as a rollback.
  const lastRollback = state.last_rollback ?? null;
  // Deploy-staleness marker (2026-08-26) — same `?? null` convention as lastRollback.
  const deployStale = state.deploy_stale ?? null;
  // Deploy-currency marker (2026-09-07) — same `?? null` convention as deployStale,
  // but this one is WARN-only (see the field's own doc comment); it must not join
  // deployStale/lastRollback in the crit set below.
  const deployCurrency = state.deploy_currency ?? null;
  // Idle-fleet-blockers aggregate (ao_fleet_slot_state_reason_tooltips_2026_08_27 Track F) —
  // same `?? null` convention; absent whenever there are no idle backlog slots to classify.
  const idleFleetBlockers = state.idle_fleet_blockers ?? null;
  let level: HealthLevel = "ok";
  if (
    counts.stale > 0 ||
    pendingBlocked > 0 ||
    highCtx > 0 ||
    thrashing > 0 ||
    parkedCount > 0 ||
    recentOrphanReapCount > 0 ||
    deployCurrency !== null
  )
    level = "warn";
  // Mirrors the existing pendingBlocked >= 3 crit threshold — a large or
  // long-standing parked backlog is exactly the "eyes on this" case this strip
  // exists for, and it can silently block dozens of downstream tasks per parked row.
  // Dormancy goes STRAIGHT to crit with no warn tier: it isn't a matter of degree
  // (there is no "slightly dormant"), and while it holds, every OTHER signal on this
  // strip stops being self-correcting — a stale slot that would normally be reaped
  // within a tick now just sits there. tmuxServerDown is the same shape, one level
  // worse: not just self-healing off, NOTHING is running at all. recentOrphanReapCount
  // >= 3 (ao_fleet_regression_triad_2026_08_16 Finding 2) mirrors pendingBlocked's own
  // un-scaled crit bar — "agents dying mid-task" is exactly the "eyes on this" case
  // this strip exists for, and a single reap can be routine cleanup (module docstring),
  // but a sustained rate is the pattern that previously had zero visibility here.
  if (
    counts.stale >= 2 ||
    pendingBlocked >= 3 ||
    thrashing >= 1 ||
    parkedCount >= 3 ||
    watchdogDormant ||
    tmuxServerDown ||
    recentOrphanReapCount >= 3 ||
    lastRollback !== null ||
    deployStale !== null
  )
    level = "crit";
  const avgCtxByStatus: Partial<Record<SlotView["status"], number>> = {};
  for (const k of Object.keys(sumCtx) as SlotView["status"][]) {
    const n = nCtx[k] || 1;
    avgCtxByStatus[k] = Math.round((sumCtx[k] || 0) / n);
  }
  return {
    counts,
    highCtx,
    pendingBlocked,
    thrashing,
    level,
    total: slots.length,
    avgCtxByStatus,
    parkedCount,
    watchdogDormant,
    tmuxServerDown,
    preBootCount,
    bootingCount,
    idleAvailable: (counts.idle || 0) - preBootCount - bootingCount,
    recentOrphanReapCount,
    lastRollback,
    deployStale,
    deployCurrency,
    idleFleetBlockers,
  };
}

// ─── derived: critical fleet-blocking health (ao_dashboard_critical_health_visibility_2026_08_07) ───

// Built from a real 2026-08-07 incident where the dashboard showed 0 agents / all
// slots idle for ~90 minutes with NO signal on the "eyes on this" strip explaining
// why — the true cause (every configured account simultaneously unusable) had to be
// hand-diagnosed via SSM + raw log grep. This surfaces that same class of signal
// natively: account exhaustion is a fleet-wide stop, a persistent role (orchestrator/
// review) with zero presence is a role-wide stop, and a non-empty-but-unclaimable
// queue looks identical to "nothing to do" without the `claimable` distinction.

/** A configured account is usable for a NEW spawn iff it's not in a
 * rate-limited/disabled/auth-failed state AND (for balance-tracked providers like
 * DeepSeek) actually has funds — mirrors AutoSpawn's own `account_is_usable` +
 * `_pick_headroom_account` definitions (`server/autospawn.py`), not a re-derived
 * approximation. `high_usage` is deliberately usable (it is the pre-rate-limit
 * warning band, not a block) — the 2026-08-07 incident's own recovery hinged on
 * this exact status still being spawnable. */
export function accountIsUsable(a: AccountView): boolean {
  if (a.status !== "healthy" && a.status !== "high_usage") return false;
  if (a.balance_is_available === false) return false;
  return true;
}

/** Counts as "available" for the Accounts panel's summary badges (the top-level
 * panel count and each per-provider group's own count) — status is exactly
 * "healthy" AND no evidence-backed health failure is flagged. This is a DISPLAY
 * signal, deliberately separate from accountIsUsable above: that one mirrors
 * AutoSpawn's own dispatch-eligibility definition (also treats high_usage as
 * usable, has no opinion on health_status) — folding health_status into it here
 * would silently change dispatch-adjacent semantics this function has no
 * business touching. Generalizes the health_status check
 * gemma_4_31b_it_persistent_timeout_2026_08_19 shipped only inside
 * NvidiaCapacityPanel.tsx's own degradedNvidiaAccounts(): before this, an
 * enabled-but-degraded account (status: "healthy", health_status: "degraded")
 * silently counted as available in these two generic counts even though its own
 * provider-specific panel already flagged it as broken. */
export function isAccountAvailableForDisplay(a: AccountView): boolean {
  return a.status === "healthy" && a.health_status !== "degraded";
}

const PERSISTENT_KINDS: { kind: AgentKind; label: string }[] = [
  { kind: "orchestrator", label: "Orchestrator (main) agent" },
  { kind: "review", label: "Review agents" },
];

export interface BlockedRole {
  kind: AgentKind;
  label: string;
}

export interface CriticalHealth {
  level: HealthLevel;
  totalAccounts: number;
  usableAccounts: number;
  /** Fleet-wide stop: every configured account is simultaneously unusable — nothing
   * can spawn anywhere, regardless of role, until at least one clears. */
  allAccountsBlocked: boolean;
  /** Earliest `five_hour_resets_at`/`weekly_resets_at` across every UNUSABLE account
   * (ISO string) — the real "back online at" ETA, not a guess. Null when unknown
   * (no unusable account carries a parseable reset timestamp) or not applicable. */
  accountsRecoverAt: string | null;
  /** Persistent roles (orchestrator/review — expected to always have ≥1 online)
   * currently showing zero online presence. */
  blockedRoles: BlockedRole[];
  /** The regular plan-worker fleet is fully idle/killed while real claimable work
   * is waiting — capacity exists on paper, nothing is running it. */
  workerFleetBlocked: boolean;
  /** Scheduled-job role names (plan_reconciler / ag_closeout_auditor
   * / na_eligibility_auditor) whose most recent run did not actually dispatch. */
  blockedScheduledJobs: string[];
  escalations: { queued: number; working: number; failed: number };
  /** From `BacklogSummary.claimable` — undefined means "not computed this poll"
   * (never coerced to 0; the backend omits rather than guesses on a read failure). */
  claimable: number | undefined;
  queued: number;
  /** The control-plane PM clone's staleness, from `state.pm_clone` — the CAUSE
   * to name beside "0 of N queued tasks dispatchable". `null`/absent means the
   * backend has never observed the clone (unknown, never "healthy"). */
  pmClone?: PmCloneView | null;
}

/** The dashboard-side reason string for a zero-dispatchable queue, or null
 * when no PM-clone cause is recorded.
 *
 * 2026-10-17 (operator): the fail-closed PM-clone guard blocked every queued
 * task for ~9 hours while the strip read only "0 of 6349 queued tasks
 * dispatchable" beside unrelated alarms. The count without its cause is what
 * made the wedge look like nothing to do — this names it, with the counts, so
 * the operator can act from the strip alone. Pure, so `layout.test.ts` pins
 * the exact string. */
export function pmCloneBlockedReason(pmClone?: PmCloneView | null): string | null {
  if (!pmClone || !pmClone.guard_blocks_dispatch) return null;
  if (pmClone.blocked_reason) return pmClone.blocked_reason;
  // Older backend that exposes the counts but not the pre-rendered string.
  const behind = pmClone.behind ?? "?";
  const diverged = (pmClone.ahead ?? 0) > 0 ? ", diverged" : "";
  return `PM clone stale: ${behind} behind${diverged}`;
}

function higherLevel(a: HealthLevel, b: HealthLevel): HealthLevel {
  const rank: Record<HealthLevel, number> = { ok: 0, warn: 1, crit: 2 };
  return rank[b] > rank[a] ? b : a;
}

/** Latest run per `job_name` — callers may pass runs in any order; ties broken by
 * `run_id` (lexically later wins) since `finished_at` collisions are possible at
 * second resolution. */
function latestRunPerJob(runs: ScheduledJobRunView[]): Map<string, ScheduledJobRunView> {
  const latest = new Map<string, ScheduledJobRunView>();
  for (const r of runs) {
    const cur = latest.get(r.job_name);
    if (!cur || r.finished_at > cur.finished_at || (r.finished_at === cur.finished_at && r.run_id > cur.run_id)) {
      latest.set(r.job_name, r);
    }
  }
  return latest;
}

export function criticalHealth(
  accounts: AccountView[],
  agents: AgentView[],
  escalations: EscalationView[],
  scheduledJobRuns: ScheduledJobRunView[],
  slots: SlotView[],
  backlog: BacklogSummaryData,
  pmClone?: PmCloneView | null,
): CriticalHealth {
  let level: HealthLevel = "ok";

  const usable = accounts.filter(accountIsUsable);
  const allAccountsBlocked = accounts.length > 0 && usable.length === 0;
  let accountsRecoverAt: string | null = null;
  if (allAccountsBlocked) {
    const resets = accounts
      .flatMap((a) => [a.five_hour_resets_at, a.weekly_resets_at])
      .filter((iso): iso is string => !!iso && !isNaN(new Date(iso).getTime()));
    if (resets.length > 0) accountsRecoverAt = resets.reduce((min, iso) => (iso < min ? iso : min));
    level = higherLevel(level, "crit");
  }

  const blockedRoles: BlockedRole[] = PERSISTENT_KINDS.filter(
    ({ kind }) => !agents.some((a) => a.agent_kind === kind && a.online),
  );
  if (blockedRoles.length > 0) level = higherLevel(level, "crit");

  const fleet = fleetSlots(slots);
  const claimableOrQueued = backlog.claimable ?? backlog.queued;
  // Section C todo 9 (ao_liveness_unified_design_and_contradiction_closeout_2026_09_16):
  // `fleet.every(...)` required a LITERALLY fully-idle fleet to trip, so a fleet mostly
  // idle with real work claimable (class-E2: measured live, 32 of 61 idle) tripped
  // nothing — a healthy-looking dashboard while most of the fleet sat doing nothing.
  // A sustained-ratio threshold instead: 75%+ idle/killed AND something claimable.
  const _WORKER_FLEET_IDLE_FRACTION = 0.75;
  const idleOrKilled = fleet.filter((s) => s.status === "idle" || s.status === "killed").length;
  const workerFleetBlocked =
    fleet.length > 0 && idleOrKilled / fleet.length >= _WORKER_FLEET_IDLE_FRACTION && claimableOrQueued > 0;
  if (workerFleetBlocked) level = higherLevel(level, "crit");

  const latestScheduled = latestRunPerJob(scheduledJobRuns);
  const blockedScheduledJobs = [...latestScheduled.values()]
    .filter((r) => r.status !== "dispatched")
    .map((r) => r.job_name);
  if (blockedScheduledJobs.length > 0) level = higherLevel(level, "warn");

  const escCounts = { queued: 0, working: 0, failed: 0 };
  for (const e of escalations) {
    if (e.status === "queued") escCounts.queued++;
    else if (e.status === "dispatched") escCounts.working++;
    else if (e.status === "unresolved" || e.status === "abandoned") escCounts.failed++;
  }
  if (escCounts.failed > 0) level = higherLevel(level, "warn");

  // A non-empty queue that is 0% claimable is the specific misleading-signal case
  // this feature exists for (a queue can sit at hundreds while every row is gated) —
  // distinct from allAccountsBlocked/workerFleetBlocked, which already explain SOME
  // zero-claimable cases; this branch catches the rest (e.g. every task genuinely
  // prereq-gated with a healthy, idle fleet ready and waiting).
  if (backlog.claimable === 0 && backlog.queued > 0 && !allAccountsBlocked && !workerFleetBlocked) {
    level = higherLevel(level, "crit");
  }

  return {
    level,
    totalAccounts: accounts.length,
    usableAccounts: usable.length,
    allAccountsBlocked,
    accountsRecoverAt,
    blockedRoles,
    workerFleetBlocked,
    blockedScheduledJobs,
    escalations: escCounts,
    claimable: backlog.claimable,
    queued: backlog.queued,
    pmClone,
  };
}

// ─── popover helper ──────────────────────────────────────────────────────

function usePopover() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current || ref.current.contains(target)) return;
      // A portaled Modal (usage_time_series_chart_2026_08_18 finding) is not a DOM
      // descendant of `ref` even when launched from inside this popover — its
      // `.modal-back` root is portaled straight to document.body precisely so it
      // escapes any ancestor's CSS containing block (see components.tsx's Modal
      // docstring). Without this carve-out, a mousedown on anything inside that
      // modal reads as "outside the popover" and closes the popover a frame before
      // the click reaches its real target — which also unmounts the modal, since
      // it's a state-gated child of the popover's own render tree.
      if (target instanceof Element && target.closest(".modal-back")) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", click);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", click);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);
  return { open, setOpen, ref };
}

// ─── settings menu (gear popover) ────────────────────────────────────────

export type Palette = "status" | "cool" | "warm" | "mono";

export interface Tweaks {
  slotLayout: "table" | "cards";
  showActivityRail: boolean;
  showPrerequisitesRail: boolean;
  compactRows: boolean;
  accent: string;
  palette: Palette;
}

export const TWEAK_DEFAULTS: Tweaks = {
  slotLayout: "table",
  showActivityRail: true,
  showPrerequisitesRail: true,
  compactRows: false,
  accent: "#7c83ff",
  palette: "status",
};

interface SettingsMenuProps {
  t: Tweaks;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
}

export function SettingsMenu({ t, setTweak }: SettingsMenuProps) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="anchor" ref={ref}>
      <button
        className={`menu-btn ${open ? "active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title="View settings"
        aria-label="View settings"
      >
        <Icon.Gear />
      </button>
      {open && (
        <div className="menu-pop" role="menu">
          <div className="m-section">
            <div className="m-head">Layout</div>
            <div className="m-row">
              <span className="lbl">Slot view</span>
              <div className="segmented">
                <button
                  className={t.slotLayout === "table" ? "active" : ""}
                  onClick={() => setTweak("slotLayout", "table")}
                >
                  Table
                </button>
                <button
                  className={t.slotLayout === "cards" ? "active" : ""}
                  onClick={() => setTweak("slotLayout", "cards")}
                >
                  Cards
                </button>
              </div>
            </div>
            <div className="m-row">
              <span className="lbl">Compact rows</span>
              <Toggle on={t.compactRows} onChange={(v) => setTweak("compactRows", v)} />
            </div>
          </div>

          <div className="m-section">
            <div className="m-head">Right rail</div>
            <div className="m-row">
              <span className="lbl">Activity feed</span>
              <Toggle on={t.showActivityRail} onChange={(v) => setTweak("showActivityRail", v)} />
            </div>
            <div className="m-row">
              <span className="lbl">Prerequisites</span>
              <Toggle on={t.showPrerequisitesRail} onChange={(v) => setTweak("showPrerequisitesRail", v)} />
            </div>
          </div>

          <div className="m-section">
            <div className="m-head">Appearance</div>
            <div className="m-row" style={{ alignItems: "flex-start" }}>
              <span className="lbl" style={{ paddingTop: 3 }}>
                Accent
              </span>
              <div className="swatch-row">
                {["#7c83ff", "#5db4ff", "#5fd3a4", "#e3a248"].map((c) => (
                  <button
                    key={c}
                    className={`swatch ${t.accent === c ? "active" : ""}`}
                    style={{ background: c }}
                    aria-label={`Accent ${c}`}
                    onClick={() => setTweak("accent", c)}
                  />
                ))}
              </div>
            </div>
            <div className="m-row" style={{ alignItems: "flex-start" }}>
              <span className="lbl" style={{ paddingTop: 3 }}>
                Palette
              </span>
              <div className="segmented" style={{ flexWrap: "wrap" }}>
                {(["status", "cool", "warm", "mono"] as const).map((p) => (
                  <button
                    key={p}
                    className={t.palette === p ? "active" : ""}
                    onClick={() => setTweak("palette", p)}
                    title={
                      p === "status"
                        ? "Default — colors match status meaning (red=alert, green=working, etc.)"
                        : p === "cool"
                          ? "Indigo/cyan/teal/violet — red kept for Blocked"
                          : p === "warm"
                            ? "Gold/orange/coral/amber — red kept for Blocked"
                            : "Almost no color — only Blocked and Agents tinted"
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="m-section" style={{ paddingBottom: 8 }}>
            <div className="m-row" style={{ color: "var(--fg-2)", fontSize: 11.5, cursor: "default" }}>
              <span className="lbl">Polling cadence</span>
              <span className="mono" style={{ color: "var(--fg-1)" }}>
                60s
              </span>
            </div>
            <div className="m-row" style={{ color: "var(--fg-2)", fontSize: 11.5, cursor: "default" }}>
              <span className="lbl">Backend list</span>
              <span className="hint mono">backends.json</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── user chip ───────────────────────────────────────────────────────────

interface UserChipProps {
  user: AuthedUser;
  sessionStartedShort?: string;
  onSignOut: () => void;
}

export function UserChip({ user, sessionStartedShort, onSignOut }: UserChipProps) {
  const { open, setOpen, ref } = usePopover();
  const initials = (user.name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <div className="anchor" ref={ref}>
      <button className="user-chip" onClick={() => setOpen((o) => !o)} aria-label="Account menu">
        <span className="avatar">{initials}</span>
        <span className="name hide-sm">{user.name}</span>
        <Icon.Chev style={{ color: "var(--fg-2)", transform: open ? "rotate(90deg)" : "" }} />
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 240 }} role="menu">
          <div className="m-section">
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="avatar" style={{ width: 28, height: 28, fontSize: 12 }}>
                {initials}
              </span>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                <span style={{ fontWeight: 600, color: "var(--fg-0)" }}>{user.name}</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-2)" }}>
                  {user.role || "operator"}
                </span>
              </div>
            </div>
          </div>
          <div className="m-section">
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="lbl" style={{ color: "var(--fg-2)" }}>
                Session started
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-1)" }}>
                {sessionStartedShort || "—"}
              </span>
            </div>
            <div className="m-row" style={{ cursor: "default" }}>
              <span className="lbl" style={{ color: "var(--fg-2)" }}>
                Machine
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--fg-1)" }}>
                {user.machine || "—"}
              </span>
            </div>
          </div>
          <div className="m-section" style={{ padding: 6 }}>
            <button
              className="btn btn-sm"
              style={{ width: "100%", justifyContent: "flex-start", background: "transparent", border: "none" }}
              onClick={onSignOut}
            >
              <Icon.SignOut /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── fleet KPIs / fleet git-health popovers ─────────────────────────────
//
// Single-VM architecture (2026-06-27) retired the multi-VM Landing page as a
// daily-use screen — nobody navigates to "/" anymore just to reach these two
// pages, so the pages themselves went effectively unseen (operator report
// 2026-07-27). Surfacing them as top-bar popovers, right where the operator
// already is (the per-VM Dashboard), means one click gets the numbers with no
// navigation at all. Reuses FleetKpis/FleetGit UNCHANGED (no onBack passed —
// there is no page to return to from a popover) so there is exactly one
// implementation of each page's data-fetch + render logic.

function FleetKpisMenu({ baseUrl, authToken }: { baseUrl: string; authToken: string | null }) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="anchor" ref={ref}>
      <button
        className={`btn btn-sm hide-sm ${open ? "active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title="Fleet efficiency KPIs — boots/dispatches/done conversion + per-account usage"
      >
        KPIs
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 560, maxWidth: 640, maxHeight: "75vh", overflowY: "auto" }}>
          <React.Suspense fallback={<div className="task-usage-pending">loading…</div>}>
            <FleetKpis baseUrl={baseUrl} authToken={authToken} />
          </React.Suspense>
        </div>
      )}
    </div>
  );
}

function FleetGitMenu({ baseUrl, authToken }: { baseUrl: string; authToken: string | null }) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="anchor" ref={ref}>
      <button
        className={`btn btn-sm hide-sm ${open ? "active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title="Fleet git-health — dirty/behind/diverged worktrees + cron liveness"
      >
        Git Health
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 560, maxWidth: 640, maxHeight: "75vh", overflowY: "auto" }}>
          <React.Suspense fallback={<div className="task-usage-pending">loading…</div>}>
            <FleetGit baseUrl={baseUrl} authToken={authToken} />
          </React.Suspense>
        </div>
      )}
    </div>
  );
}

// ─── top bar ─────────────────────────────────────────────────────────────

interface TopBarProps {
  backends: BackendConfig[];
  backendId: string;
  onBackend: (id: string) => void;
  state: StateResponse | null;
  onSnapshot: () => void;
  /** Manual "deploy now" for the dashboard's own Firebase Hosting build (2026-09-03,
   * deploy-dashboard.yml went workflow_dispatch-only the same day: see that file's
   * header for why an automatic per-push deploy was replaced with this). */
  onDeployDashboard: () => void;
  connected: boolean;
  mode: Mode | null;
  t: Tweaks;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
  user: AuthedUser;
  sessionStartedShort?: string;
  onSignOut: () => void;
  /** Reused, unmodified, inside the FleetKpis/FleetGit popovers below. */
  baseUrl: string;
  authToken: string | null;
  /** Fleet-wide "pause everything" toggle (operator ask 2026-08-30,
   * ao_fleet_pause_dispatch_ui_button_2026_08_30.md) — distinct from any
   * per-slot pause button: this is the single "everything" control.
   * `onToggleFleetDispatchPause(true)` does NOT pause directly — App.tsx routes
   * it through FleetPauseModal's confirm gate (operator ask 2026-09-03); only
   * `(false)` (resume) acts immediately. */
  dispatchPauseStatus: DispatchPauseStatusView | null;
  onToggleFleetDispatchPause: (pause: boolean) => void;
}

export function TopBar({
  backends,
  backendId,
  onBackend,
  state,
  onSnapshot,
  onDeployDashboard,
  connected,
  mode,
  t,
  setTweak,
  user,
  sessionStartedShort,
  onSignOut,
  baseUrl,
  authToken,
  dispatchPauseStatus,
  onToggleFleetDispatchPause,
}: TopBarProps) {
  const lastUpdated = state ? fmtAgo(state.last_updated) : "—";
  const pillOpts: PillOption[] = backends.map((b) => ({
    id: b.id,
    label: b.label,
    url: b.url,
    status: b.status,
    last_pinged_at: b.last_pinged_at,
  }));
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <span className="brand">
          <BrandMark />
          <span>Orchestrator</span>
          <small>
            api {state?.version ?? "—"} · ui {pkg.version}
            {state?.last_dashboard_deploy && (
              <>
                {" "}
                · dashboard{" "}
                {state.last_dashboard_deploy.skipped ? (
                  <span title={`unchanged since v${state.last_dashboard_deploy.version} — Firebase leg skipped (C6)`}>
                    {state.last_dashboard_deploy.version}
                  </span>
                ) : (
                  <span
                    title={`deployed ${state.last_dashboard_deploy.deployed_at} to ${state.last_dashboard_deploy.target}`}
                  >
                    {state.last_dashboard_deploy.version}
                  </span>
                )}
              </>
            )}
          </small>
        </span>

        {mode && <ModeBadge mode={mode} />}

        <span className="topbar-spacer" />

        <div className="topbar-meta">
          <LivePill connected={connected} lastUpdated={lastUpdated} serverStarted={state?.server_started} />
          {dispatchPauseStatus && (
            <button
              id="fleet-dispatch-pause-toggle"
              className="btn btn-sm hide-sm"
              style={dispatchPauseStatus.active ? { color: "var(--status-blocked)" } : undefined}
              onClick={() => onToggleFleetDispatchPause(!dispatchPauseStatus.active)}
              title={
                dispatchPauseStatus.active
                  ? `Fleet dispatch is PAUSED — reason: ${dispatchPauseStatus.reason ?? "not recorded"}. Armed at: ${dispatchPauseStatus.armed_at ?? "unknown"}. Click to resume every slot.`
                  : "Pause ALL fleet dispatch — blocks every slot from claiming new work and kills each one's live session immediately. Opens a confirmation first; resuming does not."
              }
            >
              {dispatchPauseStatus.active ? (
                <>
                  <Icon.Play /> Resume All
                </>
              ) : (
                <>
                  <Icon.Pause /> Pause All
                </>
              )}
            </button>
          )}
          <button
            className="btn btn-sm hide-sm"
            onClick={onSnapshot}
            title="Snapshot — writes state.json + commits to git"
          >
            <Icon.Camera /> Snapshot
          </button>
          <button
            className="btn btn-sm hide-sm"
            onClick={onDeployDashboard}
            title="Deploy this dashboard's frontend to Firebase Hosting now (manual since 2026-09-03 — was previously deployed on every push)"
          >
            <Icon.Play /> Deploy
          </button>
          <FleetGitMenu baseUrl={baseUrl} authToken={authToken} />
          <FleetKpisMenu baseUrl={baseUrl} authToken={authToken} />
          <PillSelect
            value={backendId}
            options={pillOpts}
            onChange={onBackend}
            formatLabel={(b) => (
              <>
                <span className={`status-dot ${b.status === "ok" ? "working" : "stale"}`} />
                <span className="label label-mono">{b.id}</span>
                <span className="url hide-sm">{b.url.replace(/^https?:\/\//, "")}</span>
              </>
            )}
          />
          <SettingsMenu t={t} setTweak={setTweak} />
          <UserChip user={user} sessionStartedShort={sessionStartedShort} onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}

function ModeBadge({ mode }: { mode: Mode }) {
  const label = mode === "mock" ? "MOCK" : "LIVE";
  const cls = mode === "mock" ? "mode-mock" : "mode-live";
  const title =
    mode === "mock"
      ? "Mock mode — fake data; admin endpoints available; switch via ORCHESTRATOR_MODE=live"
      : "Live mode — real data; admin endpoints disabled";
  return (
    <span className={`mode-badge ${cls}`} title={title}>
      {label}
    </span>
  );
}

// ─── health strip ────────────────────────────────────────────────────────

/** Scheduled fleet-wide commit-freeze countdown (sit_promote_lease_scheduled_freeze_
 * window_2026_08_29) — pure formatter (vitest-covered) + a tiny self-ticking shell
 * below it, mirroring ResourceWatchdog.tsx's "pure mappers, React owns only the
 * tick" split. `expiresAt` is `null` until the still-open [BACKEND] P1 follow-up
 * wires the write side through (set_fleet_freeze_status doesn't pass expires_at
 * yet) — render nothing for the countdown clause in that case rather than
 * inventing a duration the API never gave us. */
export function formatFreezeCountdown(expiresAt: string | null, nowMs: number = Date.now()): string {
  if (!expiresAt) return "";
  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  if (Number.isNaN(remainingMs)) return "";
  if (remainingMs <= 0) return "lifting…";
  const totalSec = Math.floor(remainingMs / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return `lifts in ${mm}:${ss}`;
}

function FreezeCountdown({ expiresAt }: { expiresAt: string | null }) {
  // Ticks once a second purely to re-render this span — decoupled from the ~60s
  // /api/fleet-freeze-status poll cadence (App.tsx POLL_INTERVAL_MS) so "lifts in
  // mm:ss" counts down smoothly instead of jumping once a minute.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const text = formatFreezeCountdown(expiresAt);
  return text ? <> · {text}</> : null;
}

/** GitHub slug for this repo, used to turn the bare short shas the deploy alerts
 * carry into one-click commit links. Mirrors `_REPO` in
 * `server/routes/deploy_dashboard.py` — the two are the same fact in two
 * languages, so a future org move (github_org_migration_ikennaigboaka_2026_08_30)
 * must update BOTH. The shell half of this same fix derives the slug from the
 * checkout's own `origin` remote instead (`_commit_url` in
 * `scripts/ao-self-pull.sh`), which the browser cannot do. */
const REPO_SLUG = "IggyIkenna/agent-orchestrator";

/** Commit-browse URL for a short or full sha. GitHub resolves an abbreviated sha
 * in this path, so the 9-char prefix the strip already displays is enough — no
 * need to widen the state view to carry full shas just for the link. */
export function commitUrl(sha: string): string {
  return `https://github.com/${REPO_SLUG}/commit/${encodeURIComponent(sha)}`;
}

/** One short sha, rendered as a link to its commit. Styling lives in
 * `.commit-link` (styles.css), NOT an inline style object: layout.tsx is eagerly
 * loaded via TopBar, so everything here lands in the initial JS chunk the
 * bundle-size ratchet measures, while CSS is a separate asset it does not count.
 * The class inherits the surrounding span's status colour so adding a link never
 * quietly restyles an incident banner. */
function CommitLink({ sha }: { sha: string }) {
  return (
    <a className="commit-link" href={commitUrl(sha)} target="_blank" rel="noreferrer">
      {sha.slice(0, 9)}
    </a>
  );
}

/** Provisional escalation age for the deploy-currency WARN badge below — NOT an
 * operator-ruled number. Todo 3 of ao_manual_release_deploy_currency_has_no_
 * dashboard_surface_2026_09_07.md owns that decision (acceptable staleness +
 * whether a Slack page is warranted) and is still open; this is a reasonable
 * interim so the badge isn't silent until then. The incident that surfaced this
 * gap measured 4.7h with nothing bounding it, so 4h intentionally sits just
 * under that observed worst case rather than at an arbitrary round number. */
const DEPLOY_CURRENCY_ESCALATE_AFTER_MS = 4 * 60 * 60 * 1000;

export function HealthStrip({
  summary,
  critical,
  fleetFreeze,
  onReviewParked,
  onOpenBacklog,
}: {
  summary: HealthSummary;
  /** Fleet-blocking signals (account exhaustion, a persistent role with zero
   * presence, an idle worker fleet with real work waiting, scheduled jobs not
   * dispatching, escalation failures, an unclaimable-despite-nonempty queue) —
   * omitted (section hidden) when the host page hasn't wired the extra fetches up
   * yet, so this stays backward-compatible. */
  critical?: CriticalHealth;
  /** Scheduled fleet-wide commit freeze (sit_promote_lease_scheduled_freeze_
   * window_2026_08_29) — GET /api/fleet-freeze-status's own view, `null` before
   * the host page's fetch has landed once, same backward-compat convention as
   * `critical` above. */
  fleetFreeze?: FleetFreezeStatusView | null;
  /** Opens the parked-tasks detail panel — omitted (button hidden) when the host
   * page hasn't wired one up yet, so this stays backward-compatible. */
  onReviewParked?: () => void;
  /** Opens the full backlog detail view — reused for the "0 dispatchable" action so
   * an operator lands on the SAME per-task list (prereqs, gates) that already
   * exists, rather than a new bespoke drill-down. */
  onOpenBacklog?: () => void;
}) {
  const {
    counts,
    pendingBlocked,
    highCtx,
    thrashing,
    total,
    parkedCount,
    watchdogDormant,
    tmuxServerDown,
    idleAvailable,
    preBootCount,
    bootingCount,
    recentOrphanReapCount,
    lastRollback,
    deployStale,
    deployCurrency,
    idleFleetBlockers,
  } = summary;
  const level = critical ? higherLevel(summary.level, critical.level) : summary.level;
  const titles = {
    ok: "All systems normal",
    warn: "Needs attention",
    crit: "Multiple issues — eyes on this",
  };
  const icon = level === "ok" ? <Icon.Heart /> : <Icon.Warn />;

  const subParts: ReactNode[] = [];
  // ABSOLUTE first, ahead even of tmuxServerDown below: an auto-rollback means the
  // control plane was CRASH-LOOPING moments ago (P0 incident 2026-08-26) — this is
  // a standalone incident report, not a live gauge like the rest of this strip, and
  // must not get lost among routine counts regardless of current recovery state.
  if (lastRollback)
    subParts.push(
      <span
        key="rb"
        style={{ color: "var(--status-blocked)" }}
        title={`ao-self-pull.sh rolled back a bad deploy at ${lastRollback.at}`}
      >
        <b>🔴 auto-rolled-back deploy</b> <CommitLink sha={lastRollback.rolled_back_from} /> →{" "}
        <CommitLink sha={lastRollback.rolled_back_to} /> ({lastRollback.recovered ? "recovered" : "STILL UNHEALTHY"})
      </span>,
    );
  // Deploys FROZEN (2026-08-26): ao-self-pull is skipping every tick over a dirty
  // server clone — the running brain is pinned to old code while LDR moves. Placed
  // right after the rollback report: both are deploy-pipeline incident reports,
  // not live fleet gauges.
  if (deployStale)
    subParts.push(
      <span
        key="ds"
        style={{ color: "var(--status-blocked)" }}
        title={`ao-self-pull.sh has been skipping deploys since ${deployStale.since} (${deployStale.reason}) — dirty: ${deployStale.dirty_paths}`}
      >
        <b>🔴 deploys FROZEN</b> {deployStale.commits_behind} commit{deployStale.commits_behind === 1 ? "" : "s"}{" "}
        stranded · dirty since {deployStale.since}
      </span>,
    );
  // Deploy-currency gap (2026-09-07): the manual-release running process is behind
  // its own checkout HEAD. Unlike lastRollback/deployStale above this is routine
  // for the first few minutes after a landing (a human hasn't pressed the release
  // button yet), so it renders WARN and only visually escalates past a provisional
  // age — it deliberately never joins the crit set (see `deployCurrency` in
  // `summarise()`), matching todo 2's own "a flat CRIT would repeat the always-red
  // mistake this issue was found alongside."
  if (deployCurrency) {
    const headAgeMs = Date.now() - Date.parse(deployCurrency.head_committed_at);
    const escalated = Number.isFinite(headAgeMs) && headAgeMs >= DEPLOY_CURRENCY_ESCALATE_AFTER_MS;
    subParts.push(
      <span
        key="dc"
        style={{ color: escalated ? "var(--status-blocked)" : "var(--status-stale)" }}
        title={`Running process started ${deployCurrency.proc_started_at}; checkout HEAD ${
          deployCurrency.sha
        } committed ${deployCurrency.head_committed_at} — escalation threshold is provisional, see todo 3 of ao_manual_release_deploy_currency_has_no_dashboard_surface_2026_09_07.md`}
      >
        <b>{escalated ? "🟠" : "🟡"} running behind checkout</b> {deployCurrency.commits_behind} commit
        {deployCurrency.commits_behind === 1 ? "" : "s"} not yet live · <CommitLink sha={deployCurrency.sha} />
      </span>,
    );
  }
  // FIRST of all OTHERWISE, deliberately: if the tmux SERVER itself is down, every
  // OTHER number on this strip is stale/meaningless (nothing is running to produce
  // them) — this has to be read before anything else, ahead of even watchdog
  // dormancy (ao_tmux_session_loss_mid_task_root_cause_2026_08_10, 2026-08-11).
  if (tmuxServerDown)
    subParts.push(
      <span key="tsd" style={{ color: "var(--status-blocked)" }} title="tmux server process itself is down fleet-wide">
        <b>tmux server DOWN</b> — every slot lost its session at once
      </span>,
    );
  // While the watchdog is dormant nothing else on this strip is self-correcting,
  // so it has to be read before the counts it invalidates.
  if (watchdogDormant)
    subParts.push(
      <span key="wd" style={{ color: "var(--status-stale)" }} title="WorkerLivenessWatchdog hit its daily kill cap">
        <b>watchdog dormant</b> — no auto-recovery until UTC midnight
      </span>,
    );
  // Scheduled fleet-wide commit freeze (sit_promote_lease_scheduled_freeze_window_2026_08_29)
  // — explains an otherwise-mysterious "why is nothing dispatching" window, same family as
  // tmuxServerDown/watchdogDormant immediately above. Benign/expected (a scheduled workflow
  // armed it, not an incident), so it does not escalate `level` — same non-escalating
  // treatment lastRollback/deployStale already get in this function.
  if (fleetFreeze?.active)
    subParts.push(
      <span
        key="ff"
        data-testid="fleet-freeze-banner"
        style={{ color: "var(--status-stale)" }}
        title={`Fleet-wide commit freeze — reason: ${fleetFreeze.reason ?? "not recorded"}. Armed at: ${fleetFreeze.armed_at ?? "unknown"}. full-workspace-sit runs uncontended during this window.`}
      >
        <b>🧊 fleet freeze active</b>
        {fleetFreeze.reason ? ` — ${fleetFreeze.reason}` : ""} · armed {fmtAgo(fleetFreeze.armed_at)}
        <FreezeCountdown expiresAt={fleetFreeze.expires_at} />
      </span>,
    );
  if (counts.working)
    subParts.push(
      <span key="w">
        <b>{counts.working}</b> working
      </span>,
    );
  if (pendingBlocked)
    subParts.push(
      <span key="b" style={{ color: "var(--status-blocked)" }}>
        <b>{pendingBlocked}</b> blocked question{pendingBlocked > 1 ? "s" : ""}
      </span>,
    );
  if (parkedCount)
    subParts.push(
      <span key="pk" style={{ color: "var(--status-blocked)" }}>
        <b>{parkedCount}</b> parked task{parkedCount > 1 ? "s" : ""}
      </span>,
    );
  if (counts.stale)
    subParts.push(
      <span key="s" style={{ color: "var(--status-stale)" }}>
        <b>{counts.stale}</b> stale slot{counts.stale > 1 ? "s" : ""}
      </span>,
    );
  if (thrashing)
    subParts.push(
      <span key="t" style={{ color: "var(--status-stale)" }}>
        <b>{thrashing}</b> thrashing
      </span>,
    );
  if (highCtx)
    subParts.push(
      <span key="h" style={{ color: "var(--status-stale)" }}>
        <b>{highCtx}</b> at high context
      </span>,
    );
  if (recentOrphanReapCount)
    subParts.push(
      <span
        key="orphan"
        style={{ color: "var(--status-stale)" }}
        title="Worker processes reaped as orphans in the last hour — a sustained rate can mean agents are
          dying mid-task, not just routine cleanup (ao_fleet_regression_triad_2026_08_16 Finding 2)"
      >
        <b>{recentOrphanReapCount}</b> agent{recentOrphanReapCount > 1 ? "s" : ""} reaped mid-task (last hour)
      </span>,
    );
  if (idleAvailable)
    subParts.push(
      <span key="i">
        <b>{idleAvailable}</b> idle
      </span>,
    );
  if (idleFleetBlockers && idleFleetBlockers.total_queued > 0)
    subParts.push(
      <span
        key="idle-fleet-blockers"
        data-testid="idle-fleet-blockers"
        style={{ color: "var(--status-blocked)" }}
        title={
          `Queued-task blockers across the currently available idle fleet: ` +
          `${idleFleetBlockers.fleet_blocked} fleet-blocked, ` +
          `${idleFleetBlockers.slot_excluded} slot-excluded, ` +
          `${idleFleetBlockers.capability_mismatched} capability-mismatched. ` +
          `${idleFleetBlockers.eligible_somewhere} eligible somewhere.` +
          (idleFleetBlockers.sampled < idleFleetBlockers.total_queued
            ? ` Classification sampled the top ${idleFleetBlockers.sampled} of ${idleFleetBlockers.total_queued} queued tasks.`
            : "")
        }
      >
        <b>{idleFleetBlockers.total_queued}</b> queued · <b>{idleFleetBlockers.fleet_blocked}</b> fleet-blocked ·{" "}
        <b>{idleFleetBlockers.slot_excluded}</b> slot-excluded · <b>{idleFleetBlockers.capability_mismatched}</b>{" "}
        capability-mismatched
        {idleFleetBlockers.sampled < idleFleetBlockers.total_queued && (
          <span style={{ color: "var(--fg-2)" }}> (top {idleFleetBlockers.sampled} sampled)</span>
        )}
      </span>,
    );
  if (preBootCount + bootingCount)
    subParts.push(
      <span key="bt" style={{ color: "var(--status-booting)" }}>
        <b>{preBootCount + bootingCount}</b> booting
      </span>,
    );
  if (counts.paused)
    subParts.push(
      <span key="p">
        <b>{counts.paused}</b> paused
      </span>,
    );
  if (critical?.allAccountsBlocked)
    subParts.push(
      <span key="acct" style={{ color: "var(--status-blocked)" }}>
        <b>ALL {critical.totalAccounts}</b> accounts at capacity
        {critical.accountsRecoverAt ? <> · resumes {fmtIn(critical.accountsRecoverAt)}</> : null}
      </span>,
    );
  for (const r of critical?.blockedRoles ?? [])
    subParts.push(
      <span key={`role-${r.kind}`} style={{ color: "var(--status-blocked)" }}>
        <b>{r.label}</b> down
      </span>,
    );
  if (critical?.workerFleetBlocked)
    subParts.push(
      <span key="wfb" style={{ color: "var(--status-blocked)" }}>
        worker fleet idle, work waiting
      </span>,
    );
  if (
    critical &&
    critical.claimable === 0 &&
    critical.queued > 0 &&
    !critical.allAccountsBlocked &&
    !critical.workerFleetBlocked
  ) {
    // 2026-10-17 (operator): name the CAUSE beside the count. The 9-hour
    // 2026-10-17 wedge showed "0 of 6349 queued tasks dispatchable" while the
    // real cause (a diverged, stale root PM clone failing the dispatch guards
    // closed) was invisible on this strip.
    const cause = pmCloneBlockedReason(critical.pmClone);
    subParts.push(
      <span key="claim" style={{ color: "var(--status-blocked)" }}>
        <b>0</b> of {critical.queued} queued task{critical.queued > 1 ? "s" : ""} dispatchable
        {cause ? (
          <>
            {" — "}
            <b>{cause}</b>
          </>
        ) : null}
      </span>,
    );
  }
  if (critical && critical.blockedScheduledJobs.length > 0)
    subParts.push(
      <span key="sched" style={{ color: "var(--status-stale)" }}>
        <b>{critical.blockedScheduledJobs.length}</b> scheduled job{critical.blockedScheduledJobs.length > 1 ? "s" : ""}{" "}
        not dispatching
      </span>,
    );
  if (
    critical &&
    (critical.escalations.working > 0 || critical.escalations.queued > 0 || critical.escalations.failed > 0)
  )
    subParts.push(
      <span key="esc">
        {critical.escalations.working > 0 && (
          <>
            <b>{critical.escalations.working}</b> escalation{critical.escalations.working > 1 ? "s" : ""} working
          </>
        )}
        {critical.escalations.queued > 0 && (
          <>
            {critical.escalations.working > 0 ? " · " : ""}
            <b>{critical.escalations.queued}</b> queued
          </>
        )}
        {critical.escalations.failed > 0 && (
          <span style={{ color: "var(--status-blocked)" }}>
            {critical.escalations.working > 0 || critical.escalations.queued > 0 ? " · " : ""}
            <b>{critical.escalations.failed}</b> failed
          </span>
        )}
      </span>,
    );
  if (subParts.length === 0)
    subParts.push(
      <span key="n">
        {total} slot{total === 1 ? "" : "s"}, nothing wrong
      </span>,
    );

  return (
    <div className={`health ${level}`}>
      <span className="health-icon">{icon}</span>
      <div className="copy">
        <div className="title">{titles[level]}</div>
        <div className="sub">{subParts}</div>
      </div>
      <div className="actions">
        {pendingBlocked > 0 && (
          <a href="#blocked">
            <button className="btn btn-sm">
              Answer {pendingBlocked} question{pendingBlocked > 1 ? "s" : ""}
            </button>
          </a>
        )}
        {parkedCount > 0 &&
          (onReviewParked ? (
            <button className="btn btn-sm" onClick={onReviewParked} data-testid="review-parked-btn">
              Review {parkedCount} parked task{parkedCount > 1 ? "s" : ""}
            </button>
          ) : (
            <a href="#parked">
              <button className="btn btn-sm" data-testid="review-parked-btn">
                Review {parkedCount} parked task{parkedCount > 1 ? "s" : ""}
              </button>
            </a>
          ))}
        {counts.stale > 0 && (
          <a href="#fleet">
            <button className="btn btn-sm btn-danger">Review stale slots</button>
          </a>
        )}
        {critical?.allAccountsBlocked && (
          <a href="#accounts">
            <button className="btn btn-sm btn-danger">Review accounts</button>
          </a>
        )}
        {critical && critical.blockedRoles.length > 0 && (
          <a href="#agent-types">
            <button className="btn btn-sm btn-danger">Review agent types</button>
          </a>
        )}
        {critical?.workerFleetBlocked && (
          <a href="#fleet">
            <button className="btn btn-sm btn-danger">Review fleet</button>
          </a>
        )}
        {critical && critical.claimable === 0 && critical.queued > 0 && (
          <>
            {onOpenBacklog ? (
              <button className="btn btn-sm" onClick={onOpenBacklog}>
                Investigate {critical.queued} queued task{critical.queued > 1 ? "s" : ""}
              </button>
            ) : (
              <a href="#backlog-integrity">
                <button className="btn btn-sm">
                  Investigate {critical.queued} queued task{critical.queued > 1 ? "s" : ""}
                </button>
              </a>
            )}
          </>
        )}
        {critical && critical.blockedScheduledJobs.length > 0 && (
          <a href="#scheduled-jobs">
            <button className="btn btn-sm">Review scheduled jobs</button>
          </a>
        )}
        {critical && critical.escalations.failed > 0 && (
          <a href="#escalations">
            <button className="btn btn-sm btn-danger">
              Review {critical.escalations.failed} failed escalation{critical.escalations.failed > 1 ? "s" : ""}
            </button>
          </a>
        )}
      </div>
    </div>
  );
}

// ─── KPI row ─────────────────────────────────────────────────────────────

interface KpiRowProps {
  summary: HealthSummary;
  backlog: StateResponse["backlog_summary"];
}

export function KpiRow({ summary, backlog }: KpiRowProps) {
  const { counts, total, idleAvailable, preBootCount, bootingCount } = summary;
  const booting = preBootCount + bootingCount;
  return (
    <div className="kpi-row">
      <div className="kpi">
        <div className="k-label">Slots</div>
        <div className="k-value">{total}</div>
        <div className="k-sub">
          {summary.pendingBlocked} blocked · {summary.highCtx} high ctx · {summary.thrashing} thrashing
        </div>
      </div>
      <div className="kpi k-working">
        <div className="k-label">
          <span className="k-dot working" /> Working
        </div>
        <div className="k-value">{counts.working || 0}</div>
        <div className="k-sub">avg ctx {summary.avgCtxByStatus.working ?? "—"}%</div>
      </div>
      <div className="kpi k-blocked">
        <div className="k-label">
          <span className="k-dot blocked" /> Blocked
        </div>
        <div className="k-value">{counts.blocked || 0}</div>
        <div className="k-sub">awaiting answer</div>
      </div>
      <div className="kpi k-stale">
        <div className="k-label">
          <span className="k-dot stale" /> Stale
        </div>
        <div className="k-value">{counts.stale || 0}</div>
        <div className="k-sub">silent &gt; 20m</div>
      </div>
      {/* Split out of the old "idle" bucket (ao_slot_kpi_taxonomy_2026_08_15) — a
          slot mid-launch was previously indistinguishable from one genuinely sitting
          idle. `.phase` (pre_boot vs booting) only exists while status is still
          "idle" server-side; see summarise()'s comment for why. */}
      <div className="kpi k-booting">
        <div className="k-label">
          <span className="k-dot booting" /> Booting
        </div>
        <div className="k-value">{booting}</div>
        <div className="k-sub">
          {preBootCount} pre-boot · {bootingCount} booting
        </div>
      </div>
      <div className="kpi k-paused">
        <div className="k-label">
          <span className="k-dot paused" /> Paused
        </div>
        <div className="k-value">{counts.paused || 0}</div>
        <div className="k-sub">operator-paused</div>
      </div>
      <div className="kpi">
        <div className="k-label">
          <span className="k-dot idle" /> Idle
        </div>
        <div className="k-value">{idleAvailable}</div>
        <div className="k-sub">awaiting task</div>
      </div>
      <div className="kpi">
        <div className="k-label">Backlog</div>
        <div className="k-value">{backlog.queued}</div>
        <div className="k-sub">
          {backlog.dispatched} dispatched · {backlog.done} done
        </div>
      </div>
    </div>
  );
}

// ─── slot table (hybrid expandable) ──────────────────────────────────────

export type SlotAction =
  | "pause"
  | "resume"
  | "message"
  | "reassign"
  | "delete"
  | "log"
  | "loop-interval"
  | "chat"
  | "switch-account"
  | "switch-model";

interface SlotTableProps {
  slots: SlotView[];
  onAct: (action: SlotAction, slot: SlotView) => void;
  /** Composite `slotKey(s)` (`"<host>:<slot_id>"`), not a bare slot_id — slot_id
   * collides across hosts by design, see this file's multi-host grouping section. */
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  doneBySlot?: Map<string, SlotDoneOutcome>;
  /** Per-slot typed-agent work, from `agentWorkBySlot` — surfaces what a cicd/plan_health
   * one-off is doing on a slot that carries no backlog task. */
  workBySlot?: Map<number, { task: string; source: string | null }>;
  /** `resolveCentralHostId(state.slots)` — resolved by the caller against the FULL,
   * unfiltered slot list (`fleetSlots` strips the "main" row this needs to find), see
   * this file's "multi-host fleet grouping" section. */
  centralHostId: string | null;
  /** R20's planning-box profile summary (main/review/ci_escalation/scheduled/general
   * counts for the CENTRAL host) — `planningBoxProfile(state.slots, centralHostId)`,
   * same caller-resolves-against-the-FULL-list reason as `centralHostId` above.
   * Forwarded to `HostGroupHeader`, which renders it only for the central group;
   * omitted/undefined renders exactly as before (no profile line). */
  profile?: PlanningBoxProfile;
  /** Per-host node health verdicts (`state.nodes`) — `HostGroupHeader` renders the
   * matching entry's status chip for node groups; omitted/undefined (older backend)
   * renders exactly as before. */
  nodes?: NodeHealthView[];
}

/** Fleet table columns. Widths are DEFAULTS — every one is drag-resizable and the operator's
 * choice persists (see useColumnWidths). Declared as data rather than inline <th> markup so the
 * width, the resize handle and the persisted key stay one fact per column instead of three.
 *
 * `Account` is 150 rather than the 130 it shipped at: "deepseek-v4-flash" is the longest real
 * account id in the pool today and it truncated to "deepseek-v4-fl…" at 130, which defeats the
 * point of the column (operator report, 2026-08-10). Defaults are a starting point, not a
 * prediction — that is what made resizing worth building rather than re-guessing a number that
 * rots the next time an account is named something longer. */
export const FLEET_COLUMNS: { key: string; label: string; width: number; title?: string; flex?: true }[] = [
  { key: "slot", label: "Slot", width: 60 },
  { key: "host", label: "Host", width: 90 },
  { key: "status", label: "Status", width: 100 },
  {
    key: "reserve",
    label: "Reserve",
    width: 90,
    title:
      "Structural capacity reservation — computed server-side each request from the live roster " +
      "(config.ci_escalation_reserved_slot_ids/scheduled_task_reserved_slot_ids), never a stored " +
      "field. Blank = ordinary Class-A backlog slot.",
  },
  { key: "source", label: "Source", width: 75 },
  { key: "git", label: "Git", width: 120 },
  { key: "task", label: "Task", width: 200 },
  { key: "plan", label: "Plan", width: 130 },
  // NOT the in-flight task's state (that's Status). This is latestDoneOutcomeBySlot():
  // the pass/fail verdict of the most recent task this slot COMPLETED. Renamed from
  // "Done" 2026-08-08 — under a "Done" header, a "✓ done" badge on a WORKING row reads
  // as "this task is finished", which is the opposite of what it means.
  {
    key: "lastDone",
    label: "Last done",
    width: 100,
    title: "Verdict of this slot's most recently COMPLETED task — not the task currently in flight",
  },
  // The ONE flexible column, and load-bearing rather than cosmetic: under `table-layout: fixed`
  // on a `width: 100%` table, specified widths are normalised to the table width — so if EVERY
  // column were specified, widening one would just shrink the others proportionally and a drag
  // would appear to do nothing (measured: the first cut of the resize handles failed exactly
  // this way). Leaving one column unspecified gives the surplus/deficit somewhere real to go.
  // Last message is the natural choice — it was the only width-less column before this too.
  { key: "lastMessage", label: "Last message", width: 320, flex: true },
  { key: "context", label: "Context", width: 160 },
  {
    key: "resources",
    label: "Resources",
    width: 150,
    title:
      'Per-slot CPU/RAM/disk/IO, forwarded verbatim from the node\'s own sampler. "not measured" is ' +
      "honest absence (no sampler tick yet), never a zero — hover for the full breakdown.",
  },
  { key: "ping", label: "Ping", width: 70 },
  // Which account this slot's worker is actually spending on. Was only derivable by
  // reverse-mapping the Accounts panel's "in use by #N, #N" lists back onto slot ids —
  // and that list EXCLUDES idle/paused slots, so a slot could be bound to an account and
  // appear in neither list. ProviderBadge in the Model cell only says "deepseek", not
  // WHICH of the accounts in that provider's pool.
  {
    key: "account",
    label: "Account",
    width: 150,
    title: "Account this slot's worker is bound to (bound at spawn; blank until a worker spawns here)",
  },
  { key: "model", label: "Model", width: 190 },
  { key: "actions", label: "", width: 110 },
];

// Deliberately not named `..._KEY`: gitleaks' generic-api-key rule fires on any *_KEY
// constant whose value has entropy, and this is a localStorage name, not a secret. Renaming
// beats allowlisting it — an allowlist entry would blunt the scanner for real keys.
const COLUMN_WIDTHS_STORE = "fleet.columnWidths.v1";
const MIN_COLUMN_WIDTH = 40;

/** Persisted per-column widths + a drag handler. Persistence is localStorage, not server
 * state: a column width is a per-operator, per-screen preference, and the dashboard is read
 * by the same person from a laptop and a phone. Unknown/garbage keys are ignored rather than
 * thrown on, so a stored blob from an older column set can never break the table. */
export function useColumnWidths(columns: { key: string; width: number }[]) {
  const defaults = React.useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.width])), [columns]);
  const [widths, setWidths] = React.useState<Record<string, number>>(() => {
    try {
      const raw = window.localStorage.getItem(COLUMN_WIDTHS_STORE);
      if (!raw) return defaults;
      const stored: unknown = JSON.parse(raw);
      if (typeof stored !== "object" || stored === null) return defaults;
      const merged = { ...defaults };
      for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
        if (key in merged && typeof value === "number" && Number.isFinite(value) && value >= MIN_COLUMN_WIDTH) {
          merged[key] = value;
        }
      }
      return merged;
    } catch {
      return defaults;
    }
  });

  const persist = React.useCallback((next: Record<string, number>) => {
    try {
      window.localStorage.setItem(COLUMN_WIDTHS_STORE, JSON.stringify(next));
    } catch {
      // Private-browsing / quota — resizing still works for this session, it just won't stick.
    }
  }, []);

  const startResize = React.useCallback(
    (e: React.MouseEvent, key: string) => {
      // stopPropagation: the handle lives inside a <th>, and a bare click on the header row
      // must not be read as a row/sort interaction mid-drag.
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = widths[key] ?? defaults[key] ?? MIN_COLUMN_WIDTH;
      let latest = startWidth;
      const onMove = (ev: MouseEvent) => {
        latest = Math.max(MIN_COLUMN_WIDTH, startWidth + (ev.clientX - startX));
        setWidths((prev) => ({ ...prev, [key]: latest }));
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        // Persist once on release, not per mousemove — a drag fires hundreds of moves.
        setWidths((prev) => {
          const next = { ...prev, [key]: latest };
          persist(next);
          return next;
        });
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [widths, defaults, persist],
  );

  /** Double-click a handle to put that column back to its default. */
  const resetColumn = React.useCallback(
    (key: string) => {
      setWidths((prev) => {
        const next = { ...prev, [key]: defaults[key] ?? MIN_COLUMN_WIDTH };
        persist(next);
        return next;
      });
    },
    [defaults, persist],
  );

  return { widths, startResize, resetColumn };
}

// Deliberately not named `..._KEY` — see COLUMN_WIDTHS_STORE's comment above; same reasoning.
const LAYOUT_WIDTHS_STORE = "layout.columnWidths.v1";
const MIN_LAYOUT_COL_WIDTH = 220;
const MAX_LAYOUT_COL_WIDTH = 900;
const DEFAULT_LAYOUT_WIDTHS: { activity: number; rail: number } = { activity: 360, rail: 360 };

function clampLayoutWidth(w: number): number {
  return Math.min(MAX_LAYOUT_COL_WIDTH, Math.max(MIN_LAYOUT_COL_WIDTH, w));
}

/** Persisted widths for the two side columns of the top-level 3-column `.layout` grid
 * (activity-col | main-col | rail) — the middle column (Fleet, Agents, Accounts, …)
 * always absorbs whatever's left via `minmax(0, 1fr)`, same "one flexible column" trick
 * as `useColumnWidths`/FLEET_COLUMNS above. Lets an operator narrow the Fleet column and
 * widen the rail (Scheduled Jobs, Blocked, Escalations) enough to read a full row instead
 * of the fixed 360px it shipped at (operator report, 2026-08-19: Scheduled Jobs rows
 * truncate hard at 360px). Same localStorage-not-server persistence rationale as column
 * widths — a per-operator, per-screen preference. */
export function useLayoutColumnWidths() {
  const [widths, setWidths] = React.useState<{ activity: number; rail: number }>(() => {
    try {
      const raw = window.localStorage.getItem(LAYOUT_WIDTHS_STORE);
      if (!raw) return DEFAULT_LAYOUT_WIDTHS;
      const stored: unknown = JSON.parse(raw);
      if (typeof stored !== "object" || stored === null) return DEFAULT_LAYOUT_WIDTHS;
      const s = stored as Record<string, unknown>;
      const activity =
        typeof s.activity === "number" && Number.isFinite(s.activity)
          ? clampLayoutWidth(s.activity)
          : DEFAULT_LAYOUT_WIDTHS.activity;
      const rail =
        typeof s.rail === "number" && Number.isFinite(s.rail) ? clampLayoutWidth(s.rail) : DEFAULT_LAYOUT_WIDTHS.rail;
      return { activity, rail };
    } catch {
      return DEFAULT_LAYOUT_WIDTHS;
    }
  });

  const persist = React.useCallback((next: { activity: number; rail: number }) => {
    try {
      window.localStorage.setItem(LAYOUT_WIDTHS_STORE, JSON.stringify(next));
    } catch {
      // Private-browsing / quota — resizing still works for this session, it just won't stick.
    }
  }, []);

  const startResize = React.useCallback(
    (e: React.MouseEvent, side: "activity" | "rail") => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widths[side];
      // The activity handle sits on the LEFT column's right edge (drag right -> grow),
      // same convention as a Fleet column handle. The rail handle sits on the RAIL
      // column's LEFT edge, so the sign flips: drag right -> shrink rail, drag left ->
      // grow rail.
      const sign = side === "activity" ? 1 : -1;
      let latest = startWidth;
      const onMove = (ev: MouseEvent) => {
        latest = clampLayoutWidth(startWidth + sign * (ev.clientX - startX));
        setWidths((prev) => ({ ...prev, [side]: latest }));
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        setWidths((prev) => {
          const next = { ...prev, [side]: latest };
          persist(next);
          return next;
        });
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [widths, persist],
  );

  /** Double-click a handle to put that side back to its default. */
  const resetSide = React.useCallback(
    (side: "activity" | "rail") => {
      setWidths((prev) => {
        const next = { ...prev, [side]: DEFAULT_LAYOUT_WIDTHS[side] };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  return { widths, startResize, resetSide };
}

interface LayoutResizeHandleProps {
  side: "activity" | "rail";
  onStart: (e: React.MouseEvent, side: "activity" | "rail") => void;
  onReset: (side: "activity" | "rail") => void;
}

/** Drag handle between two of `.layout`'s top-level columns. `side` names which
 * column the handle resizes (see useLayoutColumnWidths), and also picks the handle's
 * own grid track: the activity handle sits in track 2 (between activity=1 and main=3),
 * the rail handle sits in track 4 (between main=3 and rail=5) — see the `.layout`
 * 5-track grid-template-columns in styles.css. */
export function LayoutResizeHandle({ side, onStart, onReset }: LayoutResizeHandleProps) {
  return (
    <div
      className="layout-handle"
      style={{ gridColumn: side === "activity" ? 2 : 4 }}
      onMouseDown={(e) => onStart(e, side)}
      onDoubleClick={() => onReset(side)}
      title="Drag to resize · double-click to reset"
      data-testid={`layout-resize-${side}`}
    />
  );
}

export function SlotTable({
  slots,
  onAct,
  expandedId,
  setExpandedId,
  doneBySlot,
  workBySlot,
  centralHostId,
  profile,
  nodes = [],
}: SlotTableProps) {
  const { widths, startResize, resetColumn } = useColumnWidths(FLEET_COLUMNS);
  // PC-4: per-host node verdicts (`state.nodes`) keyed for HostGroupHeader — a
  // Map lookup per group, never a per-slot scan.
  const nodeHealthByHost = React.useMemo(() => new Map((nodes ?? []).map((n) => [n.host_id, n])), [nodes]);
  // Multi-host grouping (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5 [UI] P1)
  // — see this file's "multi-host fleet grouping" section for why bare slot_id is unsafe
  // once a node's slots are in this list. `centralHostId` MUST be resolved by the caller
  // against the FULL, unfiltered `state.slots` (App.tsx) and passed in — `slots` here is
  // already `fleetSlots(...)`-filtered (main/review excluded), so the "main" row
  // `resolveCentralHostId` looks for is never present in it; deriving it from `slots`
  // itself would silently resolve null on every render and misclassify every group as
  // central (measured live while building this: every action button + central-only
  // forensic stayed enabled on a node row, exactly the collision this section exists to
  // prevent).
  const groups = React.useMemo(() => groupSlotsByHost(slots, centralHostId), [slots, centralHostId]);
  return (
    // Horizontal scroll container. The fleet table's own columns already sum past a 1280px
    // viewport (measured 1493px), and once columns are resizable an operator can widen it
    // further on purpose — without this the overflow is simply unreachable, which is the
    // squeezed-column complaint in a different costume.
    <div className="slot-table-scroll">
      <table className="slot-table resizable">
        <thead>
          <tr>
            {FLEET_COLUMNS.map((col) => (
              <th
                key={col.key}
                style={{
                  // A flex column must stay width-LESS for the browser to hand it the slack.
                  ...(col.flex ? {} : { width: widths[col.key] ?? col.width }),
                  ...(col.key === "actions" ? { textAlign: "right" } : {}),
                }}
                title={col.title}
              >
                {col.label}
                {/* No handle on the flex column: it has no width of its own to drag. */}
                {!col.flex && (
                  <span
                    className="col-resize"
                    onMouseDown={(e) => startResize(e, col.key)}
                    onDoubleClick={() => resetColumn(col.key)}
                    title="Drag to resize · double-click to reset"
                    data-testid={`col-resize-${col.key}`}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <React.Fragment key={group.host}>
              <tr className="host-group-header-row">
                <td colSpan={FLEET_COLUMNS.length}>
                  <HostGroupHeader group={group} profile={profile} nodeHealth={nodeHealthByHost.get(group.host)} />
                </td>
              </tr>
              {group.slots.map((s) => {
                const key = slotKey(s);
                const open = expandedId === key;
                // Dead rows (killed / idle-no-session-no-heartbeat) blank every
                // worker-lived column — see slotRowIsDead.
                const dead = slotRowIsDead(s);
                const taskKind = taskCellKind(s, group.isCentral, dead);
                const ago = pingCellText(s, dead);
                const resources = formatSlotResources(s);
                // PC-5: the Delete button's enabled/label/title decision, extracted so vitest can pin it.
                const del = deleteSlotAffordance(s);
                // Host-qualified done forensics (ao_satellite_ao_dispatch_batch46_
                // 2026_09_07 item 3): the server stamps the completing task's
                // NULL=central: central slots must pass null (s.host = centralHostId, not null)
                // so the key matches "central:N" in the map; node slots pass their own host.
                const doneOutcome = doneBySlot?.get(doneEventKey(group.isCentral ? null : s.host, s.slot_id));
                const typedWork = group.isCentral ? workBySlot?.get(s.slot_id) : undefined;
                return (
                  <React.Fragment key={key}>
                    <tr
                      className={`row ${open ? "expanded" : ""} ${dead ? "dead" : ""}`}
                      style={dead ? { opacity: 0.55 } : undefined}
                      onClick={() => setExpandedId(open ? null : key)}
                      // Composite (host, slot_id) identity — a bare "#N" text match is
                      // ambiguous once a node's slots can share a slot_id with central's
                      // (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5 [UI] P1).
                      data-testid={`slot-row-${key}`}
                    >
                      <td>
                        <span className="slot-id">
                          <span className="chev">
                            <Icon.Chev />
                          </span>
                          #{s.slot_id}
                        </span>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 11, color: "var(--fg-2)" }}>
                          {s.host ?? "—"}
                        </span>
                      </td>
                      <td>
                        <StatusBadge
                          status={s.status}
                          phase={s.phase}
                          idleReason={s.idle_reason}
                          prebootElapsedSeconds={s.preboot_elapsed_seconds}
                          currentTask={s.current_task}
                          staleReason={s.stale_reason}
                          killReason={s.last_kill_reason}
                          deathClass={s.last_death_class}
                        />
                      </td>
                      <td>
                        <ReservedBadge reservedFor={s.reserved_for} />
                      </td>
                      <td>
                        {!dead && s.current_task ? (
                          <span
                            style={{
                              fontSize: 10,
                              padding: "1px 6px",
                              borderRadius: 3,
                              background: "rgba(99,102,241,0.15)",
                              color: "var(--fg-2)",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                            }}
                          >
                            plan
                          </span>
                        ) : (
                          <span style={{ color: "var(--fg-3)" }}>—</span>
                        )}
                      </td>
                      <td>
                        <GitStatusBadge git={s.git_status} />
                      </td>
                      <td>
                        {taskKind === "task" ? (
                          <>
                            <span className="task-id mono">{s.current_task}</span>{" "}
                            <AffinityBadge affinity={s.current_task_affinity} />
                          </>
                        ) : taskKind === "role" ? (
                          <RoleBadge role={s.spawn_base_role ?? ""} work={typedWork} />
                        ) : taskKind === "unknown" ? (
                          // Honest-absence (ao_v2_worker_runtime_and_liveness_2026_09_01.md Phase 5
                          // [UI] P1), see taskCellKind: a WORKING node row whose task the server-side
                          // host-qualified lookup / node observation could not supply. A bare "—" would
                          // read like a genuinely taskless central slot while Status says `working`.
                          <span
                            className="task-id unknown mono"
                            data-testid="node-task-unknown"
                            title="Central has no in-flight dispatch recorded for this slot right now — not a claim that this slot has no work."
                          >
                            task unknown
                          </span>
                        ) : (
                          <span className="task-id empty mono">—</span>
                        )}
                      </td>
                      <td
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={(!dead && s.plan_ref) || ""}
                      >
                        {!dead && s.plan_ref ? (
                          <span className="task-id mono">
                            {s.plan_ref.replace(/^plans\/active\//, "").replace(/\.md$/, "")}
                          </span>
                        ) : (
                          <span className="task-id empty mono">—</span>
                        )}
                      </td>
                      <td>
                        {/* last_done_visible_regardless_of_slot_lifecycle_2026_08_25: this is
                        the verdict of the slot's most recently COMPLETED task — history,
                        not current state — so it stays meaningful even once the slot that
                        ran it is killed/idle-with-no-process. Unlike Task/Plan/Source above
                        (which describe what's IN FLIGHT and correctly blank on a dead slot),
                        gating this on `dead` hid real, still-valid data for no reason. */}
                        <DoneBadge outcome={doneOutcome} />
                      </td>
                      {/* No `maxWidth: 0` here any more: that was an AUTO-layout trick to stop a long
                    message stretching the table. The table is `table-layout: fixed` now, so the
                    <th> width is authoritative and the trick would fight it. */}
                      <td
                        style={{
                          color: "var(--fg-1)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={(!dead && s.last_msg) || ""}
                      >
                        {/* Declared-wait indicator (SSOT server/declared_wait.py, 2026-08-23):
                        typed busy_until/is_within_declared_wait let the operator tell "declared
                        a 25-min QG" from "wedged" at a glance, instead of decoding the raw
                        `last_msg` text below (the declared-wait route also stamps a "declared
                        wait <n>s: <command snippet>" string there — this is additive, not a
                        replacement for that). Gated on !dead the same as last_msg itself: a
                        dead slot's declared wait, if any, is stale bookkeeping, not live state.
                        `&& s.tmux_session` (ao_declared_wait_liveness_contract_2026_08_23.md
                        "a declared wait must not outlive its worker") is belt-and-suspenders
                        client-side over the backend's own is_within_declared_wait gate — that
                        field is already False whenever tmux_session is null
                        (agent-orchestrator@c4822e72), so this never fires on its own today, but
                        it's the same defense-in-depth `slotRowIsDead` above already applies
                        rather than trusting a single upstream computation for a field that has
                        gone stale in production before (10:22Z-11:07Z, 2026-09-02). */}
                        {/* Context-plateau indicator (SlotRow.context_plateau_since, SSOT
                        server/context_lifecycle.py's plateau detector). Deliberately a SEPARATE
                        badge from the declared wait below, not a variant of it: "declared a
                        25-min QG" is a healthy worker being silent on purpose, whereas a plateau
                        is the wedge itself — the slot still reports `working` while its context%
                        has stopped climbing, which is exactly the confusion this badge exists to
                        end. `is_context_plateaued` is already gated server-side on
                        status=="working" + a live tmux session (routes/state.py's
                        _is_context_plateaued); `!dead` matches the liveness gating every other
                        row badge applies. */}
                        {!dead && s.is_context_plateaued && s.context_plateau_since ? (
                          <span
                            className="context-plateau-badge"
                            data-testid="context-plateau-badge"
                            title={`Context% plateaued at ~${s.context_plateau_pct ?? "?"}% since ${fmtClock(s.context_plateau_since)} UTC — possible stuck/looping worker`}
                            style={{
                              display: "inline-block",
                              fontSize: 10,
                              padding: "1px 6px",
                              borderRadius: 3,
                              background: "rgba(239,68,68,0.15)",
                              color: "var(--status-blocked)",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              marginRight: 6,
                            }}
                          >
                            ⚠ wedged since {fmtClock(s.context_plateau_since)} UTC
                          </span>
                        ) : null}
                        {!dead && s.is_within_declared_wait && s.busy_until && s.tmux_session ? (
                          <span
                            className="declared-wait-badge"
                            data-testid="declared-wait-badge"
                            title={`Declared wait active until ${fmtClock(s.busy_until)} UTC`}
                            style={{
                              display: "inline-block",
                              fontSize: 10,
                              padding: "1px 6px",
                              borderRadius: 3,
                              background: "rgba(99,102,241,0.15)",
                              color: "var(--fg-2)",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              marginRight: 6,
                            }}
                          >
                            ⏳ wait until {fmtClock(s.busy_until)} UTC
                          </span>
                        ) : null}
                        {dead ? "" : s.last_msg || ""}
                      </td>
                      <td>
                        {dead ? (
                          <span className="task-id empty mono">—</span>
                        ) : (
                          <CtxBar
                            pct={s.context_used_pct}
                            pressure={s.context_pressure}
                            compactionsLastHour={s.compactions_last_hour}
                            stale={s.context_reading_stale}
                            tokens={s.context_tokens}
                            windowTokens={s.context_window_tokens}
                          />
                        )}
                      </td>
                      <td>
                        {dead ? (
                          <span className="task-id empty mono">—</span>
                        ) : (
                          <span
                            className="mono"
                            style={{ fontSize: 11, color: resources.stale ? "var(--status-blocked)" : "var(--fg-2)" }}
                            title={resources.title}
                            data-testid={`slot-resources-${key}`}
                          >
                            {resources.text}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`ago-cell ${s.status === "stale" ? "stale" : ""}`}>{ago}</span>
                        {/* "Pinging but not doing anything" (2026-09-27 operator complaint): a fresh
                        `ago` above only proves the poll loop is alive — this is the genuine-work gap
                        (`last_real_activity_at`) surfacing next to it, only when it has actually
                        blown past REAL_ACTIVITY_STALE_SECONDS on a row claiming "working". */}
                        {!dead && activityStalenessKind(s) === "stale" && (
                          <span
                            className="ago-cell"
                            style={{ display: "block", color: "var(--status-blocked)", fontSize: 10.5 }}
                            title={`last genuine activity (slot_progress/slot_done/slot_blocked): ${fmtClock(s.last_real_activity_at)} UTC — pinging, but no real work reported since`}
                            data-testid={`slot-activity-stale-${key}`}
                          >
                            ⚠ activity {fmtAgo(s.last_real_activity_at)}
                          </span>
                        )}
                        {/* PC8 (ao_node_worker_parity_followups_2026_10_13): the age above is a NODE
                        TICK for a node-hosted "working" row, not the worker's own heartbeat — the
                        node bumps `last_ping` on every busy observation (deliberate kill-safety), so
                        a wedged worker keeps reading fresh. Only rendered where the two genuinely
                        diverge (never for a central row — see workerHeartbeatStalenessKind). */}
                        {!dead && workerHeartbeatStalenessKind(s, group.isCentral) === "stale" && (
                          <span
                            className="ago-cell"
                            style={{ display: "block", color: "var(--status-blocked)", fontSize: 10.5 }}
                            title={`the worker's own heartbeat: ${fmtClock(s.worker_last_heartbeat_at)} UTC — the age above is this NODE's tick (it advances while the slot reads "busy"), so a wedged worker still reads fresh there`}
                            data-testid={`slot-worker-heartbeat-stale-${key}`}
                          >
                            ⚠ worker {fmtAgo(s.worker_last_heartbeat_at)}
                          </span>
                        )}
                      </td>
                      {/* Unconditional (not blanked on a dead row) for the same reason as
                    ProviderBadge below: account_id is the binding the slot row still
                    carries, not live worker state — blanking it would hide exactly the
                    "which account did this killed slot burn credit on" answer. */}
                      <td
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={s.account_id || "No account bound — nothing has spawned on this slot"}
                      >
                        {s.account_id ? (
                          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
                            {s.account_id}
                          </span>
                        ) : (
                          <span style={{ color: "var(--fg-3)" }}>—</span>
                        )}
                      </td>
                      <td className="model-cell">
                        {/* Assigned-vs-spawned duration (2026-08-29, operator ask): the gap between
                      the two is pre-boot/auth lag — "assigned" is dispatch time, "spawned" is
                      when the tmux session actually started. Both fmtAgo (point-in-time), not
                      fmtDuration (span): "how long ago" reads naturally for an ongoing slot,
                      unlike the backlog modal's fixed dispatched_at->done_at span. */}
                        {!dead && (s.assigned_at || s.last_spawned_at) ? (
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: "var(--fg-3)", display: "block", marginBottom: 2 }}
                            title={`Assigned: ${fmtDate(s.assigned_at)}\nSpawned: ${fmtDate(s.last_spawned_at)}`}
                          >
                            {s.assigned_at ? `assigned ${fmtAgo(s.assigned_at)}` : ""}
                            {s.assigned_at && s.last_spawned_at ? " · " : ""}
                            {s.last_spawned_at ? `spawned ${fmtAgo(s.last_spawned_at)}` : ""}
                          </span>
                        ) : null}
                        <ModelBadge model={s.model} effort={s.effort} thinking={s.thinking} />{" "}
                        {/* No `operator` badge here (removed 2026-08-09): every slot in this
                      fleet spawns through the one central orchestrator server, so it was
                      a hardcoded constant ("planning") on every row — zero information,
                      easily misread as a live per-slot signal. The Task cell's RoleBadge
                      now carries the real per-slot "what is this doing" signal instead.
                      Unconditional, like ModelBadge right beside it in this same cell —
                      provider is account metadata, not real-time worker state, so it
                      doesn't blank on a dead row the way Task/Plan/Context/Ping do. */}
                        <ProviderBadge provider={s.provider} />
                        <TokenUsageBadge {...slotTokenUsageBadgeProps(s)} />
                        {s.tmux_session && s.tmux_alive && (
                          <span
                            className="mono"
                            title={`tmux session alive. Attach: tmux attach -t ${s.tmux_session}`}
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              padding: "1px 5px",
                              borderRadius: 3,
                              background: "var(--bg-3)",
                              color: "var(--fg-2)",
                            }}
                          >
                            tmux
                          </span>
                        )}
                        {/* Tasks-completed-per-slot counter (ao_fleet_cache_tokens_and_task_count_
                      2026_08_05) — a tmux session is a fixed per-slot name reused across every
                      sequential task until a full worker respawn, so without this there was no
                      way to tell a freshly-spun-up slot from one that has already ground
                      through many tasks. Only shown once the slot has served at least one task
                      (0 adds noise to every idle/fresh slot). */}
                        {s.tasks_completed != null && s.tasks_completed > 0 && (
                          <span
                            className="mono"
                            data-testid="slot-tasks-completed"
                            title={`${s.tasks_completed} task${s.tasks_completed === 1 ? "" : "s"} completed by this slot, lifetime`}
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              padding: "1px 5px",
                              borderRadius: 3,
                              background: "var(--bg-3)",
                              color: "var(--fg-2)",
                            }}
                          >
                            {s.tasks_completed}✓
                          </span>
                        )}
                        {s.worker_alive && !s.tmux_alive && (
                          <span
                            className="mono"
                            title="Worker process is heartbeating but its tmux session is gone (likely killed by prune wave). `tmux attach` will not work."
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              padding: "1px 5px",
                              borderRadius: 3,
                              background: "var(--warn-bg, #4a3a1a)",
                              color: "var(--warn-fg, #ffd479)",
                            }}
                          >
                            bash-only
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="row-actions">
                          {/* Pause/resume/message/reassign are host-qualified backend routes as of
                          ao_operator_actions_ionos_node_slot_parity_2026_09_09 — safe for every row,
                          not just central's, mirroring the detail-card's identical split above.
                          loop-interval is NOT included: its route (slots_ops.py::set_loop_interval)
                          still has the same bare-ss.get_slot bug this doc's other five actions had —
                          out of scope for this pass (never named in the original bug report), stays
                          gated until it gets the same fix. */}
                          {s.status === "paused" ? (
                            <button
                              className="btn btn-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAct("resume", s);
                              }}
                              title="Resume"
                            >
                              <Icon.Play />
                            </button>
                          ) : (
                            <button
                              className="btn btn-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAct("pause", s);
                              }}
                              title="Pause"
                            >
                              <Icon.Pause />
                            </button>
                          )}
                          <button
                            className="btn btn-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAct("message", s);
                            }}
                            title="Send message"
                          >
                            <Icon.Send />
                          </button>
                          {group.isCentral ? (
                            <button
                              className="btn btn-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAct("loop-interval", s);
                              }}
                              title="Set /loop cadence (live — re-issues the loop, no respawn)"
                            >
                              <Icon.Bolt />
                            </button>
                          ) : null}
                          <button
                            className="btn btn-xs btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAct("reassign", s);
                            }}
                            disabled={!s.current_task}
                            title="Reassign task to queue"
                          >
                            <Icon.Refresh />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="detail-row">
                        {/* Must equal FLEET_COLUMNS.length, not a hand-copied literal — this had
                      already drifted once (comment previously said "13 as of the Account
                      column" while FLEET_COLUMNS itself is 14 long) with no test to catch it,
                      which is exactly why it's derived now instead of re-copied. */}
                        <td colSpan={FLEET_COLUMNS.length}>
                          <div className="detail-card">
                            <div>
                              {/* Declared-wait indicator (SSOT server/declared_wait.py, 2026-08-23) —
                              same additive badge as the compact Fleet row, shown here too since an
                              operator drilling into a silent slot's detail card is exactly who most
                              needs "declared a 25-min QG" vs "wedged" spelled out. `&& s.tmux_session`
                              is the same client-side liveness belt-and-suspenders as the compact row
                              above (ao_declared_wait_liveness_contract_2026_08_23.md). */}
                              {/* Context-plateau indicator — the detail card is where an
                              operator drilling into a confusingly-"working" slot lands, so
                              this spells out the wedge AND how long it has held, which the
                              compact row badge can only hint at. */}
                              {s.is_context_plateaued && s.context_plateau_since ? (
                                <div className="field">
                                  <span className="label">Context plateau</span>
                                  <div className="msg" data-testid="context-plateau-badge-detail">
                                    ⚠ wedged — context% flat at ~{s.context_plateau_pct ?? "?"}% since{" "}
                                    {fmtClock(s.context_plateau_since)} UTC ({fmtAgo(s.context_plateau_since)})
                                  </div>
                                </div>
                              ) : null}
                              {s.is_within_declared_wait && s.busy_until && s.tmux_session ? (
                                <div className="field">
                                  <span className="label">Declared wait</span>
                                  <div className="msg" data-testid="declared-wait-badge-detail">
                                    ⏳ active until {fmtClock(s.busy_until)} UTC
                                  </div>
                                </div>
                              ) : null}
                              <div className="field">
                                <span className="label">Last message</span>
                                <div className="msg">{s.last_msg || "—"}</div>
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div className="field">
                                  <span className="label">Worktree</span>
                                  <span className="value">{s.worktree || "—"}</span>
                                </div>
                                <div className="field">
                                  <span className="label">Branch</span>
                                  <span className="value">{s.branch || "—"}</span>
                                </div>
                                <div className="field">
                                  <span className="label">Assigned</span>
                                  <span className="value">
                                    {s.assigned_at ? fmtClock(s.assigned_at) + " UTC · " + fmtAgo(s.assigned_at) : "—"}
                                  </span>
                                </div>
                                <div className="field">
                                  <span className="label">Last ping</span>
                                  <span className="value">
                                    {fmtClock(s.last_ping)} UTC · {fmtAgo(s.last_ping)}
                                  </span>
                                </div>
                                <div className="field">
                                  {/* Distinct from "Last ping" above (2026-09-27 operator complaint): that
                                  one advances on a bare /heartbeat or /boot poll even when the worker has
                                  done nothing since — this is the last GENUINE task-scoped signal
                                  (slot_progress, slot_done variants, slot_blocked). "—" for a node-hosted
                                  row (no activity_log to read back — SlotView.last_real_activity_at's
                                  docstring), never a fabricated timestamp. */}
                                  <span className="label">Last real activity</span>
                                  <span
                                    className="value"
                                    style={
                                      activityStalenessKind(s) === "stale"
                                        ? { color: "var(--status-blocked)" }
                                        : undefined
                                    }
                                  >
                                    {s.last_real_activity_at
                                      ? `${fmtClock(s.last_real_activity_at)} UTC · ${fmtAgo(s.last_real_activity_at)}`
                                      : "—"}
                                  </span>
                                </div>
                                <div className="field">
                                  {/* PC8 (ao_node_worker_parity_followups_2026_10_13): distinct from "Last ping"
                                  above on a NODE-hosted row, where that one is the node's own tick (bumped on
                                  every busy observation, kill-safety) rather than the worker's heartbeat. "—" on a
                                  central row (there the two ARE the same fact) and on a node that never reported
                                  one, never a fabricated timestamp. */}
                                  <span className="label">Worker heartbeat</span>
                                  <span
                                    className="value"
                                    style={
                                      workerHeartbeatStalenessKind(s, group.isCentral) === "stale"
                                        ? { color: "var(--status-blocked)" }
                                        : undefined
                                    }
                                  >
                                    {s.worker_last_heartbeat_at
                                      ? `${fmtClock(s.worker_last_heartbeat_at)} UTC · ${fmtAgo(s.worker_last_heartbeat_at)}`
                                      : "—"}
                                  </span>
                                </div>
                                <div className="field">
                                  <span className="label">Compactions</span>
                                  <span className="value">
                                    {s.compactions_total} total · {s.compactions_last_hour} in last hr · last{" "}
                                    {fmtAgo(s.last_compacted_at)}
                                  </span>
                                </div>
                                <div className="field">
                                  <span className="label">Context</span>
                                  <span
                                    className="value"
                                    title={ctxArithmetic(s.context_tokens, s.context_window_tokens)}
                                  >
                                    {s.context_used_pct}% ({s.context_pressure})
                                    {ctxArithmetic(s.context_tokens, s.context_window_tokens) && (
                                      <span
                                        className="mono"
                                        style={{ color: "var(--fg-3)", marginLeft: 6, fontSize: 10.5 }}
                                      >
                                        {ctxArithmetic(s.context_tokens, s.context_window_tokens)}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {s.tmux_session && (
                                  <div className="field" style={{ gridColumn: "1 / -1" }}>
                                    <span className="label">tmux session</span>
                                    <span className="value" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                      <span className="mono">{s.tmux_session}</span>
                                      {/* PC-10: both commands below are only correct for a slot on the
                                      host the DASHBOARD itself runs on. `window.location.hostname` is the
                                      dashboard's host, never a node's, and the session name belongs to the
                                      slot's own host — so a node row used to offer a command that attached
                                      to the wrong machine. Gated on `slotAttachAllowed` (see its docstring
                                      for why no node row qualifies today); the session NAME stays visible
                                      either way, since it is honest information about the row. */}
                                      {slotAttachAllowed(s, centralHostId) && (
                                        <>
                                          <button
                                            className="btn btn-xs"
                                            onClick={() => {
                                              void navigator.clipboard?.writeText(`tmux attach -t ${s.tmux_session}`);
                                            }}
                                            title="Copy local-attach command"
                                          >
                                            Copy attach
                                          </button>
                                          <button
                                            className="btn btn-xs"
                                            onClick={() => {
                                              const host =
                                                window.location.hostname === "localhost"
                                                  ? "<your-host>"
                                                  : window.location.hostname;
                                              void navigator.clipboard?.writeText(
                                                `ssh ${host} -t tmux attach -t ${s.tmux_session}`,
                                              );
                                            }}
                                            title="Copy SSH+attach command (for mobile / remote use)"
                                          >
                                            Copy ssh+attach
                                          </button>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <div className="danger-zone">
                                <div className="label">Operator actions</div>
                                <div className="detail-actions">
                                  {/* Every action below is now a host-qualified backend route
                                  (ao_operator_actions_ionos_node_slot_parity_2026_09_09) — safe to
                                  render for every row, not just central's. Chat + Show log were the
                                  first to get this treatment (ao_node_worker_slot1_stuck_and_parity_
                                  investigation_2026_09_04.md's [UI] P2); message/pause/resume/
                                  reassign/switch-account/switch-model/delete follow the same pattern. */}
                                  <button className="btn btn-sm" onClick={() => onAct("message", s)}>
                                    <Icon.Send /> Send message…
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    onClick={() => onAct("chat", s)}
                                    title="Live chat — type directly into the worker's tmux pane, see its next output"
                                  >
                                    <Icon.Chat /> Chat
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    onClick={() => onAct("log", s)}
                                    title="Tail tmux capture-pane scrollback in a modal (auto-refreshes every 5s)"
                                  >
                                    Show log
                                  </button>
                                  {s.status === "paused" ? (
                                    <button className="btn btn-sm" onClick={() => onAct("resume", s)}>
                                      <Icon.Play /> Resume
                                    </button>
                                  ) : (
                                    <button className="btn btn-sm" onClick={() => onAct("pause", s)}>
                                      <Icon.Pause /> Pause
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => onAct("reassign", s)}
                                    disabled={!s.current_task}
                                  >
                                    <Icon.Refresh /> Reassign task
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    onClick={() => onAct("switch-account", s)}
                                    title="Emergency lever: force this slot onto a specific account (kill + resume, context intact) — usable from a phone with no laptop/SSH access"
                                  >
                                    Switch Account
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    onClick={() => onAct("switch-model", s)}
                                    title="Switch this slot onto a specific model (kill + resume, context intact)"
                                  >
                                    Switch Model
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => onAct("delete", s)}
                                    disabled={del.disabled}
                                    title={del.title}
                                  >
                                    {del.label}
                                  </button>
                                </div>
                                <div style={{ color: "var(--fg-2)", fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
                                  Reassign returns{" "}
                                  <b style={{ color: "var(--fg-0)" }}>{s.current_task || "the task"}</b> to the queue
                                  and frees this slot. Delete removes the slot row entirely and kills the tmux session
                                  if attached. Both confirm before running.
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── slot cards (alternative layout) ─────────────────────────────────────

export function SlotCards({
  slots,
  onAct,
  doneBySlot,
  centralHostId,
  profile,
  nodes = [],
}: {
  slots: SlotView[];
  onAct: SlotTableProps["onAct"];
  doneBySlot?: Map<string, SlotDoneOutcome>;
  /** MUST be resolved by the caller against the FULL, unfiltered `state.slots` — see
   * SlotTable's identical prop for the full rationale (this file's "multi-host fleet
   * grouping" section). */
  centralHostId: string | null;
  /** R20's planning-box profile — see `SlotTableProps.profile`'s identical doc. */
  profile?: PlanningBoxProfile;
  /** Per-host node health verdicts — see `SlotTableProps.nodes`' identical doc. */
  nodes?: NodeHealthView[];
}) {
  // Multi-host grouping — see SlotTable's identical setup + this file's "multi-host
  // fleet grouping" section for why bare slot_id is unsafe once a node's slots are here.
  const groups = React.useMemo(() => groupSlotsByHost(slots, centralHostId), [slots, centralHostId]);
  // PC-4: per-host node verdicts — see SlotTable's identical setup.
  const nodeHealthByHost = React.useMemo(() => new Map((nodes ?? []).map((n) => [n.host_id, n])), [nodes]);
  return (
    <div className="slot-cards">
      {groups.map((group) => (
        <React.Fragment key={group.host}>
          <div className="host-group-header-card">
            <HostGroupHeader group={group} profile={profile} nodeHealth={nodeHealthByHost.get(group.host)} />
          </div>
          {group.slots.map((s) => {
            // NULL=central: same fix as SlotTable — central cards pass null, node cards pass s.host.
            const doneOutcome = doneBySlot?.get(doneEventKey(group.isCentral ? null : s.host, s.slot_id));
            return (
              <div key={slotKey(s)} className={`slot-card ${s.status}`} data-testid={`slot-card-${slotKey(s)}`}>
                <div className="row-1">
                  <span className="id mono">#{s.slot_id}</span>
                  <StatusBadge
                    status={s.status}
                    idleReason={s.idle_reason}
                    staleReason={s.stale_reason}
                    killReason={s.last_kill_reason}
                    deathClass={s.last_death_class}
                  />
                  <GitStatusBadge git={s.git_status} />
                  <ReservedBadge reservedFor={s.reserved_for} />
                  <span className="spacer" />
                  {/* Account — parity with the Fleet table's Account column. */}
                  {s.account_id && (
                    <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }} title={s.account_id}>
                      {s.account_id}
                    </span>
                  )}
                  <span className="model-cell">
                    <ModelBadge model={s.model} effort={s.effort} thinking={s.thinking} />{" "}
                    <ProviderBadge provider={s.provider} />
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--fg-1)", lineHeight: 1.45, minHeight: 34 }}>
                  {s.current_task ? (
                    <span className="task-id mono" style={{ marginRight: 6 }}>
                      {s.current_task}
                    </span>
                  ) : taskCellKind(s, group.isCentral, slotRowIsDead(s)) === "unknown" ? (
                    // Honest-absence (Phase 5 [UI] P1) — see taskCellKind and SlotTable's identical branch.
                    <span
                      className="task-id unknown mono"
                      data-testid="node-task-unknown-card"
                      style={{ marginRight: 6 }}
                      title="Central has no in-flight dispatch recorded for this slot right now — not a claim that this slot has no work."
                    >
                      task unknown
                    </span>
                  ) : null}
                  {/* Per-slot usage/spend (ao_v2_fleet_visibility_and_reconciliation_2026_09_03
                Phase 5 UI P2, ~197) — same TokenUsageBadge the Fleet table already renders,
                shared prop logic via slotTokenUsageBadgeProps (falls back to SESSION-scoped
                tokens/spend/turns for a node row, which never carries a current_task-scoped
                breakdown — see that function's own docstring). Honestly blank when even the
                session totals are null (TokenUsageBadge's own null-safe contract). */}
                  <TokenUsageBadge {...slotTokenUsageBadgeProps(s)} />
                  {/* Per-slot resource telemetry (ao_fleet_slot_resource_ui_and_scale_controls_2026_09_05.md
                todo 1) — same formatSlotResources mapper the Fleet table uses, so the card view and table
                view never disagree about what "not measured"/"stale" means. */}
                  {(() => {
                    const cardResources = formatSlotResources(s);
                    return (
                      <span
                        className="mono"
                        data-testid={`slot-resources-card-${slotKey(s)}`}
                        title={cardResources.title}
                        style={{
                          fontSize: 10,
                          color: cardResources.stale ? "var(--status-blocked)" : "var(--fg-3)",
                          marginLeft: 6,
                        }}
                      >
                        {cardResources.text}
                      </span>
                    );
                  })()}
                  {/* Declared-wait indicator (SSOT server/declared_wait.py, 2026-08-23) — same
                additive badge as the Fleet table row, for the mobile card layout. `&& s.tmux_session`
                is the same client-side liveness belt-and-suspenders as the compact row above
                (ao_declared_wait_liveness_contract_2026_08_23.md). */}
                  {s.is_context_plateaued && s.context_plateau_since ? (
                    <span
                      className="context-plateau-badge"
                      data-testid="context-plateau-badge-card"
                      title={`Context% plateaued at ~${s.context_plateau_pct ?? "?"}% since ${fmtClock(s.context_plateau_since)} UTC — possible stuck/looping worker`}
                      style={{
                        display: "inline-block",
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background: "rgba(239,68,68,0.15)",
                        color: "var(--status-blocked)",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        marginRight: 6,
                      }}
                    >
                      ⚠ wedged since {fmtClock(s.context_plateau_since)} UTC
                    </span>
                  ) : null}
                  {s.is_within_declared_wait && s.busy_until && s.tmux_session ? (
                    <span
                      className="declared-wait-badge"
                      data-testid="declared-wait-badge-card"
                      title={`Declared wait active until ${fmtClock(s.busy_until)} UTC`}
                      style={{
                        display: "inline-block",
                        fontSize: 10,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background: "rgba(99,102,241,0.15)",
                        color: "var(--fg-2)",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        marginRight: 6,
                      }}
                    >
                      ⏳ wait until {fmtClock(s.busy_until)} UTC
                    </span>
                  ) : null}
                  {s.last_msg || ""}
                  <GitStatusDetails git={s.git_status} />
                </div>
                <SlotStageMap stage={s.stage} slotKey={slotKey(s)} />
                <CtxBar
                  pct={s.context_used_pct}
                  pressure={s.context_pressure}
                  compactionsLastHour={s.compactions_last_hour}
                  stale={s.context_reading_stale}
                  tokens={s.context_tokens}
                  windowTokens={s.context_window_tokens}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="ago-cell" style={{ fontSize: 11.5 }}>
                    {pingCellText(s, slotRowIsDead(s))}
                  </span>
                  {/* PC8 — the same divergence the table's PING cell warns about, on the card. */}
                  {!slotRowIsDead(s) && workerHeartbeatStalenessKind(s, group.isCentral) === "stale" && (
                    <span
                      className="ago-cell"
                      style={{ color: "var(--status-blocked)", fontSize: 10.5 }}
                      title={`the worker's own heartbeat: ${fmtClock(s.worker_last_heartbeat_at)} UTC — the ping beside it is this NODE's tick, so a wedged worker still reads fresh there`}
                      data-testid={`slot-worker-heartbeat-stale-${slotKey(s)}`}
                    >
                      ⚠ worker {fmtAgo(s.worker_last_heartbeat_at)}
                    </span>
                  )}
                  <DoneBadge outcome={doneOutcome} />
                  <span className="spacer" style={{ flex: 1 }} />
                  <div className="row-actions">
                    {/* Pause/resume/message/reassign are host-qualified backend routes as of
                    ao_operator_actions_ionos_node_slot_parity_2026_09_09 — safe for every row, not
                    just central's; Chat got this treatment first (see the detail-card's identical
                    split above). */}
                    {s.status === "paused" ? (
                      <button className="btn btn-xs" onClick={() => onAct("resume", s)}>
                        <Icon.Play />
                      </button>
                    ) : (
                      <button className="btn btn-xs" onClick={() => onAct("pause", s)}>
                        <Icon.Pause />
                      </button>
                    )}
                    <button className="btn btn-xs" onClick={() => onAct("message", s)} title="Send async message">
                      <Icon.Send />
                    </button>
                    <button className="btn btn-xs" onClick={() => onAct("chat", s)} title="Live chat with this worker">
                      <Icon.Chat />
                    </button>
                    <button
                      className="btn btn-xs btn-danger"
                      disabled={!s.current_task}
                      onClick={() => onAct("reassign", s)}
                    >
                      <Icon.Refresh />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── blocked questions ───────────────────────────────────────────────────

interface BlockedPanelProps {
  queue: BlockedView[];
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole, ruling?: BlockedRuling) => void;
  roles: RoleView[];
  /** Base URL for API calls — passed through to BlockedCard for transcript fetching.
   * Optional: cards without it simply hide the transcript-jump affordance. */
  baseUrl?: string;
}

// operator_gated_blocked_answer_is_a_no_op_2026_07_30 D3/D4: the two PERSISTENT
// supervisors are never valid reclassify targets for an ad-hoc worker task — every
// other role (including the generic `worker`) is a legitimate dropdown choice; the
// operator judges fit from the todo text, same responsibility as at plan-authoring
// time. Keyed by the role's DECLARED `role:` frontmatter value (agents/main.md
// declares `role: project_management`, not "main" — see role_registry.spec_for_file).
const NON_DISPATCH_TARGET_ROLES = new Set(["project_management", "review"]);

/** A `BLK-op-*` blocked_id is the synthetic sentinel bootstrap.py seeds for an
 * [OPERATOR]-gated plan todo (no worker was ever spawned for it) — distinct from
 * an ordinary live worker escalating a real decision to the operator (which also
 * carries authority="operator" but has a real slot behind it, per
 * operator_gated_blocked_answer_is_a_no_op_2026_07_30's D3 investigation). Only
 * the former can accept a ruling_action (the server 400s otherwise) and gets the
 * purple treatment so it reads as "nobody is working this" at a glance. */
function isOperatorGatedRow(q: BlockedView): boolean {
  return q.blocked_id.startsWith("BLK-op-");
}

/** ao_dashboard_escalations_and_pending_filter_2026_08_07: true once
 * blocked_reconcile has actually paged Slack for this row (survived
 * classify_needs_page's grace window unanswered) — the genuine "a human got
 * paged" signal, as opposed to merely "still open" (most open rows resolve
 * within the grace window and never reach this). Deliberately not named
 * "escalated" in the export — that word is already the separate CI/dispatch
 * EscalationsPanel's vocabulary. */
export function isPagedBlocked(q: BlockedView): boolean {
  return q.paged_at !== null;
}

/** True for a to_agent message this agent hasn't /reply-ack'd yet — the same
 * "awaiting a reply" signal already computed server-side for the header's
 * deliveryChip (count_pending_to_agent), applied per-message so a history
 * list can filter down to it. from_agent messages (replies) are never
 * "pending" themselves. */
export function isPendingToAgentMessage(m: ChatMessage): boolean {
  return m.direction === "to_agent" && m.answered_at == null;
}

// operator_gated_blocked_answer_is_a_no_op_2026_07_30 fixed option A (D2) and
// added the reclassify/instruct dispatch path (D3), but canned options B
// ("already done") and C ("not needed") were left submitting as a plain final
// answer -- which nothing materializes into a dispatchable task, so they were
// (and, unfixed, remain) silent no-ops that just hide the row. Route them
// through the SAME proven D3 mechanism instead of adding new server-side
// handling: submit as ruling_action="instruct" with a canned instruction, byte-
// identical in shape to what typing free text on a gated row already does.
// Option A is untouched -- its own D2 partial-upgrade already works correctly
// and a ruling would incorrectly force it "final" (see the endpoint's own
// `if req.ruling_action is not None: partial = False`).
function rulingForCannedOption(option: string, gated: boolean): BlockedRuling | undefined {
  if (!gated) return undefined;
  const letter = option.match(/^([A-Z]):/)?.[1];
  if (letter === "B") {
    return {
      action: "instruct",
      instruction:
        "Operator marked this todo ALREADY DONE. Verify the described work is genuinely complete in the " +
        "codebase (cite the evidence -- commit/file/behavior). If confirmed, flip the checkbox to [x] with that " +
        "evidence in the SAME commit. If it is NOT actually done, do the work first, then flip the checkbox with " +
        "evidence -- do not leave it open on the strength of this instruction alone.",
    };
  }
  if (letter === "C") {
    return {
      action: "instruct",
      instruction:
        "Operator marked this todo NOT NEEDED. Do not implement it. In the SAME commit, flip its checkbox to " +
        '[x] with a one-line rationale (e.g. "Not needed -- operator ruled it out") so it stops being offered, ' +
        "preserving the line for the plan's audit trail rather than deleting it.",
    };
  }
  return undefined;
}

export function BlockedPanel({ queue, onAnswer, roles, baseUrl }: BlockedPanelProps) {
  const [pagedOnly, setPagedOnly] = useState(false);
  const allPending = queue.filter((q) => q.answered_at === null);
  // ao_dashboard_escalations_and_pending_filter_2026_08_07: paged_at is only set
  // once a row survives blocked_reconcile's grace window unanswered (see
  // classify_needs_page) — this is the genuine "a human got paged for this"
  // signal, distinct from "still open" (most open rows never reach it; main
  // answers within the grace window). Deliberately NOT named "escalations" in
  // the UI — that word is already taken by the separate CI/dispatch
  // EscalationsPanel below.
  const pending = pagedOnly ? allPending.filter(isPagedBlocked) : allPending;
  // Telemetry chip (2026-06-12): main-vs-operator answered split + median
  // time-to-answer, computed from the full queue the panel already receives.
  // The main agent is the standing FIRST responder (MainAgentKeeper) — this
  // is its SLA at a glance; GET /api/blocked/stats serves the same rollup to
  // non-UI consumers.
  const answered = queue.filter((q) => q.answered_at !== null);
  const byMain = answered.filter((q) => (q.answered_by ?? "").trim() === "main").length;
  const ttas = answered
    .map((q) => (q.answered_at ? Date.parse(q.answered_at) - Date.parse(q.created_at) : NaN))
    .filter((ms) => Number.isFinite(ms) && ms >= 0)
    .sort((a, b) => a - b);
  const medianMin = ttas.length > 0 ? Math.round(ttas[Math.floor((ttas.length - 1) / 2)] / 60000) : null;
  const statsLabel =
    answered.length > 0
      ? `${byMain}/${answered.length} by main${medianMin !== null ? ` · median ${medianMin}m` : ""}`
      : null;
  return (
    <Panel
      title="Blocked questions"
      collapsible
      count={pending.length}
      right={
        <>
          {statsLabel && <span className="livepill">{statsLabel}</span>}
          {pending.length > 0 && (
            <span className="livepill">
              <span className="dot" style={{ background: "var(--status-blocked)" }} />
              awaiting
            </span>
          )}
          <button
            type="button"
            className="btn btn-xs btn-ghost"
            onClick={() => setPagedOnly((v) => !v)}
            title="Only rows that already paged Slack (survived main's grace window) — hides ones main is likely to still answer on its own."
          >
            {pagedOnly ? "Show all" : "Paged only"}
          </button>
        </>
      }
      flush
      tone="blocked"
    >
      {pending.length === 0 ? (
        <Empty
          icon={<Icon.Check />}
          title={pagedOnly ? "No paged questions" : "No questions waiting"}
          sub={
            pagedOnly
              ? "Nothing has survived the grace window unanswered — main is keeping up."
              : "When a worker or review agent hits ambiguity, it'll show up here."
          }
        />
      ) : (
        <div className="blocked-list">
          {pending.map((q) => (
            <BlockedCard key={q.blocked_id} q={q} onAnswer={onAnswer} roles={roles} baseUrl={baseUrl} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function BlockedCard({
  q,
  onAnswer,
  roles,
  baseUrl,
}: {
  q: BlockedView;
  onAnswer: BlockedPanelProps["onAnswer"];
  roles: RoleView[];
  baseUrl?: string;
}) {
  const [text, setText] = useState("");
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [fromRole, setFromRole] = useState<FromRole>("main");
  const [reclassifyRole, setReclassifyRole] = useState("");
  // blocked_questions_ux_redesign_context_loss_and_scale_2026_07_24 (todo -002):
  // transcript-jump affordance — fetch the ASKING worker's transcript on demand.
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptLines, setTranscriptLines] = useState<string | null>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptAvailable, setTranscriptAvailable] = useState<boolean | null>(null);
  const recIdx = q.recommendation ? q.recommendation.charCodeAt(0) - 65 : -1;
  const inputRef = useRef<HTMLInputElement>(null);
  const gated = isOperatorGatedRow(q);
  const dispatchTargets = roles.filter((r) => !NON_DISPATCH_TARGET_ROLES.has(r.role));

  const submit = (answer: string, ruling?: BlockedRuling) => {
    if (!answer.trim()) return;
    onAnswer(q, answer, fromRole, ruling);
    setText("");
    setSelectedOption(null);
    setReclassifyRole("");
  };

  // blocked_questions_ux_redesign_context_loss_and_scale_2026_07_24 (todo -002):
  // fetch the ASKING worker's transcript on demand. Uses the same
  // transcript_log primitives as the existing slot "Show log" — resolve by
  // claude_session_id (survives respawns), render via render_transcript.
  const toggleTranscript = async () => {
    if (transcriptOpen) {
      setTranscriptOpen(false);
      return;
    }
    setTranscriptOpen(true);
    if (transcriptLines !== null) return; // already fetched
    if (!baseUrl || !q.claude_session_id) {
      setTranscriptAvailable(false);
      return;
    }
    setTranscriptLoading(true);
    try {
      // Dynamic import — api.ts is already a module-level dependency of this
      // file's consumer (App.tsx), so this is a same-bundle call, not a code split.
      const { api } = await import("./api");
      const result = await api.blockedTranscript(baseUrl, q.blocked_id);
      if (result.available && result.lines) {
        setTranscriptLines(result.lines);
        setTranscriptAvailable(true);
      } else {
        setTranscriptAvailable(false);
      }
    } catch {
      setTranscriptAvailable(false);
    } finally {
      setTranscriptLoading(false);
    }
  };

  // Operator-gated (BLK-op-* — no worker was ever spawned for this todo) → purple
  // border, so it reads as "nobody is working this" at a glance, distinct from an
  // ordinary live worker escalating a real decision (authority="operator" → amber).
  // authority="main_agent" → default blue border (main agent may answer).
  const authorityClass = gated
    ? "authority-operator-gated"
    : (q.authority ?? "main_agent") === "operator"
      ? "authority-operator"
      : "authority-main";

  return (
    <div className={`blocked-card ${authorityClass}`}>
      <div className="head">
        {/* The `doc_drift:` prefix lives on TASK_ID, not blocked_id: plan_health.py builds
            `task_id=f"doc_drift:{drift_key}"` and `add_blocked` RETURNS a generated blocked_id
            that never carries the prefix. Keyed off blocked_id (as first shipped), the test
            never matched in production, so every plan_health doc_drift finding rendered the
            "operator-gated" chip instead — telling the operator "no worker was ever spawned,
            a human must reclassify" about a row a recurring system scan had raised. Caught by
            blocked-context-rendering.spec.ts once the e2e harness could run again. */}
        {q.slot_id === -1 ? (
          q.task_id.startsWith("doc_drift:") ? (
            <span
              className="source-chip source-chip-plan-health"
              title="Plan health detector — no originating worker session. This finding was raised by a recurring system scan, not a live worker."
            >
              plan_health
            </span>
          ) : (
            <span
              className="source-chip source-chip-operator-gated"
              title="Operator-gated todo — no worker was ever spawned for this. A human operator must reclassify or instruct."
            >
              operator-gated
            </span>
          )
        ) : (
          <span className="slot-ref">#{q.slot_id}</span>
        )}
        <span className="task-id mono">{q.task_id}</span>
        {gated ? (
          <span
            className="authority-badge authority-badge-gated"
            title="Operator-gated todo — no worker was spawned for this; reclassify it to a role or leave instructions"
          >
            operator-gated
          </span>
        ) : (
          (q.authority ?? "main_agent") === "operator" && (
            <span className="authority-badge" title="Only an operator (Harsh/Ikenna) should answer this question">
              operator-only
            </span>
          )
        )}
        <span className="spacer" />
        <span>{fmtAgo(q.created_at)}</span>
        {q.claude_session_id && baseUrl && (
          <button
            className="btn btn-xs btn-ghost"
            style={{ marginLeft: 8, fontSize: 10.5 }}
            onClick={toggleTranscript}
            title="View the ASKING worker's session transcript — survives respawns, so you see what the agent actually saw even if the slot has since moved on."
          >
            {transcriptOpen ? "Hide transcript" : "View transcript"}
          </button>
        )}
      </div>
      <div className="question">{q.question}</div>
      {q.context != null && (
        <details className="context-details">
          <summary className="context-summary">Context</summary>
          <pre className="context-body">{JSON.stringify(q.context, null, 2)}</pre>
        </details>
      )}
      {q.similar_ids.length > 0 && (
        <div
          className="dedup-banner"
          title={`Same normalized question text as ${q.similar_ids.join(", ")} — the same underlying question was asked more than once.`}
        >
          ⧉ {q.similar_ids.length} other open question{q.similar_ids.length === 1 ? "" : "s"} look like this one
        </div>
      )}
      <div className="options">
        {q.options.map((opt, i) => {
          const letter = opt.match(/^([A-Z]):/)?.[1] ?? String.fromCharCode(65 + i);
          const rest = opt.replace(/^([A-Z]):\s*/, "");
          const rec = i === recIdx;
          const selected = opt === selectedOption;
          return (
            <button
              key={i}
              className={`opt ${rec ? "recommended" : ""} ${selected ? "selected" : ""}`}
              onClick={() => {
                setSelectedOption(opt);
                setText("");
              }}
            >
              <span className="letter">{letter}</span>
              <span style={{ flex: 1 }}>{rest}</span>
              {rec && <span className="rec-tag">worker rec</span>}
            </button>
          );
        })}
        <button
          className="opt"
          onClick={() => {
            setSelectedOption(null);
            setText("");
            inputRef.current?.focus();
          }}
        >
          <span className="letter">…</span>
          <span style={{ flex: 1 }}>Other — type a custom answer</span>
        </button>
      </div>
      {gated && (
        <div className="reclassify-row">
          <select
            value={reclassifyRole}
            onChange={(e) => setReclassifyRole(e.target.value)}
            style={{ minWidth: 180 }}
            title="Dispatch this todo to a worker of this role"
          >
            <option value="">Reclassify to role…</option>
            {dispatchTargets.map((r) => (
              <option key={r.role} value={r.role}>
                {r.role} ({r.model})
              </option>
            ))}
          </select>
          <button
            className="btn btn-sm"
            disabled={!reclassifyRole}
            onClick={() => submit(`Reclassify to ${reclassifyRole}`, { action: "reclassify", role: reclassifyRole })}
          >
            <Icon.Bolt /> Dispatch
          </button>
        </div>
      )}
      {selectedOption && (
        <div className="selected-answer-row">
          <span className="selected-answer-label">
            Selected: <strong>{selectedOption}</strong>
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => submit(selectedOption, rulingForCannedOption(selectedOption, gated))}
          >
            <Icon.Send /> Submit
          </button>
        </div>
      )}
      <div className="answer-row">
        <select
          value={fromRole}
          onChange={(e) => setFromRole(e.target.value as FromRole)}
          style={{ width: "auto", minWidth: 88 }}
          title="Whose decision is this — main agent, review agent, or you (operator)"
        >
          <option value="main">as main</option>
          <option value="review">as review</option>
          <option value="operator">as operator</option>
        </select>
        <input
          ref={inputRef}
          type="text"
          placeholder={
            gated
              ? 'Instructions for whoever picks this up — e.g. "assign to infra, then do X and Y"'
              : "…or type a custom answer"
          }
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSelectedOption(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit(text, gated ? { action: "instruct", instruction: text } : undefined);
          }}
        />
        <button
          className="btn btn-primary btn-sm"
          disabled={!text.trim()}
          onClick={() => submit(text, gated ? { action: "instruct", instruction: text } : undefined)}
        >
          <Icon.Send /> Send
        </button>
      </div>
      {transcriptOpen && (
        <div
          className="blocked-transcript"
          style={{
            marginTop: 10,
            padding: 10,
            background: "var(--bg-2)",
            borderRadius: 6,
            border: "1px solid var(--border-1)",
            maxHeight: 420,
            overflowY: "auto",
          }}
        >
          {transcriptLoading ? (
            <span style={{ color: "var(--fg-2)", fontSize: 12.5 }}>Loading transcript…</span>
          ) : transcriptAvailable === false ? (
            <span style={{ color: "var(--fg-2)", fontSize: 12.5 }}>
              {q.claude_session_id
                ? "Transcript no longer available — the session's log file has been rotated out or was never written."
                : "No transcript available — this question has no originating worker session (pre-migration or synthetic row)."}
            </span>
          ) : transcriptLines ? (
            <pre
              style={{
                margin: 0,
                fontSize: 11.5,
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: "var(--fg-1)",
                fontFamily: "var(--font-mono)",
              }}
            >
              {transcriptLines}
            </pre>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─── backlog integrity (sibling-reset-guard collisions) ─────────────────────
//
// ao_backlog_collision_alert_and_remediation_ui_2026_07_26 todo 4: the guard in
// server/bootstrap.py::sync_backlog_to_db refuses to silently reset a done+
// done_sha TaskRow on a positional task_id collision (todo 1 turns that refusal
// into a `backlog_sibling_reset_guard_refused` activity row; todo 2 pages Slack,
// deduped; todo 3 is the one-click `POST /api/backlog/{id}/remint-collision`
// fix). Without this panel the only way to notice a stuck collision was an
// agent cross-referencing /api/backlog against the DB by hand (see the plan's
// "Why this, why now").

export const COLLISION_EVENT_REFUSED = "backlog_sibling_reset_guard_refused";
export const COLLISION_EVENT_REMINTED = "backlog_sibling_reset_guard_collision_reminted";

/** Reduce a raw activity page (must include both COLLISION_EVENT_* types) to the
 * currently-unresolved collisions: group by task_id, keep only the LATEST event
 * per id (by `id`, falling back to `ts` for rows with a null id), and surface it
 * only if that latest event is still a `_refused` (a later `_reminted` for the
 * same task_id means it was already fixed). Never invents a resolved/unresolved
 * verdict from anything the backend didn't already record as an event — this is
 * why remint is re-validated server-side too (see remint_backlog_collision's own
 * 404-on-already-resolved check); a manual backlog.yaml edit that reverts a
 * collision without going through remint will surface here until the next Fix
 * click 404s and the row is dropped by the caller's own refetch. */
export function unresolvedBacklogCollisions(activity: ActivityEntry[]): BacklogCollisionView[] {
  const latestByTaskId = new Map<string, ActivityEntry>();
  for (const entry of activity) {
    if (entry.event_type !== COLLISION_EVENT_REFUSED && entry.event_type !== COLLISION_EVENT_REMINTED) continue;
    if (!entry.task_id) continue;
    const prior = latestByTaskId.get(entry.task_id);
    if (!prior) {
      latestByTaskId.set(entry.task_id, entry);
      continue;
    }
    const entryKey = entry.id ?? Date.parse(entry.ts);
    const priorKey = prior.id ?? Date.parse(prior.ts);
    if (entryKey > priorKey) latestByTaskId.set(entry.task_id, entry);
  }
  const out: BacklogCollisionView[] = [];
  for (const entry of latestByTaskId.values()) {
    if (entry.event_type !== COLLISION_EVENT_REFUSED || !entry.task_id) continue;
    out.push({
      task_id: entry.task_id,
      incoming_brief: typeof entry.details.incoming_brief === "string" ? entry.details.incoming_brief : "",
      existing_done_sha: typeof entry.details.existing_done_sha === "string" ? entry.details.existing_done_sha : null,
      first_detected_at: entry.ts,
    });
  }
  // Newest-first — a fresh collision is the most actionable one.
  return out.sort((a, b) => Date.parse(b.first_detected_at) - Date.parse(a.first_detected_at));
}

interface BacklogIntegrityPanelProps {
  collisions: BacklogCollisionView[];
  onFix: (taskId: string) => void;
  /** task_id currently mid-Fix — disables just that row's button. */
  fixingId: string | null;
  fixError: string | null;
}

export function BacklogIntegrityPanel({ collisions, onFix, fixingId, fixError }: BacklogIntegrityPanelProps) {
  return (
    <Panel
      title="Backlog integrity"
      count={collisions.length}
      right={
        collisions.length > 0 && (
          <span className="livepill">
            <span className="dot" style={{ background: "var(--status-blocked)" }} />
            unresolved
          </span>
        )
      }
      flush
      tone="blocked"
    >
      {fixError && <div style={{ color: "var(--status-stale)", fontSize: 11.5, padding: "8px 12px" }}>{fixError}</div>}
      {collisions.length === 0 ? (
        <Empty
          icon={<Icon.Check />}
          title="No unresolved id collisions"
          sub="A new checkbox that lands on a reused, already-done task_id shows up here until fixed."
        />
      ) : (
        <div className="blocked-list" data-testid="collision-list">
          {collisions.map((c) => {
            const busy = fixingId === c.task_id;
            return (
              <div key={c.task_id} className="blocked-card authority-operator" data-testid="collision-row">
                <div className="head">
                  <span className="task-id mono">{c.task_id}</span>
                  <span className="spacer" />
                  <span>{fmtAgo(c.first_detected_at)}</span>
                </div>
                <div className="question">{c.incoming_brief}</div>
                <div style={{ fontSize: 11, color: "var(--fg-2)", marginTop: 4 }}>
                  stuck behind done_sha=<span className="mono">{c.existing_done_sha ?? "?"}</span>
                </div>
                <div className="answer-row">
                  <span style={{ flex: 1 }} />
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    data-testid="collision-fix-btn"
                    onClick={() => onFix(c.task_id)}
                  >
                    <Icon.Bolt /> {busy ? "Fixing…" : "Fix"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

// ─── escalations ────────────────────────────────────────────────────────────
// CI-failure (ldr_qg_failure, …) + scheduled-dispatch (plan_health, …)
// escalations currently queued or in-flight (GET /api/escalations/active —
// previously API-only; the deployment-ui Repos-CI board read it but the AO
// dashboard itself had no panel for it, 2026-07-27).

interface EscalationsPanelProps {
  escalations: EscalationView[];
  // Live agent registry (same poll as AgentsPanel) — joined against each
  // dispatched escalation's own AgentRow (agent_id == escalation_id by
  // construction, server/escalation.py "gives a 1:1 AgentRow↔queue join")
  // so the card can say what's ACTUALLY true right now, not just what the
  // escalation-queue row's coarse queued/dispatched status implies. Without
  // this, a one-shot escalation worker that finished in 10s still reads
  // "dispatched to slot N" for up to 2h (the queue row's own active-window,
  // server/escalation.py::list_active_escalations — "escalation workers are
  // one-shot with no completion callback") while the Fleet table already
  // shows slot N on a completely different task, reading as a contradiction.
  agents: AgentView[];
}

// Terminal verdicts written by the escalation watchdog (server/escalation.py
// verify_dispatched_escalations → _mark_resolved / _mark_unresolved_and_maybe_
// reescalate) — it polls the WALL's real signal (PR state / quality-gates-v2),
// not just whether the one-shot worker process is still alive, so this is a
// genuine done/failed distinction, not a liveness proxy.
export function escalationStatusLine(
  e: EscalationView,
  liveAgent: AgentView | undefined,
): { text: React.ReactNode; color: string } {
  switch (e.status) {
    case "queued": {
      // A row that has been looked at under the head-of-line no-capacity stop
      // (server/escalation.py `_stamp_no_capacity_reason`) carries a real reason
      // — show it instead of the generic bare text. A fresh row never yet
      // evaluated has both fields null, so it falls through to the old text.
      // last_error is already capped at 300 chars server-side
      // (`_no_free_slot_reason`'s docstring) and this card has no overflow/nowrap
      // rule, so it wraps rather than needing its own truncation.
      if (e.last_error) {
        const since = e.last_evaluated_at ? ` · since ${fmtAgo(e.last_evaluated_at)}` : "";
        return { text: `${e.last_error}${since}`, color: "var(--fg-2)" };
      }
      return {
        text: `queued — waiting for a free slot${e.attempts > 0 ? ` (attempt ${e.attempts + 1})` : ""}`,
        color: "var(--fg-2)",
      };
    }
    case "resolved":
      return {
        text: `resolved — ${e.resolution ?? "wall cleared"}${e.reescalations > 0 ? ` (after ${e.reescalations} re-escalation${e.reescalations === 1 ? "" : "s"})` : ""}`,
        color: "var(--status-working)",
      };
    case "unresolved":
      return {
        text: `unresolved — ${e.resolution ?? "still red past deadline"} (re-escalated ${e.reescalations}×)`,
        color: "var(--status-stale)",
      };
    case "abandoned":
      return { text: `abandoned — ${e.resolution ?? "expired"}`, color: "var(--status-idle)" };
    case "dispatched":
      if (liveAgent) return { text: `working — slot ${e.slot_id ?? "?"}`, color: "var(--fg-2)" };
      return { text: "worker finished — waiting for the watchdog's verdict", color: "var(--fg-2)" };
    default:
      return { text: e.status, color: "var(--fg-2)" };
  }
}

export function EscalationsPanel({ escalations, agents }: EscalationsPanelProps) {
  const queued = escalations.filter((e) => e.status === "queued").length;
  return (
    <Panel
      title="Escalations"
      collapsible
      count={escalations.length}
      right={
        queued > 0 && (
          <span className="livepill">
            <span className="dot" style={{ background: "var(--status-blocked)" }} />
            {queued} queued
          </span>
        )
      }
      flush
      tone="blocked"
    >
      {escalations.length === 0 ? (
        <Empty
          icon={<Icon.Check />}
          title="No active escalations"
          sub="CI-failure and scheduled-dispatch escalations show up here while queued, in-flight, or recently resolved."
        />
      ) : (
        <div className="blocked-list">
          {escalations.map((e) => {
            // Only "queued" gets the red flag border — nothing is working it
            // yet. Every other status (dispatched/resolved/unresolved/
            // abandoned) gets the neutral border; the status LINE below (with
            // its own color) is what distinguishes success from failure.
            const authorityClass = e.status === "queued" ? "authority-operator" : "authority-main";
            const liveAgent =
              e.status === "dispatched" ? agents.find((a) => a.agent_id === e.escalation_id) : undefined;
            const { text: statusText, color: statusColor } = escalationStatusLine(e, liveAgent);
            const terminalAt =
              e.status === "resolved" || e.status === "unresolved" || e.status === "abandoned" ? e.resolved_at : null;
            // "Took" is dispatched_at -> resolved_at for a terminal row (the
            // real worker-plus-watchdog-poll duration this panel used to bury
            // inside a single ambiguous age badge); for a still-dispatched row
            // there's no end yet, so show live elapsed-since-dispatch instead
            // of leaving the column blank.
            const tookLabel = terminalAt
              ? fmtDurationBetween(e.dispatched_at, terminalAt)
              : e.dispatched_at
                ? `${fmtDuration(Date.now() - new Date(e.dispatched_at).getTime())} so far`
                : "—";
            return (
              <a
                key={e.escalation_id}
                className={`blocked-card ${authorityClass}`}
                href={scopedEscalationPath(e.escalation_id)}
                style={{ display: "block", color: "inherit", textDecoration: "none" }}
                title="open this escalation on its own page"
              >
                <div className="head">
                  <span className="task-id mono">
                    {e.repo}
                    {e.pr_number ? `#${e.pr_number}` : ""}
                  </span>
                  <span className="spacer" />
                  <span>{e.created_at ? fmtAgo(e.created_at) : "—"}</span>
                </div>
                <div className="question">{e.wall_type}</div>
                <div style={{ fontSize: 11, color: statusColor, marginTop: 4 }}>{statusText}</div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    fontSize: 10.5,
                    color: "var(--fg-3)",
                    marginTop: 4,
                  }}
                >
                  <span title={e.created_at ?? undefined}>created {e.created_at ? fmtAgo(e.created_at) : "—"}</span>
                  <span title={e.dispatched_at ?? undefined}>
                    dispatched {e.dispatched_at ? fmtAgo(e.dispatched_at) : "—"}
                  </span>
                  <span title="dispatched_at -> resolved_at (still-dispatched rows show live elapsed)">
                    took {tookLabel}
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

// ─── scheduled jobs ─────────────────────────────────────────────────────────
// The daily-scheduled planning-cleanup jobs (plan_reconciler
// / ag_closeout_auditor / na_eligibility_auditor / context_scout_auditor) dispatch via VM systemd timers
// entirely outside any request the dashboard sees — before
// agent_orchestrator_scheduled_job_observability_2026_07_28 their outcome
// (dispatched / no capacity / a branch-state quarantine / a curl timeout)
// existed ONLY in the VM's local journalctl. Each dispatch-attempt report shows
// up here with a real status + timestamp, and (when a worker was actually
// spawned) a "log" button reusing the existing agent log viewer.

const SCHEDULED_JOB_STATUS_COLOR: Record<ScheduledJobRunView["status"], string> = {
  dispatched: "var(--status-working)",
  // Deferred, not dropped — real tracked work waiting on capacity, so it reads
  // as pending rather than grey/inert like the old no_capacity outcome did.
  queued: "var(--status-queued, var(--fg-2))",
  no_capacity: "var(--fg-2)",
  // Distinct from no_capacity/queued (2026-08-20 split): a deliberate operator
  // hold, not a capacity wait — reuses the same --status-paused var the slot
  // tiles already use for the identical concept (layout.tsx's Paused KPI).
  paused: "var(--status-paused)",
  quarantined: "var(--status-stale)",
  timeout: "var(--status-stale)",
  error: "var(--status-stale)",
};

const SCHEDULED_JOB_STATUS_LABEL: Record<ScheduledJobRunView["status"], string> = {
  dispatched: "dispatched",
  queued: "queued",
  no_capacity: "no capacity",
  paused: "paused",
  quarantined: "quarantined",
  timeout: "timeout",
  error: "error",
};

/** "dispatched" only proves the spawn succeeded — this derives the REAL
 * done-status from the joined AgentRow's is_actually_done flag so a
 * silently-died worker (reaped-stale, dead-tmux-session, or any other
 * non-clean exit) never looks the same as a genuine completion in the UI.
 * Fixed 2026-08-06: the prior version only special-cased "reaped-stale" and
 * let every OTHER non-clean exit_reason (dead-tmux-session, session-reused,
 * stale-no-session) fall through to "complete" — this keys on the backend's
 * is_actually_done instead, which is true ONLY for exit_reason ===
 * "lifecycle-complete", closing that gap. See ScheduledJobRunView's doc
 * comment. */
export type RealRunOutcome = "running" | "complete" | "went_stale" | "unknown";

export function realRunOutcome(r: ScheduledJobRunView): RealRunOutcome {
  if (r.status !== "dispatched") return "unknown";
  if (r.agent_status === "active" || r.agent_status === "stale") return "running";
  if (r.is_actually_done === true) return "complete";
  if (r.is_actually_done === false) return "went_stale";
  return "unknown"; // no dispatch_agent_id, or the AgentRow aged out of retention
}

const REAL_OUTCOME_BADGE: Record<RealRunOutcome, { icon: string; label: string; color: string }> = {
  running: { icon: "▶", label: "running", color: "var(--status-working)" },
  complete: { icon: "✅", label: "complete", color: "var(--status-working)" },
  went_stale: { icon: "⚠️", label: "went stale — never finished", color: "var(--status-stale)" },
  unknown: { icon: "", label: "", color: "var(--fg-3)" },
};

/** Latest run per (job_name, tranche) — finer-grained than `latestRunPerJob`
 * (job_name-only, used for the critical banner) so a sharded auditor's 10
 * tranches are judged independently rather than collapsed onto one. */
function latestRunPerJobTranche(runs: ScheduledJobRunView[]): Map<string, ScheduledJobRunView> {
  const latest = new Map<string, ScheduledJobRunView>();
  for (const r of runs) {
    const key = `${r.job_name}:${r.tranche ?? "-"}`;
    const cur = latest.get(key);
    if (!cur || r.finished_at > cur.finished_at || (r.finished_at === cur.finished_at && r.run_id > cur.run_id)) {
      latest.set(key, r);
    }
  }
  return latest;
}

/** How many distinct (job_name, tranche) instances currently need attention —
 * their LATEST report is still queued/no_capacity — as opposed to
 * `runs.length`, the raw count of dispatch-attempt records in the lookback
 * window. The two diverge whenever a job has cycled through capacity waits
 * and successful dispatches more than once in the window (operator report,
 * 2026-08-19: `runs.length` read as an alarming flat count — e.g.
 * ci_reconciler cycling queued→dispatched→queued 5x in 48h is 5 real,
 * individually-meaningful rows but ZERO currently-outstanding work — the
 * badge should answer "what's still open", not "how much history exists"). */
export function stillOpenScheduledJobCount(runs: ScheduledJobRunView[]): number {
  return [...latestRunPerJobTranche(runs).values()].filter((r) => r.status === "queued" || r.status === "no_capacity")
    .length;
}

/** One row per (job_name, tranche) instead of one row per dispatch-attempt
 * record — the panel used to show na_eligibility_auditor[ui] as TWO separate
 * rows ("queued x3" and "no_capacity x6") for what is the same recurring
 * scheduled task, just two different reported outcomes over time (operator
 * report, 2026-08-20). `history` carries every record for that (job_name,
 * tranche), newest first, so the Status cell can render the full transition
 * history in one place rather than splitting it across rows. `latest` is
 * `history[0]` — used for every OTHER column (real outcome, evidence, ack/
 * duration, log button), since those only make sense for the most recent
 * attempt. */
export function groupScheduledJobRuns(
  runs: ScheduledJobRunView[],
): { key: string; latest: ScheduledJobRunView; history: ScheduledJobRunView[] }[] {
  const groups = new Map<string, ScheduledJobRunView[]>();
  const order: string[] = [];
  for (const r of runs) {
    const key = `${r.job_name}::${r.tranche ?? ""}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(r);
  }
  return order.map((key) => {
    // Sorted explicitly rather than trusting `runs`' own order — this function
    // needs to be correct standing alone, not just when fed an already-sorted
    // (newest-first) API response.
    const history = [...groups.get(key)!].sort(
      (a, b) => new Date(b.finished_at).getTime() - new Date(a.finished_at).getTime(),
    );
    return { key, latest: history[0], history };
  });
}

interface ScheduledJobsPanelProps {
  runs: ScheduledJobRunView[];
  onViewLog: (agentId: string) => void;
}

export function ScheduledJobsPanel({ runs, onViewLog }: ScheduledJobsPanelProps) {
  const failing = runs.filter(
    (r) => r.status === "quarantined" || r.status === "timeout" || r.status === "error",
  ).length;
  // Distinct from `failing`: the dispatch itself succeeded (a worker spawned),
  // but that worker never reached a clean completion — the exact "dispatched
  // != done" gap this panel used to hide entirely.
  const wentStale = runs.filter((r) => realRunOutcome(r) === "went_stale").length;
  const stillOpen = stillOpenScheduledJobCount(runs);
  return (
    <Panel
      title="Scheduled jobs"
      collapsible
      count={stillOpen}
      right={
        <span style={{ display: "flex", gap: 6 }}>
          {stillOpen !== runs.length && (
            <span
              className="livepill"
              title="Total dispatch-attempt records in the last 48h, including already-settled history (successes, past capacity waits) — not all of these need attention."
            >
              of {runs.length} in 48h
            </span>
          )}
          {failing > 0 && (
            <span className="livepill">
              <span className="dot" style={{ background: "var(--status-stale)" }} />
              {failing} failing
            </span>
          )}
          {wentStale > 0 && (
            <span
              className="livepill"
              title="Dispatch succeeded but the worker never reached a clean completion — likely lost work, not just a routine capacity skip."
            >
              <span className="dot" style={{ background: "var(--status-stale)" }} />
              {wentStale} went stale
            </span>
          )}
        </span>
      }
      flush
      tone="blocked"
    >
      {runs.length === 0 ? (
        <Empty
          icon={<Icon.Check />}
          title="No scheduled-job runs in the last 48h"
          sub="plan-reconciler, ag-closeout-auditor, and na-eligibility-auditor report here after every daily dispatch attempt."
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="slot-table scheduled-jobs-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Real outcome</th>
                <th>Reported</th>
                <th>When</th>
                <th title="Time from dispatch-script start to the dispatch HTTP call being acknowledged — proves a worker booted, NOT how long it ran.">
                  Dispatch ack
                </th>
                <th title="Real worker runtime (AgentRow registered_at → finished_at) — only known once the run reaches a terminal state.">
                  Run duration
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groupScheduledJobRuns(runs).map(({ key, latest, history }) => {
                const outcome = realRunOutcome(latest);
                const badge = REAL_OUTCOME_BADGE[outcome];
                return (
                  <tr key={key}>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {latest.job_name}
                      {latest.tranche ? `[${latest.tranche}]` : ""}
                    </td>
                    {/* Full transition history, newest first, one line per reported status —
                        the fix for na_eligibility_auditor[ui] etc. showing as two separate
                        table rows ("queued x3" / "no_capacity x6") instead of one job with a
                        history. verticalAlign: top so a multi-line stack here doesn't drag the
                        other (single-line) cells in this row down to its middle. */}
                    <td style={{ fontSize: 11.5, verticalAlign: "top" }}>
                      {history.map((h) => (
                        <div
                          key={h.run_id}
                          style={{ color: SCHEDULED_JOB_STATUS_COLOR[h.status], whiteSpace: "normal" }}
                          title={h.detail ?? undefined}
                        >
                          {SCHEDULED_JOB_STATUS_LABEL[h.status]}
                          {h.attempts > 1 ? ` ×${h.attempts}` : ""}
                          {" · "}
                          {fmtAgo(h.finished_at)}
                        </div>
                      ))}
                    </td>
                    <td style={{ color: badge.color, fontSize: 11.5 }} title={latest.agent_exit_reason ?? undefined}>
                      {badge.icon} {badge.label}
                    </td>
                    {/* WHAT the run found. For 5 of the 7 scheduled jobs this string is the
                        entire result (chat-text reporting, no structured endpoint), so it is
                        worth a column rather than burying it in the log viewer. Truncated
                        inline with the full text on hover — these run to a few hundred chars. */}
                    <td
                      className="scheduled-job-evidence"
                      style={{ fontSize: 11.5, maxWidth: 320 }}
                      title={latest.agent_done_evidence ?? undefined}
                    >
                      {latest.agent_done_evidence ? (
                        latest.agent_done_evidence
                      ) : (
                        <span style={{ opacity: 0.45 }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: 11.5 }}>{fmtAgo(latest.finished_at)}</td>
                    <td style={{ fontSize: 11.5 }}>{fmtDurationBetween(latest.started_at, latest.finished_at)}</td>
                    <td style={{ fontSize: 11.5 }}>
                      {latest.agent_registered_at
                        ? fmtDurationBetween(latest.agent_registered_at, latest.agent_finished_at ?? undefined)
                        : "—"}
                    </td>
                    <td>
                      {latest.dispatch_agent_id && (
                        <button
                          className="btn btn-xs"
                          onClick={() => onViewLog(latest.dispatch_agent_id!)}
                          title="Tail this run's worker log (auto-refreshes every 5s)"
                        >
                          Log
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ─── scheduled dispatch pause/resume ────────────────────────────────────────
// Per-mode operator kill switch for scheduled dispatch (plan_health.dispatch's
// 10 modes + the separately-ticked ci_reconcile) — mirrors the existing
// per-slot pause/resume, but keyed by scheduled-task-type name rather than a
// numbered worker slot (ao_tmux_session_loss_mid_task_root_cause_2026_08_10,
// 2026-08-11: the API shipped that day; this is the UI toggle for it). Sorted
// alphabetically for a stable render order — `status.modes`/`paused` carry no
// ordering guarantee of their own (server returns them pre-sorted, but the
// panel doesn't rely on that).

interface ScheduledDispatchPanelProps {
  status: ScheduledDispatchStatusView | null;
  onToggle: (mode: string, paused: boolean) => void;
}

export function ScheduledDispatchPanel({ status, onToggle }: ScheduledDispatchPanelProps) {
  const modes = [...(status?.modes ?? [])].sort();
  const pausedSet = new Set(status?.paused ?? []);
  return (
    <Panel
      title="Scheduled dispatch"
      collapsible
      count={modes.length > 0 ? `${pausedSet.size} paused` : undefined}
      flush
      tone="blocked"
    >
      {modes.length === 0 ? (
        <Empty
          title="No scheduled-dispatch modes reported"
          sub="plan_health, ci_reconcile, and the other scheduled task types show up here once the backend reports them."
        />
      ) : (
        <div className="cond-list">
          {modes.map((mode) => {
            const paused = pausedSet.has(mode);
            const details = status?.pause_details?.[mode];
            return (
              <div key={mode} className="cond">
                <span className="name mono" title={mode}>
                  {mode}
                </span>
                {paused && (
                  <span
                    className="mono"
                    style={{ fontSize: 10.5, color: "var(--status-blocked)", marginLeft: 4 }}
                    title={
                      "Scheduled dispatch for this mode is paused — no future plan_health.dispatch/CIReconcileLoop tick will act on it until resumed. Reason: " +
                      (details?.reason ?? "not recorded") +
                      ". Paused at: " +
                      (details?.paused_at || "unknown") +
                      "."
                    }
                  >
                    paused
                  </span>
                )}
                <span className="meta">
                  {paused && details ? details.reason + " · paused at " + (details.paused_at || "unknown") : ""}
                </span>
                <Toggle on={!paused} onChange={(v) => onToggle(mode, !v)} />
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

// ─── prerequisites ──────────────────────────────────────────────────────────

interface PrerequisitesPanelProps {
  prerequisites: Record<string, PrerequisiteView>;
  onToggle: (name: string, value: boolean) => void;
}

// Plan B (audit Ask 3): show the most-actionable few + collapse the rest.
const CONDITIONS_COLLAPSED_COUNT = 5;
// When expanded, cap the visible height at ~15 rows; beyond that the list
// scrolls and is drag-resizable (operator 2026-06-22). ~31rem ≈ 15 × 33px rows.
// `height` (not maxHeight) is the initial size so the native resize handle can
// grow it past the default, not just shrink it.
const CONDITIONS_MAX_VISIBLE = 15;
const CONDITIONS_EXPANDED_HEIGHT = "31rem";

/** Sort prerequisites for display: gates_queued>0 (tasks actually waiting) first,
 * then OFF prerequisites (likely the next gate to flip), then alpha for stability.
 * Returns `[name, view]` tuples so the panel maps directly. */
export function sortPrerequisitesForDisplay(
  prerequisites: Record<string, PrerequisiteView>,
): Array<[string, PrerequisiteView]> {
  return Object.entries(prerequisites).sort(([an, a], [bn, b]) => {
    const aBlocking = a.gates_queued > 0 && !a.value ? 1 : 0;
    const bBlocking = b.gates_queued > 0 && !b.value ? 1 : 0;
    if (aBlocking !== bBlocking) return bBlocking - aBlocking; // blocking gates first
    const aOff = a.value ? 1 : 0;
    const bOff = b.value ? 1 : 0;
    if (aOff !== bOff) return aOff - bOff; // OFF (0) before ON (1)
    return an.localeCompare(bn);
  });
}

export function PrerequisitesPanel({ prerequisites, onToggle }: PrerequisitesPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const sorted = sortPrerequisitesForDisplay(prerequisites);
  const onCount = sorted.filter(([, v]) => v.value).length;
  const total = sorted.length;
  const collapsible = total > CONDITIONS_COLLAPSED_COUNT;
  const shown = expanded || !collapsible ? sorted : sorted.slice(0, CONDITIONS_COLLAPSED_COUNT);
  const hiddenCount = total - shown.length;
  // Once expanded past 15 rows, cap the height + make the list scroll + drag-resize
  // (operator 2026-06-22) so a long prerequisites list never pushes the page down.
  const scrollWhenExpanded = expanded && total > CONDITIONS_MAX_VISIBLE;

  return (
    <Panel title="Prerequisites" count={`${onCount}/${total}`} flush tone="prerequisites">
      {total === 0 ? (
        <Empty
          title="No prerequisites yet"
          sub="Define gates in backlog.yaml; toggling them here unblocks downstream tasks."
        />
      ) : (
        <>
          <div
            className="cond-list"
            style={
              scrollWhenExpanded
                ? { height: CONDITIONS_EXPANDED_HEIGHT, overflow: "auto", resize: "vertical" }
                : undefined
            }
          >
            {shown.map(([name, c]) => (
              <div key={name} className="cond">
                <span className="name" title={name}>
                  {name}
                </span>
                {c.gates_queued > 0 && !c.value && (
                  <span
                    className="mono"
                    style={{ fontSize: 10.5, color: "var(--status-blocked)", marginLeft: 4 }}
                    title={`${c.gates_queued} queued task${c.gates_queued === 1 ? "" : "s"} waiting on this gate`}
                  >
                    gates {c.gates_queued}
                  </span>
                )}
                <span className="meta">{c.set_by ? `${c.set_by} · ${fmtAgo(c.set_at)}` : ""}</span>
                <Toggle on={c.value} onChange={(v) => onToggle(name, v)} />
              </div>
            ))}
          </div>
          {collapsible && (
            <button
              className="btn btn-xs btn-ghost"
              style={{ margin: "6px auto", display: "block" }}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Collapse ▴" : `Show ${hiddenCount} more ▾`}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

// ─── activity feed ───────────────────────────────────────────────────────
//
// Plan B (agent_orchestrator_dashboard_monitoring): server-driven filter tabs
// (the category's event_types are passed to the backend as `types=` CSV so the
// SQL filter runs BEFORE the limit), cursor pagination ("Load older" via
// `before_id`), and ×N row collapse + expand (the "90% repeats" fix).

export type ActivityFilter = "all" | "alerts" | "ops" | "done_passed" | "done_failed";

// The category type-sets are single-sourced here so the SAME list drives both
// the client predicate AND the `types=` CSV sent to the server (audit Ask 2).
export const ALERT_TYPES = [
  "slot_blocked",
  "slot_stale",
  // Section D todo 10 (ao_liveness_unified_design_and_contradiction_closeout_2026_09_16):
  // slot_idle_stale (health.py's idle-slot staleness flip, distinct from slot_stale) hit
  // NEITHER half of the alerts oracle — the failure-suffix regex above only special-cases
  // the exact string "slot_stale", not the "_stale" suffix generally, and this type wasn't
  // in this explicit list either. Measured: 16 occurrences/24h rendered nowhere on the tab.
  "slot_idle_stale",
  // ao_liveness_unified_design_and_contradiction_closeout_2026_09_16.md Section E
  // (2026-09-17, operator-ruled): the future liveness oracle's AMBIGUOUS verdict pages
  // + shows here rather than silently resolving kill-or-wait either way. Not yet emitted
  // by any code path — the oracle itself is still an open Section E build item.
  "slot_liveness_ambiguous",
  "slot_reassigned",
  "slot_compacted",
  "agent_stale",
  "escalation_dispatch_failed",
  "autospawn_failed",
  "account_rotation_failed",
  "agentkeeper_review_failed",
  "watchdog_usage_cap_frozen",
  "failover_rerouted",
  // persistent_slot_tmux_session_hijacked_by_transient_plan_health_dispatch_2026_08_01
  // todo 3: a transient/one-shot dispatch (plan_health/escalation family) cleared its
  // own stale role off a slot reserved for the persistent review role — evidence of a
  // dispatch collision, not routine cleanup. Distinct from the routine
  // stale_spawn_base_role_cleared it always fires alongside.
  "stale_spawn_base_role_collision_detected",
  // ao_human_gated_recovery_audit_closable_gaps_2026_08_06: neither event's TYPE NAME
  // matches isFailureEvent's suffix heuristic (ends in "_reached" / "_cleaned", not
  // "_failed"/"_dead"/etc.), so both were completely invisible on the default activity
  // view despite carrying diagnostically critical information — the exact gap that let
  // slot 10 hit spawn_retry_cap_reached 92x/24h, stuck 20+ hours, with zero visibility
  // short of a direct SQLite query. spawn_retry_cap_reached signals a slot whose
  // diagnostic pane-diagnosis retry is exhausted for this spawn attempt (pane state +
  // retry count in the summary below); quarantine_artifact_auto_cleaned signals the
  // self-heal added alongside this fix actually fired — worth seeing even though it
  // resolved itself, since a slot repeatedly hitting this is a sign something upstream
  // keeps leaving broken artifacts behind.
  "spawn_retry_cap_reached",
  "quarantine_artifact_auto_cleaned",
  // gemini_free_tier_tpm_unmeasured_retry_storm_2026_08_26 [UI] P3: the daily-
  // exhaustion rotation pass (server/worker_liveness/_gemini_exhaustion.py) is
  // log+digest only per the alerting SSOT (never a page), but was previously
  // invisible on the Alerts tab too — an operator could not see WHEN a
  // project exhausted or WHERE its worker went without reading the raw
  // activity feed / querying sqlite directly.
  "gemini_project_daily_exhausted",
  "gemini_daily_exhausted_rotation",
  // worker_context_thrashing_slot_28_2026_08_27 follow-up: the per-project Gemini
  // worker cap (tuning.gemini_max_active_workers_per_project) can still be violated
  // (a TOCTOU race between independent autospawn calls, confirmed live 2026-08-27 —
  // 4 slots landed on one project) — same "was invisible outside raw SQL" gap as
  // the daily-exhaustion pair above, same fix shape.
  "gemini_project_concurrency_violation",
  "gemini_project_concurrency_violation_resolved",
  // m0_bug_register_ao_gemini_project_concurrency_bypass_unowned_2026_10_16 todo 2
  // (2026-10-17): the detector's bounded remediation rotating a slot off an over-cap
  // project. A lifecycle action like gemini_daily_exhausted_rotation — log + digest,
  // never a page — but it must be visible on the Alerts tab, or an operator cannot see
  // that (and where) a worker moved.
  "gemini_project_concurrency_remediated",
  // DeepSeek wallet guard (server/deepseek_wallet_guard.py) writes one state-transition row;
  // the type matches neither this list nor isFailureEvent's regex, so the Slack page fired
  // while the Alerts tab stayed blank.
  "deepseek_wallet_state",
  // provider_concurrency_backoff_alerting_parity_2026_08_27 [BACKEND] P2: was
  // previously relying solely on isFailureEvent's "_failed"-suffix regex fallback
  // to appear on the Alerts tab — correct today, but implicit and one rename away
  // from silently dropping every provider-API-failure alert (gemini, anthropic,
  // deepseek, codex, muse, glm) off the tab with no test catching it. Explicit
  // membership here is load-bearing, not decorative.
  "worker_provider_api_failed",
  // ao_alert_class_rca_2026_09_03 §4 [UI] P2: these five reached NEITHER half of the
  // alerts oracle — none matches isFailureEvent's failure-suffix regex
  // ("_detected"/"_flap"/"_killed" are not in it) and none was in this list — so two
  // flap detectors whose whole purpose is visibility, and three kills the 2026-08-24
  // operator ruling classifies as genuinely costly (see
  // BENIGN_RECYCLE_SIGNAL_EVENTS's exclusion comments (deathRecycle.ts)), never rendered on the
  // Alerts filter at all. Explicit per-type membership (this list's convention)
  // instead of widening the regex: a generic `_killed` suffix would also flag
  // deliberate benign teardowns (worker_one_task_per_session_reset et al.).
  "autospawn_fleet_flap_detected",
  "main_agent_flap",
  "worker_account_unusable_killed",
  "slot_wedged_killed_for_resume",
  "watchdog_slot_killed",
] as const;

export const OPS_TYPES = [
  "task_dispatched",
  "blocked_answered",
  "prerequisite_set",
  "session_checkpoint",
  "slot_paused",
  "slot_resumed",
  "slot_boot",
  "agent_restored",
  "message_sent",
  "peer_agent_message_sent",
] as const;

// ao_kpi_done_vs_detail_mismatch_2026_08_05: a worker's /done call either lands
// (slot_done, paired 1:1 with a slot_done_verified audit record — see
// mergeDonePassed below) or gets bounced by a done-gate 409 (task stays
// dispatched, NOT a completion). A raw activity-log tally that sums every
// "done"-shaped event type — slot_done + slot_done_verified + every rejected
// retry below — reads roughly 2-3x the real completion count (the exact gap
// between the fleet-kpis "done" tile and a manual "how many done tasks" count).
// Splitting these into their own tabs keeps "passed" and "failed" from blending.
// ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18 — slot_done is the
// numbered-slot backlog-worker vocabulary only; escalation and scheduled-task
// (plan_health-family) completions use disjoint event names and were previously
// invisible to this tab entirely (measured: 23 slot_done vs 55 real completions
// in one live window, a ~58% undercount). escalation_resolved/
// escalation_resolved_pre_dispatch (server/escalation.py) and plan_health_result
// (server/plan_health.py) are the success cases for those two roles.
export const DONE_PASSED_TYPES = [
  "slot_done",
  "slot_done_verified",
  "escalation_resolved",
  "escalation_resolved_pre_dispatch",
  "plan_health_result",
] as const;

export const DONE_FAILED_TYPES = [
  "slot_done_rejected_dirty",
  "slot_done_rejected_no_plan_flip",
  "slot_done_rejected_sha_unverifiable",
  "slot_done_rejected_already_done",
  "slot_done_rejected_not_holder",
  "slot_done_rejected_not_on_origin",
  "slot_done_rejected_no_quickmerge",
  // escalation_unresolved (server/escalation.py) is the escalation role's own
  // "attempted but did not close" outcome — explicitly not a success, same
  // ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18 fix as above.
  "escalation_unresolved",
] as const;

/** The comma-separated `types=` value for the dedicated done-activity pull feeding
 * the Fleet "Last done" badge (App.tsx) and the HumanFleet page's own "Last done"
 * column — one source so the two pulls can never drift on which event types count. */
export const DONE_ACTIVITY_TYPES_CSV = [...DONE_PASSED_TYPES, ...DONE_FAILED_TYPES, "tmux_session_lost"].join(",");

/** The comma-separated `types=` value the server query expects for a category
 * tab; `undefined` for "all" (no filter). Single source for client + server. */
export function categoryTypesCsv(filter: ActivityFilter): string | undefined {
  if (filter === "alerts") return ALERT_TYPES.join(",");
  if (filter === "ops") return OPS_TYPES.join(",");
  if (filter === "done_passed") return DONE_PASSED_TYPES.join(",");
  if (filter === "done_failed") return DONE_FAILED_TYPES.join(",");
  return undefined;
}

const ALERT_TYPE_SET: ReadonlySet<string> = new Set(ALERT_TYPES);
const OPS_TYPE_SET: ReadonlySet<string> = new Set(OPS_TYPES);
const DONE_PASSED_TYPE_SET: ReadonlySet<string> = new Set(DONE_PASSED_TYPES);
const DONE_FAILED_TYPE_SET: ReadonlySet<string> = new Set(DONE_FAILED_TYPES);

/** A failure-class event whose `details.error` (if present) we render inline +
 * flag red (audit Ask: surface WHY a dispatch failed without DB access). Open-ended
 * by design — any `*_failed`/`*_dead`/`*_quarantined`/`*_error` event qualifies,
 * so a new failure event type renders its reason without a code change.
 *
 * `slot_dirty_state_resolved` is special-cased against `details`, not the type-name
 * suffix: the SAME event type fires for every resolution outcome (clean / inherited /
 * stashed / quarantined) — routine auto-commits vastly outnumber the quarantined case,
 * so adding the bare type to the suffix regex would flag every routine resolution as a
 * failure. Only `action === "quarantined"` means "this slot is now blocked from ever
 * spawning again until someone/something fixes it" (ao_human_gated_recovery_audit
 * _closable_gaps_2026_08_06 — this exact verdict blocked slot 10 for 20+ hours). */
export function isFailureEvent(eventType: string, details?: Record<string, unknown> | null): boolean {
  if (eventType === "slot_dirty_state_resolved") return details?.action === "quarantined";
  // tmux_session_lost: a one_shot/scheduled worker's routine end-of-task archival
  // (details.archived_lifecycle_complete === true) is success, not a failure — see
  // activityLabel's matching special case above for the full explanation. A
  // slot-scope benign recycle (worker_one_task_per_session_reset et al., see
  // BENIGN_RECYCLE_SIGNAL_EVENTS (deathRecycle.ts)) is the SAME "nothing was actually lost"
  // outcome under a different signal shape (death_class/death_class_signal_event
  // instead of archived_lifecycle_complete) — exempt both
  // (ao_alert_class_rca_2026_09_03, class D1: this was misclassifying the
  // worker_one_task_per_session_reset slice, ~43% of sampled rows, as fleet
  // deaths on the Alerts filter).
  if (eventType === "tmux_session_lost")
    return details?.archived_lifecycle_complete !== true && !isBenignRecycle(details);
  return /(_failed|_fail|_error|_dead|_quarantined|_stuck|_down)$/.test(eventType) || eventType === "slot_stale";
}

/** Pull a human error string out of an activity row's details_json. The server
 * persists the reason under `error` (escalation_dispatch_failed et al.); we also
 * accept `last_error`/`reason`/`detail` so the renderer is robust to the writer. */
export function extractFailureError(details: Record<string, unknown> | null | undefined): string | null {
  if (!details) return null;
  for (const key of ["error", "last_error", "reason", "detail", "message"]) {
    const v = details[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

export interface CollapsedActivity {
  /** Most-recent representative row of the group (drives the row render). */
  head: ActivityEntry;
  /** How many rows collapsed into this group (≥1). Renders as "×N" when >1. */
  count: number;
  /** All rows in the group, newest-first, for the expanded view. */
  rows: ActivityEntry[];
  /** Stable key for React + expand state — event_type[:slot]. */
  key: string;
}

/** Collapse adjacent same-(event_type, slot, host) rows into ×N groups (the "90%
 * repeating alerts" denoise; slot 11 on two hosts is two slots). Input is newest-first; a run of identical
 * (type, slot, host) rows folds into one group keyed by the run, preserving order so
 * distinct bursts stay distinct. */
export function collapseActivity(entries: ActivityEntry[]): CollapsedActivity[] {
  const out: CollapsedActivity[] = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (last && last.head.event_type === e.event_type && last.head.slot_id === e.slot_id && last.head.host === e.host) {
      last.count += 1;
      last.rows.push(e);
    } else {
      out.push({
        head: e,
        count: 1,
        rows: [e],
        key: `${e.event_type}:${e.slot_id ?? "-"}:${e.id ?? out.length}`,
      });
    }
  }
  return out;
}

/** For the "Done ✓" tab: fold each slot_done_verified row into its matching
 * slot_done row (same task_id, always logged in the same /done call — see
 * DONE_PASSED_TYPES above) as `details.verification`, then drop the standalone
 * verified row. One real completion renders as ONE row with its audit detail
 * attached, instead of two lines that read like two completions. A verified row
 * with no matching slot_done in the current page (its pair fell off an older
 * page) is simply dropped rather than shown detail-less.
 *
 * Every OTHER type in DONE_PASSED_TYPES (escalation_resolved,
 * escalation_resolved_pre_dispatch, plan_health_result —
 * ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18) has no
 * slot_done/slot_done_verified pairing concept at all and must pass through
 * unchanged — this function used to hard-filter to ONLY "slot_done", which
 * silently dropped every one of those rows from the tab entirely (the actual
 * render-layer half of that gap; DONE_PASSED_TYPES alone wasn't enough). */
export function mergeDonePassed(entries: ActivityEntry[]): ActivityEntry[] {
  const verifiedByTask = new Map<string, ActivityEntry>();
  for (const e of entries) {
    if (e.event_type === "slot_done_verified" && e.task_id) {
      verifiedByTask.set(e.task_id, e);
    }
  }
  return entries
    .filter((e) => e.event_type !== "slot_done_verified")
    .map((e) => {
      if (e.event_type !== "slot_done") return e;
      const verified = e.task_id ? verifiedByTask.get(e.task_id) : undefined;
      return verified ? { ...e, details: { ...e.details, verification: verified.details } } : e;
    });
}

interface ActivityFeedProps {
  activity: ActivityEntry[];
  /** Selected category tab (lifted to the parent so the server refetch with the
   * category's `types=` CSV is driven from there). Defaults to local-only if absent. */
  filter?: ActivityFilter;
  onFilterChange?: (f: ActivityFilter) => void;
  /** "Load older" — parent fetches `before_id = oldest row id` and appends. */
  onLoadOlder?: (beforeId: number) => void;
  loadingOlder?: boolean;
  /** False once the server returns a short page (no more history). */
  hasMore?: boolean;
  /** `StateResponse.server_started` — threaded down to `activitySummary` so a
   * `tmux_session_lost` burst row can be annotated against a real orchestrator
   * restart/deploy signal (ao_fleet_death_reasons_investigation_and_fixes_
   * 2026_08_25 todo 12) instead of rendering as an unexplained mass death. */
  serverStartedAt?: string | null;
  /** `resolveCentralHostId(state.slots)` — the host id a row reads as "central" (any other host gets its own chip). */
  centralHostId?: string | null;
}

export function ActivityFeed({
  activity,
  filter,
  onFilterChange,
  onLoadOlder,
  loadingOlder = false,
  hasMore = false,
  serverStartedAt,
  centralHostId,
}: ActivityFeedProps) {
  // Self-contained fallback when the parent doesn't lift the filter (e.g. the
  // mobile/no-slots layout): filter client-side over the polled page.
  const [localView, setLocalView] = useState<ActivityFilter>("all");
  const view = filter ?? localView;
  const setView = onFilterChange ?? setLocalView;
  const serverDriven = filter !== undefined; // parent already filtered via `types=`
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Noise toggle: when false (default), hide events not in ALERT_TYPES or OPS_TYPES on the
  // "all" tab — backend heartbeats and polling noise stay hidden until the operator opts in.
  const [showBackendNoise, setShowBackendNoise] = useState(false);
  // Datetime-range filter (default: last 2h). sinceHours=0 means no lower bound.
  const [sinceHours, setSinceHours] = useState(2);
  const q = search.trim().toLowerCase();

  const sinceMs = sinceHours > 0 ? Date.now() - sinceHours * 3600_000 : 0;

  const filtered = activity.filter((a) => {
    // Datetime lower-bound filter.
    if (sinceMs > 0 && new Date(a.ts).getTime() < sinceMs) return false;
    // Only client-filter by category when the parent is NOT server-filtering.
    if (!serverDriven) {
      if (view === "alerts" && !ALERT_TYPE_SET.has(a.event_type) && !isFailureEvent(a.event_type, a.details))
        return false;
      if (view === "ops" && !OPS_TYPE_SET.has(a.event_type)) return false;
      if (view === "done_passed" && !DONE_PASSED_TYPE_SET.has(a.event_type)) return false;
      if (view === "done_failed" && !DONE_FAILED_TYPE_SET.has(a.event_type)) return false;
      // Noise suppression on "all" tab: hide events outside both known sets unless toggled on.
      if (
        view === "all" &&
        !showBackendNoise &&
        !ALERT_TYPE_SET.has(a.event_type) &&
        !OPS_TYPE_SET.has(a.event_type) &&
        !isFailureEvent(a.event_type, a.details)
      )
        return false;
    }
    if (q && !activityHaystack(a).includes(q)) return false;
    return true;
  });

  // "Done ✓" folds each slot_done_verified row into its paired slot_done row
  // (see mergeDonePassed) — the tab's own count/list must use the merged rows,
  // not `filtered` (which still holds both event types and would double the
  // displayed count right back to the "449 vs 239" confusion this tab exists
  // to fix). `oldestId` (the "Load older" cursor) still walks `filtered` so
  // pagination sees every underlying row, verified included.
  const rowsForDisplay = view === "done_passed" ? mergeDonePassed(filtered) : filtered;
  const groups = collapseActivity(rowsForDisplay);
  const oldestId = filtered.length > 0 ? filtered[filtered.length - 1]!.id : null;

  return (
    <Panel
      title="Activity"
      count={rowsForDisplay.length}
      flush
      tone="activity"
      right={
        <Tabs<ActivityFilter>
          value={view}
          onChange={setView}
          options={[
            { value: "all", label: "All" },
            { value: "alerts", label: "Alerts" },
            { value: "ops", label: "Ops" },
            { value: "done_passed", label: "Done ✓" },
            { value: "done_failed", label: "Done ✗" },
          ]}
        />
      }
    >
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid var(--line)",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="search"
          placeholder="Filter by slot (#4), host (ionos-4), task (R-001), or event type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 180,
            fontSize: 12,
            padding: "5px 8px",
            background: "var(--bg-1)",
            color: "var(--fg-0)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            fontFamily: "var(--font-mono)",
          }}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: "var(--fg-2)",
            whiteSpace: "nowrap",
          }}
        >
          Last
          <select
            value={sinceHours}
            onChange={(e) => setSinceHours(Number(e.target.value))}
            style={{
              fontSize: 12,
              padding: "3px 6px",
              background: "var(--bg-1)",
              color: "var(--fg-0)",
              border: "1px solid var(--line)",
              borderRadius: 4,
            }}
          >
            <option value={1}>1h</option>
            <option value={2}>2h</option>
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={0}>All time</option>
          </select>
        </label>
        {view === "all" && (
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "var(--fg-2)",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            <input type="checkbox" checked={showBackendNoise} onChange={(e) => setShowBackendNoise(e.target.checked)} />
            Show backend noise
          </label>
        )}
      </div>
      {rowsForDisplay.length === 0 ? (
        <Empty
          title={q || view !== "all" ? "No matching events" : "No activity to show"}
          sub={
            q || view !== "all"
              ? "Clear the filter to see all events."
              : "Events stream into here every time a worker boots, pings, finishes, or escalates."
          }
        />
      ) : (
        <div className="activity">
          {groups.map((g) => (
            <ActivityGroupRow
              key={g.key}
              group={g}
              expanded={!!expanded[g.key]}
              onToggle={() => setExpanded((m) => ({ ...m, [g.key]: !m[g.key] }))}
              serverStartedAt={serverStartedAt}
              centralHostId={centralHostId}
            />
          ))}
          {onLoadOlder && hasMore && oldestId != null && (
            <button
              className="btn btn-xs btn-ghost"
              style={{ margin: "8px auto", display: "block" }}
              disabled={loadingOlder}
              onClick={() => onLoadOlder(oldestId)}
              title="Fetch older activity from the server (cursor pagination)"
            >
              {loadingOlder ? "Loading…" : "Load older ▾"}
            </button>
          )}
        </div>
      )}
    </Panel>
  );
}

/** A collapsed activity group: renders the representative row + a ×N badge when
 * >1, expandable to show every row in the run. */
function ActivityGroupRow({
  group,
  expanded,
  onToggle,
  serverStartedAt,
  centralHostId,
}: {
  group: CollapsedActivity;
  expanded: boolean;
  onToggle: () => void;
  serverStartedAt?: string | null;
  centralHostId?: string | null;
}) {
  if (group.count === 1)
    return <ActivityRow a={group.head} serverStartedAt={serverStartedAt} centralHostId={centralHostId} />;
  return (
    <>
      <div className="activity-row" style={{ cursor: "pointer" }} onClick={onToggle} title="Click to expand repeats">
        <span className="ts">{fmtClock(group.head.ts)}</span>
        <SlotCell a={group.head} centralHostId={centralHostId} />
        <span className="msg">
          <span className={`type ${activityTypeClass(group.head.event_type)}`}>
            {activityLabel(group.head.event_type)}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 10,
              padding: "0 5px",
              marginRight: 6,
              borderRadius: 8,
              background: "var(--bg-3)",
              color: "var(--fg-1)",
            }}
            title={`${group.count} occurrences collapsed`}
          >
            ×{group.count}
          </span>
          {activitySummary(group.head, { serverStartedAt })}
          <span style={{ color: "var(--fg-3)", marginLeft: 6 }}>{expanded ? "▴" : "▾"}</span>
        </span>
      </div>
      {expanded &&
        group.rows.map((r, i) => (
          <ActivityRow key={r.id ?? i} a={r} indent serverStartedAt={serverStartedAt} centralHostId={centralHostId} />
        ))}
    </>
  );
}

// ── activity row rendering helpers (exported pure for vitest) ──────────────

/** The host chip of an activity row: `central` for the control plane's own host, else the host id without its
 * `ao-` prefix (`ao-ionos-4` → `ionos-4`). Slot ids collide across hosts, so "#11" alone is ambiguous. An inferred
 * host (the server's `host_inferred`) is still SHOWN — `SlotCell` dims it and adds a "not stored" tooltip — because
 * the operator wants a host on every slot-numbered row, and the inferred fallback is `central` (correct for the
 * pre-stamp history, which is central's own). */
export function activityHostChip(host: string | null | undefined, centralHostId: string | null | undefined): string {
  if (!host) return "";
  return host === centralHostId ? "central" : host.replace(/^ao-/, "");
}

/** Lower-cased text the feed's search box matches: type, `#slot`, host, task and the raw payload. */
export function activityHaystack(a: ActivityEntry): string {
  return [
    a.event_type,
    a.slot_id != null ? `#${a.slot_id} slot${a.slot_id}` : "",
    a.host ?? "",
    a.task_id || "",
    a.details ? JSON.stringify(a.details) : "",
  ]
    .join(" ")
    .toLowerCase();
}

/** The `#11 · ionos-4` cell: slot, then its host (subdued for central, emphasised for a node). On a phone
 * (styles.css) the central chip is hidden and a node's stacks under the slot number, the `sep` dot with it. */
export function SlotCell({ a, centralHostId }: { a: ActivityEntry; centralHostId?: string | null }) {
  const chip = activityHostChip(a.host, centralHostId);
  const inferred = a.host_inferred === true;
  const showChip = chip !== "" && (a.slot_id != null || chip !== "central");
  return (
    <span
      className={`slot ${a.slot_id != null ? "has" : ""}`}
      title={a.host ? `host ${a.host}${inferred ? " (inferred — not stored)" : ""}` : undefined}
    >
      {a.slot_id != null ? `#${a.slot_id}` : "—"}
      {showChip && (
        <span className={`${chip === "central" ? "host" : "host node"}${inferred ? " inferred" : ""}`}>
          <span className="sep"> · </span>
          {chip}
        </span>
      )}
    </span>
  );
}

export function activityTypeClass(eventType: string, details?: Record<string, unknown> | null): string {
  if (isFailureEvent(eventType, details)) return "crit";
  if (eventType === "tmux_session_lost" && details?.archived_lifecycle_complete === true) return "ok";
  return (
    (
      {
        slot_blocked: "warn",
        slot_stale: "crit",
        slot_compacted: "warn",
        slot_done: "ok",
        slot_done_verified: "cool",
        slot_done_rejected_dirty: "crit",
        slot_done_rejected_no_plan_flip: "crit",
        slot_done_rejected_sha_unverifiable: "crit",
        slot_done_rejected_already_done: "crit",
        slot_done_rejected_not_holder: "crit",
        slot_done_rejected_not_on_origin: "crit",
        slot_done_rejected_no_quickmerge: "crit",
        blocked_answered: "ok",
        task_dispatched: "cool",
        prerequisite_set: "cool",
        slot_boot: "",
        slot_paused: "warn",
        slot_progress: "",
        session_checkpoint: "cool",
        message_sent: "cool",
        peer_agent_message_sent: "cool",
        // ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18 — explicit here
        // because escalation_unresolved does not end in any of isFailureEvent's
        // recognized failure suffixes (_failed/_error/_dead/...), so it would
        // otherwise fall through to the "" default despite being a real failure.
        escalation_resolved: "ok",
        escalation_resolved_pre_dispatch: "ok",
        escalation_unresolved: "crit",
        plan_health_result: "ok",
        fleet_freeze_armed: "warn",
        fleet_freeze_cleared: "ok",
      } as Record<string, string>
    )[eventType] || ""
  );
}

export function activityLabel(eventType: string, details?: Record<string, unknown> | null): string {
  // tmux_session_lost fires for two OPPOSITE outcomes under one name (server/
  // tmux_pruner.py): a genuinely unexpected session death (persistent worker,
  // no clean /done) AND a one_shot/scheduled worker's ROUTINE end-of-task
  // archival (details.archived_lifecycle_complete === true). Read literally,
  // a burst of the latter looks exactly like a burst of fleet deaths — this is
  // the display-side fix; server/tmux_session_loss_rate_canary.py already
  // excludes lifecycle-complete agents from its own alerting for the same reason.
  if (eventType === "tmux_session_lost" && details?.archived_lifecycle_complete === true) {
    return "task completed";
  }
  return (
    (
      {
        slot_blocked: "blocked",
        slot_stale: "stale",
        slot_compacted: "compacted",
        slot_done: "done",
        slot_done_verified: "verified",
        slot_done_rejected_dirty: "done failed · dirty worktree",
        slot_done_rejected_no_plan_flip: "done failed · no plan flip",
        slot_done_rejected_sha_unverifiable: "done failed · sha unverifiable",
        slot_done_rejected_already_done: "done failed · already done",
        slot_done_rejected_not_holder: "done failed · not holder",
        slot_done_rejected_not_on_origin: "done failed · not on origin",
        slot_done_rejected_no_quickmerge: "done failed · no quickmerge",
        blocked_answered: "answered",
        task_dispatched: "dispatched",
        prerequisite_set: "prerequisite",
        slot_boot: "booted",
        slot_paused: "paused",
        slot_resumed: "resumed",
        slot_progress: "progress",
        session_checkpoint: "snapshot",
        message_sent: "msg",
        peer_agent_message_sent: "peer msg",
        escalation_dispatch_failed: "escalation failed",
        escalation_resolved: "escalation resolved",
        escalation_resolved_pre_dispatch: "escalation resolved · pre-dispatch",
        escalation_unresolved: "done failed · escalation unresolved",
        plan_health_result: "scheduled task done",
        autospawn_failed: "autospawn failed",
        spawn_retry_cap_reached: "spawn retry cap reached",
        quarantine_artifact_auto_cleaned: "quarantine auto-cleaned",
        gemini_project_daily_exhausted: "gemini project daily-exhausted",
        gemini_daily_exhausted_rotation: "gemini worker rotated off exhausted project",
        gemini_project_concurrency_violation: "gemini per-project worker cap violated",
        gemini_project_concurrency_violation_resolved: "gemini per-project worker cap violation resolved",
        gemini_project_concurrency_remediated: "gemini worker rotated off an over-cap project",
        deepseek_wallet_state: "deepseek wallet state",
        fleet_freeze_armed: "fleet freeze armed",
        fleet_freeze_cleared: "fleet freeze cleared",
      } as Record<string, string>
    )[eventType] || eventType
  );
}

/** Whether an activity row's timestamp falls in the window right after the
 * orchestrator process last started (`StateResponse.server_started`, stamped
 * server-side by `server/routes/state.py::get_started_at()`) — the one
 * restart/deploy timestamp source that already exists in this codebase (no
 * new tracking invented per ao_fleet_death_reasons_investigation_and_fixes_
 * 2026_08_25 todo 12's explicit "don't fabricate a source" guard). A
 * multi-slot `tmux_session_lost` burst inside this window is very plausibly
 * the orchestrator coming back up and re-discovering N already-gone tmux
 * sessions in one sweep, not N independent unexplained deaths — but it's a
 * CORRELATION, not proof, so the caller renders it as a hint, never a verdict.
 * Returns `null` (deliberately distinct from `false`) when `serverStartedAt`
 * is unavailable — an absent signal must never be rendered as a confirmed
 * "not near a restart" claim (the same absent-signal-is-not-confirmation
 * discipline `intentional` already applies to `SlotDoneOutcome` above). */
export function burstNearRestart(
  entryTs: string,
  serverStartedAt: string | null | undefined,
  windowSeconds = 300,
): boolean | null {
  if (!serverStartedAt) return null;
  const entryMs = new Date(entryTs).getTime();
  const startMs = new Date(serverStartedAt).getTime();
  if (Number.isNaN(entryMs) || Number.isNaN(startMs)) return null;
  const deltaS = (entryMs - startMs) / 1000;
  // Small negative tolerance for clock skew between the event write and the
  // server_started read; the real signal is "shortly AFTER a restart".
  return deltaS >= -30 && deltaS <= windowSeconds;
}

export function activitySummary(a: ActivityEntry, opts?: { serverStartedAt?: string | null }): string {
  const d = a.details || {};
  switch (a.event_type) {
    case "slot_blocked":
      return typeof d.question === "string" ? d.question : "ambiguity escalated";
    case "slot_stale":
      return `silent ${Math.floor(((d.silence_seconds as number) || 0) / 60)}m (threshold ${Math.floor(((d.threshold_seconds as number) || 0) / 60)}m)`;
    case "slot_compacted":
      return `compactions in last hr: ${d.compactions_last_hour ?? "?"}`;
    case "slot_done": {
      let base = `${a.task_id} → ${typeof d.sha === "string" ? d.sha.slice(0, 7) : "?"}${d.evidence ? " · " + String(d.evidence).slice(0, 80) : ""}`;
      // Merged in by mergeDonePassed (the "Done ✓" tab) from the paired
      // slot_done_verified row — file_count is the cheapest tell that the
      // merge landed, since it's only ever present on that companion event.
      const v = d.verification as Record<string, unknown> | undefined;
      if (v && typeof v.file_count === "number") {
        base += ` · ${v.file_count} file(s) verified${v.verified === false ? " ⚠ unverified" : ""}`;
      }
      // Per-task token usage (input/cache_creation/cache_read/output + $spend — see
      // orm.TaskUsageRow), written alongside this event by done_slot. Full four-
      // category breakdown is in `d.token_usage` / GET /api/backlog/{task_id}/usage;
      // this line is the at-a-glance total, not the whole picture.
      const tu = d.token_usage as Record<string, unknown> | undefined;
      if (tu && typeof tu.input_tokens === "number") {
        const total =
          (tu.input_tokens as number) +
          ((tu.cache_creation_input_tokens as number) || 0) +
          ((tu.cache_read_input_tokens as number) || 0) +
          ((tu.output_tokens as number) || 0);
        const totalStr = total >= 1000 ? `${(total / 1000).toFixed(1)}K tok` : `${total} tok`;
        const spendStr = typeof tu.spend_usd === "number" ? ` ($${tu.spend_usd.toFixed(4)})` : "";
        base += ` · ${totalStr}${spendStr} / ${tu.turn_count ?? "?"} turns`;
        // DeepSeek native-proxy reasoning-token breakdown (deepseek_native_usage_
        // reasoning_tokens_visibility_2026_08_12) — its OWN labeled segment, never
        // folded into `total` above: it's a subset WITHIN output_tokens (not
        // additive spend), so adding it into the total would double-count.
        // Omitted entirely (not "reasoning 0") when null/undefined — no matching
        // DeepSeekNativeUsageRow for this task's claude_session_id.
        if (typeof tu.reasoning_tokens === "number") {
          const rStr =
            tu.reasoning_tokens >= 1000 ? `${(tu.reasoning_tokens / 1000).toFixed(1)}K` : `${tu.reasoning_tokens}`;
          base += ` · reasoning ${rStr} tok`;
        }
      }
      return base;
    }
    case "slot_done_verified": {
      const parts: string[] = [];
      if (typeof d.commit_subject === "string" && d.commit_subject) parts.push(d.commit_subject);
      if (typeof d.file_count === "number") parts.push(`${d.file_count} file(s)`);
      if (d.verified === false) parts.push("⚠ unverified");
      if (d.on_origin === false) parts.push("⚠ not on origin");
      return parts.join(" · ") || "verification recorded";
    }
    case "slot_done_rejected_dirty":
      return `${d.dirty_repo_count ?? "?"} dirty repo(s) — commit/stash before retrying /done`;
    case "slot_done_rejected_no_plan_flip":
      return `commit didn't touch the plan checkbox at ${d.plan_ref ?? "?"}`;
    case "slot_done_rejected_sha_unverifiable":
      return `sha ${typeof d.sha === "string" ? d.sha.slice(0, 7) : "?"} not resolvable${d.error ? " — " + String(d.error).slice(0, 80) : ""}`;
    case "slot_done_rejected_already_done":
      return `already done by ${d.stored_holder ?? "?"} (${typeof d.stored_sha === "string" ? d.stored_sha.slice(0, 7) : "?"})`;
    case "slot_done_rejected_not_holder":
      return `slot no longer holds this task — held by #${d.current_holder ?? "?"}`;
    case "slot_done_rejected_not_on_origin":
      return `sha ${typeof d.sha === "string" ? d.sha.slice(0, 7) : "?"} verified locally but not on origin — push likely failed`;
    case "slot_done_rejected_no_quickmerge": {
      const remedy = d.is_tip === false ? "reprovenance_bypass.sh required (mid-history)" : "re-ship via quickmerge";
      return `${d.repo ?? "?"} bypassed quickmerge since live-defi-rollout — ${remedy}`;
    }
    case "blocked_answered":
      return `"${d.answer}" — ${d.from}`;
    case "task_dispatched":
      return `${a.task_id} · ${d.reason ?? ""}`;
    case "prerequisite_set":
      return `${d.name} = ${String(d.value)} (${d.set_by})`;
    case "fleet_freeze_armed":
      return typeof d.reason === "string" && d.reason ? d.reason : "fleet-wide commit freeze armed";
    case "fleet_freeze_cleared":
      return typeof d.reason === "string" && d.reason ? d.reason : "fleet-wide commit freeze cleared";
    case "slot_boot":
      return `model=${d.model} ctx=${d.context_pct}%`;
    case "slot_progress":
      return typeof d.message === "string" && d.message ? d.message : "ping";
    case "message_sent":
      return `${d.from || "operator"} → #${a.slot_id} · ${d.text}`;
    case "peer_agent_message_sent": {
      // A genuine cross-agent/cross-host send (stamp-matched — see slots_ops.py
      // _PEER_MESSAGE_STAMP_RE), distinguished here from the generic `message_sent` row
      // that always accompanies it: this is the one an operator scanning the feed for
      // "are agents actually talking to each other" should read.
      const to = d.to_session ? `${d.to_host}/${d.to_slot}/${d.to_session}` : `${d.to_host}/${d.to_slot}`;
      return `${d.from_host}/${d.from_slot} → ${to} · ${d.text}`;
    }
    case "session_checkpoint":
      return `reason=${d.reason}`;
    case "spawn_retry_cap_reached": {
      // ao_human_gated_recovery_audit_closable_gaps_2026_08_06: this is the ONE
      // event a stuck-spawn-loop slot (like slot 10, 92x/24h) emits repeatedly —
      // pane_state + retry_count is exactly what you'd otherwise need a direct
      // SQLite query to see.
      const retryCount = d.retry_count ?? "?";
      const paneState = typeof d.pane_state === "string" ? d.pane_state : "unknown";
      const elapsed = typeof d.elapsed_s === "number" ? `${d.elapsed_s}s silent` : "";
      return `retry ${retryCount}/2 · pane=${paneState}${elapsed ? " · " + elapsed : ""}`;
    }
    case "quarantine_artifact_auto_cleaned": {
      const removed = Array.isArray(d.removed) ? (d.removed as unknown[]).join(", ") : "?";
      const resultingAction = typeof d.resulting_action === "string" ? d.resulting_action : "?";
      return `removed ${removed} → now ${resultingAction}`;
    }
    case "slot_dirty_state_resolved": {
      // Only the quarantined case gets a summary here — the routine outcomes
      // (clean/inherited/stashed) are frequent, low-signal, and already render
      // fine with just the type label; quarantined is the one that means
      // "blocked from spawning until fixed" and deserves the detail surfaced.
      if (d.action === "quarantined" && typeof d.detail === "string") return d.detail;
      return "";
    }
    case "tmux_session_lost": {
      // Operator ask (2026-08-24): the Fleet "died mid-task" badge showed a
      // fixed label with NO detail at all — this fell to the `default: ""`
      // below even though server/tmux_pruner.py already logs a rich
      // diagnosis onto this exact event (death_class, resume_decision,
      // pane_death_info, rate_limit_in_tail, task_runtime_seconds — see its
      // log_activity call). Surfacing it here is the only change needed:
      // DoneBadge already threads `outcome.detail` through, it was just
      // never populated for this event type.
      if (d.scope === "agent") {
        // Singleton agents (main/review/scheduled) carry a different, smaller
        // detail shape — no pane_death_info/death_class, see tmux_pruner.py's
        // agent-scope log_activity call.
        const parts: string[] = [];
        // Same gap the slot-scope branch below had: the kill reason was captured but never read.
        if (typeof d.kill_reason === "string" && d.kill_reason) parts.push(d.kill_reason);
        if (typeof d.role === "string" && d.role) parts.push(d.role);
        if (typeof d.current_task === "string" && d.current_task) parts.push(`task: ${d.current_task}`);
        return parts.join(" · ") || "singleton agent session lost";
      }
      const parts: string[] = [];
      // The kill reason is the single most useful field on this event, and it was the ONE field never read here.
      // A lifecycle kill (one_task_per_session_reset / one_shot_done_synchronous_free /
      // node_proxy_done_reset_before_next — all in deathRecycle.ts's BENIGN_RECYCLE_KILL_REASONS) exits the pane
      // with pane_dead_status "" and signal 0 and leaves resume_decision null, so every check below misses and
      // the row rendered the bare "session lost — no further diagnostic captured" fallback even though the
      // reason was right there. Measured 2026-10-14 on the live DB: 410 of 603 tmux_session_lost rows in 12h
      // carried a kill_reason this branch discarded (operator ask: "why all these session lost no further
      // diagnostic captured").
      if (typeof d.kill_reason === "string" && d.kill_reason) parts.push(d.kill_reason);
      if (typeof d.reason === "string" && d.reason && d.reason !== d.kill_reason) parts.push(d.reason);
      if (d.death_class === "intentional_teardown") parts.push("intentional teardown");
      else if (d.death_class === "account_overage_exhausted") parts.push("account overage exhausted");
      else if (d.death_class === "unexplained") parts.push("unexplained death");
      if (typeof d.death_class_signal_event === "string" && d.death_class_signal_event) {
        parts.push(`via ${d.death_class_signal_event}`);
      }
      // fleet_wide_mid_task_death_root_cause_2026_08_21 (operator ask): surface
      // WHAT the slot was working on right alongside the death detail so a
      // pattern across repeatedly-failing tasks is spottable at a glance —
      // already captured server-side (tmux_pruner.py) and already reaches this
      // branch's `details`, just never read here, unlike the agent-scope branch
      // above which already does this.
      if (typeof d.current_task === "string" && d.current_task) parts.push(`task: ${d.current_task}`);
      if (typeof d.resume_decision === "string") parts.push(`resume=${d.resume_decision}`);
      const pdi = d.pane_death_info as Record<string, unknown> | null | undefined;
      if (pdi) {
        const status = pdi.pane_dead_status;
        const signal = pdi.pane_dead_signal;
        if (status || (signal && signal !== "0")) {
          parts.push(`pane exit ${status || "?"}${signal && signal !== "0" ? ` (signal ${signal})` : ""}`);
        }
        const oom = pdi.cgroup_oom_counters as Record<string, unknown> | null | undefined;
        if (oom && Object.values(oom).some((v) => typeof v === "number" && v > 0)) parts.push("⚠ OOM counter nonzero");
      }
      if (d.rate_limit_in_tail === true) parts.push("rate-limit text in pane tail");
      if (typeof d.task_runtime_seconds === "number") {
        parts.push(`ran ${Math.round(d.task_runtime_seconds / 60)}m`);
      }
      if (typeof d.burst_size === "number" && d.burst_size > 1) {
        // ao_fleet_death_reasons_investigation_and_fixes_2026_08_25 todo 12:
        // annotate a multi-slot burst with whether it correlates to a
        // detected orchestrator restart (server_started, threaded in via
        // opts) vs. an ordinary multi-slot recycle the operator has to infer
        // from raw activity queries otherwise.
        const near = burstNearRestart(a.ts, opts?.serverStartedAt);
        const restartNote = near === null ? "" : near ? " · near orchestrator restart" : " · not restart-correlated";
        parts.push(`burst of ${d.burst_size}${restartNote}`);
      }
      return parts.join(" · ") || "session lost — no further diagnostic captured";
    }
    // watchdog_slot_killed (worker_liveness_watchdog.py `_kill_slot`): the
    // event ITSELF carries `details.reason` (context_full/stuck_at_prompt/
    // usage_cap/heartbeat_silent/codex_sandbox_heartbeat_loop/context_burn),
    // but until now activitySummary had no case for it, so this row fell to
    // the `default: ""` below and the dashboard showed only the bare bucket
    // label — this session had to query /api/activity + read details.reason
    // by hand to learn WHY a slot died. Same fix class as the tmux_session_
    // lost case above (ao_fleet_death_reasons_investigation_and_fixes_2026_08_25
    // todo 12), scoped to the standalone watchdog-kill event this time.
    case "watchdog_slot_killed": {
      const reason = typeof d.reason === "string" && d.reason ? d.reason : "unknown reason";
      const killsToday =
        typeof d.kills_today === "number"
          ? ` · kill ${d.kills_today}${typeof d.cap === "number" ? `/${d.cap}` : ""} today`
          : "";
      return `(${reason})${killsToday}`;
    }
    // context_wedge_recovered (context_lifecycle.py `_recover_wedged_target`):
    // /compact repeatedly failed to shrink a saturated session — same
    // reason-surfacing gap as watchdog_slot_killed above, fixed the same way.
    case "context_wedge_recovered": {
      const pct = typeof d.pct === "number" ? `${d.pct}%` : "?";
      const role = typeof d.role === "string" && d.role ? ` · role=${d.role}` : "";
      const forces =
        typeof d.consecutive_ineffective_forces === "number"
          ? ` · ${d.consecutive_ineffective_forces} ineffective /compact(s)`
          : "";
      return `(wedged at ${pct})${role}${forces}`;
    }
    default:
      return "";
  }
}

export interface SlotDoneOutcome {
  passed: boolean;
  /** True when the most recent relevant event is an unresolved `tmux_session_lost` —
   * a third outcome, distinct from passed/failed: the slot never got to call `/done`
   * at all, so this is neither a completion nor an explicit rejection. */
  died?: boolean;
  /** True only when `died` AND the underlying kill is CONFIRMED zero-cost — see
   * `BENIGN_RECYCLE_SIGNAL_EVENTS`'s (deathRecycle.ts) per-site evidence. NOT the same claim as
   * `death_class==="intentional_teardown"` (the server's classifier merely
   * recognizing WHAT killed the session) — the first cut of this field
   * conflated the two (2026-08-24 operator correction): `context_wedge_
   * recovered`/`watchdog_slot_killed`/etc. are equally "recognized" by the
   * classifier but represent a task that WAS interrupted and had to restart,
   * not a routine no-op recycle. `death_class="unexplained"` (the common,
   * ambiguous case — see /plans/active/issues/fleet_wide_mid_task_death_root_
   * cause_2026_08_21.md's 2026-08-24 todo on classifier misses) also stays
   * `false`, since an absent signal is not confirmation of anything benign. */
  intentional?: boolean;
  /** True when this task's session was torn down right after it finished, so the NEXT task starts with a FRESH
   * context (see `isCompletionRecycle`). A disposition ON a real outcome, never an outcome of its own: `passed`
   * still says how the task ended. Absent means no such teardown was seen in the fetched window, which is NOT
   * proof the context was kept. */
  contextReset?: boolean;
  label: string;
  detail: string;
  /** Account/model the worker was bound to WHEN this outcome was recorded — stamped
   * into the event's details server-side. NOT the slot's current binding: after a
   * death the slot respawns onto a possibly different account, so the Fleet
   * Account/Model columns can't answer "what was it running when it died".
   * Absent on events from before the stamp shipped (2026-08-24). */
  accountId?: string;
  model?: string;
  /** What the completed task WAS (operator ask 2026-10-05: the hover showed the commit and
   * file count but not the work). `taskTitle`/`planRef` are stamped into `slot_done` details
   * server-side and are absent on older events; `taskId` comes from the event row itself. */
  taskId?: string;
  taskTitle?: string;
  planRef?: string;
  /** WHY the session ended, as a single classified string (`gemini_resource_exhausted`, `stuck_at_prompt`,
   * `spawn_retry_cap_idle_doa`, `unexplained`, …). Stamped server-side onto the `tmux_session_lost` row by
   * tmux_pruner via server/terminal_outcome.py, which folds four independent evidence channels (a
   * proximate provider-failure row — `worker_provider_api_failed` OR `provider_failure_deferred` — the pane
   * tail, the recorded kill reason, the death class)
   * most-specific-first. This is the field that makes the badge able to answer "why" and not only "that".
   * Absent on deaths recorded before 2026-10-16, which degrade to the generic detail line. */
  terminalReason?: string;
  /** Which evidence channel produced `terminalReason` — `transcript` | `pane_tail` | `kill_reason` |
   * `death_class`. The trust level, and the reason it is surfaced rather than hidden: a `transcript`
   * reason shares that signature scan's known self-match caveat (a worker merely PRINTING an error string
   * produces a matching row), whereas `kill_reason` is a recorded fact. */
  terminalReasonSource?: string;
  /** Provider / gcp_project / limit axis, present only when the `transcript` channel supplied them. */
  terminalReasonDetail?: { provider?: string; gcp_project?: string; limit_axis?: string };
}

/** Composite (host, slot_id) key for a done-outcome lookup — mirrors `slotKey`'s
 * rationale: bare slot_id collides across hosts BY DESIGN, so a done event is only
 * attributable to a fleet row when its host matches too. `host` is stamped into the
 * slot_done/tmux_session_lost details by the server as the completing task's
 * dispatched_to_host (None = central, the same NULL=central equivalence TaskRow
 * itself uses); events from before the stamp shipped key as central, which is
 * exactly this map's central-only behavior before node rows rendered at all. */
export function doneEventKey(host: string | null | undefined, slotId: number): string {
  return `${host ?? "central"}:${slotId}`;
}

/** Fleet "Done" badge (ao_done_categorization_display_and_quickmerge_gate_2026_08_06):
 * the most recent real completion (`slot_done`) or rejected attempt
 * (`DONE_FAILED_TYPES`) per slot, so the same Done ✓/✗ distinction the Activity
 * panel already makes is visible right on the Fleet row without switching panels.
 * Deliberately ignores `slot_done_verified` — it's a companion confirmation of an
 * already-counted `slot_done`, not a second outcome (mirrors mergeDonePassed's own
 * "don't double count" reasoning for the Done ✓ tab). `activity` is assumed
 * newest-first (id descending, the feed's own pagination order), but this sorts a
 * copy ascending by `id` and lets the latest event per slot win, so it's correct
 * whatever order the feed arrives in.
 *
 * A post-completion teardown (`isCompletionRecycle`) is NOT a death: it keeps the real
 * outcome of the task it followed (matched on task id) and marks it `contextReset`;
 * with no matching completion row in the window (a one-shot's `/done` leaves none) it
 * stands in as a plain "done" carrying the same mark.
 *
 * Also tracks `tmux_session_lost` (fleet_wide_deepseek_crash_loop_undetected_2026_08_11
 * follow-up): neither `slot_done` nor `DONE_FAILED_TYPES` — a session that dies mid-task
 * never gets to call `/done`, rejected or otherwise — so without this the badge silently
 * kept showing the last REAL completion through any number of later mid-task deaths, with
 * no staleness signal at all. A `tmux_session_lost` more recent than the last real
 * done/rejection now wins as its own "died mid-task" outcome instead of being invisible. */
export function latestDoneOutcomeBySlot(activity: ActivityEntry[]): Map<string, SlotDoneOutcome> {
  const out = new Map<string, SlotDoneOutcome>();
  const ascending = [...activity].sort((x, y) => (x.id ?? 0) - (y.id ?? 0));
  for (const a of ascending) {
    if (a.slot_id == null || a.id == null) continue;
    // Keyed by (host, slot_id) — see doneEventKey above. `details.host` is
    // typeof-guarded: pre-2026-09-08 events carry no host stamp and correctly
    // key as central (the map's behavior before node rows rendered).
    const key = doneEventKey(typeof a.details.host === "string" ? a.details.host : null, a.slot_id);
    // ao_dashboard_activity_log_role_vocabulary_gap_2026_08_18 — was hardcoded to
    // literal "slot_done", so an escalation_resolved/plan_health_result row (now
    // fetched by DONE_ACTIVITY_TYPES above) still fell through to `continue`
    // below and was silently dropped a second time. DONE_PASSED_TYPE_SET covers
    // all of them (plus slot_done_verified, already handled correctly — it just
    // relabels as "done" like slot_done itself, see the label ternary below).
    const isPassed = DONE_PASSED_TYPE_SET.has(a.event_type);
    const isFailed = DONE_FAILED_TYPE_SET.has(a.event_type);
    const isDied = a.event_type === "tmux_session_lost";
    if (!isPassed && !isFailed && !isDied) continue;
    // Account/model stamped into the event server-side (slot_done + tmux_session_lost) —
    // typeof-guarded rather than cast because pre-stamp rows simply lack the keys.
    const accountId = typeof a.details.account_id === "string" ? a.details.account_id : undefined;
    const model = typeof a.details.model === "string" ? a.details.model : undefined;
    const nonEmpty = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
    const taskId = nonEmpty(a.task_id);
    const taskTitle = nonEmpty(a.details.task_title);
    const planRef = nonEmpty(a.details.plan_ref);
    // Terminal reason (2026-10-16) — typeof-guarded rather than cast, same as accountId/model above,
    // because a death row recorded before the stamp simply lacks these keys.
    const terminalReason = nonEmpty(a.details.terminal_reason);
    const terminalReasonSource = nonEmpty(a.details.terminal_reason_source);
    const rawReasonDetail = a.details.terminal_reason_detail;
    const terminalReasonDetail =
      rawReasonDetail !== null && typeof rawReasonDetail === "object"
        ? (rawReasonDetail as SlotDoneOutcome["terminalReasonDetail"])
        : undefined;
    if (isDied && isCompletionRecycle(a.details)) {
      // Keep the real outcome only when it is provably the SAME task: matching on slot alone would pin an
      // earlier, unrelated task's title onto this teardown.
      const prior = out.get(key);
      out.set(
        key,
        prior && !prior.died && taskId && prior.taskId === taskId
          ? { ...prior, contextReset: true }
          : {
              passed: true,
              contextReset: true,
              label: "done",
              detail: activitySummary(a),
              accountId,
              model,
              taskId,
              taskTitle,
              planRef,
              terminalReason,
              terminalReasonSource,
              terminalReasonDetail,
            },
      );
      continue;
    }
    out.set(
      key,
      isDied
        ? {
            passed: false,
            died: true,
            intentional: isBenignRecycle(a.details),
            label: "died mid-task",
            detail: activitySummary(a),
            accountId,
            model,
            taskId,
            taskTitle,
            planRef,
            terminalReason,
            terminalReasonSource,
            terminalReasonDetail,
          }
        : {
            passed: isPassed,
            label: isPassed ? "done" : activityLabel(a.event_type),
            detail: activitySummary(a),
            accountId,
            model,
            taskId,
            taskTitle,
            planRef,
            terminalReason,
            terminalReasonSource,
            terminalReasonDetail,
          },
    );
  }
  return out;
}

/** What a slot-bound typed agent (cicd / plan_health / data_pipeline_failure) is actually
 * working on, keyed by the slot it runs in.
 *
 * A typed one-off holds no backlog task, so its SlotRow's `current_task` is null and the
 * Fleet row falls through to a bare `<RoleBadge>` — the operator sees "cicd" and nothing
 * else, while the Agents panel shows the real work ("unified-trading-pm#2709 —
 * sit_failure") the whole time. Same session, two panels, one of them mute. The agent
 * carries it as `current_task`/`source`; the slot never learns it, so join here on the
 * `orch-slot-N` session name rather than adding a backend field for something the client
 * already has in hand. Mirrors `latestDoneOutcomeBySlot` above: derive a Map in App and
 * pass it down, so SlotTable/SlotCards stay presentational.
 */
/** Whether an agent's context reading is a MEASUREMENT rather than an absent one.
 *
 * `AgentRow.context_used_pct` is `nullable=False, default=0`, so the column cannot
 * distinguish "never sampled" from "measured empty" — the ambiguity that produced three
 * separate bugs in ao_context_pct_0_for_monitor_heavy_workers_2026_07_29 (a fabricated
 * compaction, a clobbered slot reading, and nearly a silent dispatch-gate bypass). The
 * request models now express it (`int | None`), but the DB and this view still cannot.
 *
 * So the panel declines to assert what it does not know: 0 renders as "—", not "0%".
 * Rendering "0%" claims a measurement, and for the roles that never report one it claimed
 * it indefinitely — an operator reasonably read a busy agent as idle (the 2026-08-10
 * report that opened this issue). The cost is that a genuinely-just-booted session shows
 * "—" for the seconds before its first real reading, which understates rather than
 * misleads. Not a heuristic guess between the two cases: with no persisted sentinel they
 * are indistinguishable here, and inventing a guess would re-conflate them differently.
 */
export function agentContextIsReported(pct: number | null | undefined): boolean {
  return typeof pct === "number" && pct > 0;
}

export function agentWorkBySlot(agents: AgentView[]): Map<number, { task: string; source: string | null }> {
  const out = new Map<number, { task: string; source: string | null }>();
  for (const a of agents) {
    if (!a.tmux_session?.startsWith("orch-slot-")) continue;
    if (!a.current_task) continue;
    const slotId = Number(a.tmux_session.slice("orch-slot-".length));
    if (!Number.isInteger(slotId)) continue;
    // An archived record can linger on a slot a live agent has since taken over; the
    // live one must win regardless of iteration order.
    if (a.status === "archived" && out.has(slotId)) continue;
    out.set(slotId, { task: a.current_task, source: a.source });
  }
  return out;
}

/** Returns true iff `details.transcript_excerpt` is a non-empty string — the
 * shape `server/provider_failure_alert.py::check_slot_for_provider_api_failure`
 * writes on `worker_provider_api_failed` (and any future event type that
 * carries the same field gets the same pretty rendering, no allowlist by
 * event_type needed). */
export function detailsHaveTranscriptExcerpt(details: Record<string, unknown> | null | undefined): boolean {
  return typeof details?.transcript_excerpt === "string" && details.transcript_excerpt.length > 0;
}

/** `details` minus the two fields the pretty renderer already surfaces on its
 * own (chips for `matched_signatures`, TranscriptExcerptBlocks for
 * `transcript_excerpt`) — everything else (e.g. `transcript_path`) still
 * renders, same as the old raw-JSON dump, just without duplicating the two
 * fields that now have a dedicated view. */
export function otherDetailFields(details: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(details).filter(([k]) => k !== "transcript_excerpt" && k !== "matched_signatures"),
  );
}

/** Alerts detail expander for a row whose `details.transcript_excerpt` is
 * present — pretty-renders the excerpt via TranscriptExcerptBlocks +
 * matched_signatures chips instead of the bare JSON dump every other event
 * type still gets (glm_fair_usage_throttle_router_blind_2026_09_02.md
 * operator ask: "the Alerts detail should read like the Logs view, not a raw
 * transcript view"). A "raw" toggle restores the ORIGINAL behaviour byte-for-
 * byte (`JSON.stringify(details, null, 2)`) for exact-fidelity debugging. */
function ActivityTranscriptDetail({ details }: { details: Record<string, unknown> }) {
  const [showRawJson, setShowRawJson] = useState(false);
  const excerpt = typeof details.transcript_excerpt === "string" ? details.transcript_excerpt : "";
  const blocks = useMemo(() => parseTranscriptExcerpt(excerpt), [excerpt]);
  const otherDetails = useMemo(() => otherDetailFields(details), [details]);
  const hasOtherDetails = Object.keys(otherDetails).length > 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--fg-2)" }}>
          transcript excerpt
        </span>
        <button
          type="button"
          className="mono"
          style={{
            fontSize: 10,
            padding: "0 5px",
            borderRadius: 3,
            background: "transparent",
            color: "var(--fg-2)",
            border: "1px solid var(--fg-2)",
            cursor: "pointer",
          }}
          onClick={() => setShowRawJson((v) => !v)}
          title="Toggle between the pretty per-turn rendering and the original details_json payload"
        >
          {showRawJson ? "pretty ▴" : "raw ▾"}
        </button>
      </div>
      {showRawJson ? (
        <pre className="mono">{JSON.stringify(details, null, 2)}</pre>
      ) : (
        <>
          <MatchedSignatureChips signatures={details.matched_signatures} />
          <TranscriptExcerptBlocks blocks={blocks} />
          {hasOtherDetails && (
            <>
              <div
                style={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "var(--fg-2)",
                  margin: "8px 0 4px",
                }}
              >
                other fields
              </div>
              <pre className="mono">{JSON.stringify(otherDetails, null, 2)}</pre>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** The model (and provider, when stamped) a row RAN ON, or undefined.
 *
 * Operator ask 2026-10-17: the Activity feed showed what a slot DID but never WHICH model did
 * it, so a `done` row could not be attributed to a provider without expanding the raw payload.
 * The server has stamped `details.model` / `details.account_id` / `details.provider` since
 * 2026-08-24 and `SlotDoneOutcome` already reads them; the feed simply never rendered them.
 * Closes the same gap `ao_deepseek_provider_model_telemetry_mislabeled_2026_08_06` closed for
 * `slot_boot` rows, whose own done-when was "a human/agent reading `activity_log` alone should
 * be able to tell the real model".
 *
 * `model` is preferred over `account_id`: for Gemini the account id IS the model string
 * (`gemini-3-5-flash-lite-proj9`), while for Anthropic the account is an identity and the
 * model is the substance. Undefined when the event predates the stamp or carries neither key,
 * so older rows render exactly as before. */
export function activityModelBadge(a: ActivityEntry): { label: string; provider?: string; title: string } | undefined {
  const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
  const model = str(a.details.model);
  const account = str(a.details.account_id);
  const provider = str(a.details.provider);
  const label = model ?? account;
  if (!label) return undefined;
  const title = [provider ? `provider ${provider}` : "", account && account !== label ? `account ${account}` : ""]
    .filter(Boolean)
    .join(" · ");
  return { label, provider, title: title || label };
}

function ActivityRow({
  a,
  indent = false,
  serverStartedAt,
  centralHostId,
}: {
  a: ActivityEntry;
  indent?: boolean;
  serverStartedAt?: string | null;
  centralHostId?: string | null;
}) {
  const [showError, setShowError] = useState(false);
  // Raw-payload expand (operator ask, 2026-08-13): many event types (e.g.
  // `deepseek_spawn_selected`, `escalation_held_past_ttl`,
  // `context_force_idle_gate_blocked`) have no curated `activitySummary()`
  // text at all — the row shows only the bare type label with zero detail,
  // and the row-level `slot_id` column is itself often null for these even
  // though the payload carries slot/task context. This is a universal
  // escape hatch: every row can expand to its full `details` payload,
  // independent of whether a curated summary exists.
  const [showRaw, setShowRaw] = useState(false);
  const failureError = isFailureEvent(a.event_type, a.details) ? extractFailureError(a.details) : null;
  const summary = activitySummary(a, { serverStartedAt });
  const hasDetails = a.details != null && Object.keys(a.details).length > 0;
  const hasTranscriptExcerpt = detailsHaveTranscriptExcerpt(a.details);

  return (
    <div className="activity-row" style={indent ? { paddingLeft: 18, opacity: 0.85 } : undefined}>
      <span className="ts">{fmtClock(a.ts)}</span>
      <SlotCell a={a} centralHostId={centralHostId} />
      <span className="msg">
        <span className={`type ${activityTypeClass(a.event_type, a.details)}`}>
          {activityLabel(a.event_type, a.details)}
        </span>
        {summary}
        {(() => {
          const badge = activityModelBadge(a);
          return badge ? (
            <span
              className="mono"
              style={{
                fontSize: 10,
                padding: "0 5px",
                marginLeft: 6,
                borderRadius: 8,
                background: "var(--bg-3)",
                color: "var(--fg-1)",
                whiteSpace: "nowrap",
              }}
              title={badge.title}
              data-testid={`activity-model-${badge.label}`}
            >
              {badge.label}
            </span>
          ) : null;
        })()}
        {hasDetails && (
          <button
            className="mono"
            style={{
              fontSize: 10,
              padding: "0 5px",
              marginLeft: 4,
              borderRadius: 3,
              background: "transparent",
              color: "var(--fg-2)",
              border: "1px solid var(--fg-2)",
              cursor: "pointer",
            }}
            onClick={() => setShowRaw((v) => !v)}
            title="Show the full event payload (details_json) — includes any slot/task/account context not shown in this row's own columns"
          >
            {showRaw ? "hide detail ▴" : "detail ▾"}
          </button>
        )}
        {failureError && (
          <button
            className="mono"
            style={{
              fontSize: 10,
              padding: "0 5px",
              marginLeft: 4,
              borderRadius: 3,
              background: "var(--status-stale, #b91c1c)",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
            onClick={() => setShowError((v) => !v)}
            title="Show the failure reason (details_json.error)"
          >
            {showError ? "hide reason ▴" : "why? ▾"}
          </button>
        )}
      </span>
      {/* Expanded content renders OUTSIDE .msg deliberately — .msg has a 2-line
       * -webkit-line-clamp (overflow: hidden) for the collapsed summary, which
       * silently clipped this content to near-nothing when it was nested inside
       * (the "detail expands to empty, can't copy it" bug: the full JSON WAS in
       * the DOM, just clipped past line 2 with no visible overflow). A sibling
       * spanning the full grid row has no such clamp. */}
      {((showRaw && hasDetails) || (showError && failureError)) && (
        <div className="mono activity-row-detail" style={{ gridColumn: "1 / -1" }}>
          {showRaw && hasDetails && !hasTranscriptExcerpt && <pre>{JSON.stringify(a.details, null, 2)}</pre>}
          {showRaw && hasDetails && hasTranscriptExcerpt && <ActivityTranscriptDetail details={a.details} />}
          {showError && failureError && <span className="activity-row-detail-error">{failureError}</span>}
        </div>
      )}
    </div>
  );
}

// ─── backlog summary ─────────────────────────────────────────────────────

/** Backlog task ids are `<plan_slug>-<12-hex-char-id>` (`plans/PLAN_FORMAT.md`
 * backlog task-id convention) — recovers the owning plan's slug by stripping that
 * suffix. Falls back to the id verbatim when it doesn't match the convention (e.g.
 * synthetic e2e-fixture ids like "E2E-QUEUED") so a non-conforming id still renders
 * as its own one-entry group below rather than throwing or silently merging with
 * something unrelated. */
export function planSlugFromBacklogTaskId(taskId: string): string {
  const m = /^(.+)-[0-9a-f]{12}$/.exec(taskId);
  return m ? m[1] : taskId;
}

export interface TopBlockerGroup {
  planSlug: string;
  /** [taskId, count] pairs sharing `planSlug`, in the server's original
   * (count-DESC) insertion order. */
  entries: Array<[string, number]>;
}

/** Groups `IdleFleetBlockersView.top_blockers` (ao_fleet_slot_state_reason_
 * tooltips_2026_08_27.md Track H [UI] P2, regrouped by-plan per the 2026-09-03
 * operator report — "five stacked unreadable boxes", one per task id, when they're
 * usually all predecessor tasks of the SAME plan) by owning plan slug — task_id ->
 * count of queued tasks blocked behind it, already server-sorted count DESC and
 * capped at <=5 entries; this never re-sorts, only groups. A plan_slug that owns
 * only one of the top-blocker entries still gets its own one-entry group (see
 * formatGroupedBlockerLine's "1 task blocks N queued" grammar) so every entry
 * renders through exactly one code path instead of branching singleton-vs-grouped.
 * Exported for direct unit-testing (same convention as formatFreezeCountdown
 * above). */
export function groupTopBlockersByPlan(topBlockers: Record<string, number> | null | undefined): TopBlockerGroup[] {
  const groups = new Map<string, TopBlockerGroup>();
  for (const [taskId, count] of Object.entries(topBlockers ?? {})) {
    const planSlug = planSlugFromBacklogTaskId(taskId);
    const existing = groups.get(planSlug);
    if (existing) existing.entries.push([taskId, count]);
    else groups.set(planSlug, { planSlug, entries: [[taskId, count]] });
  }
  return [...groups.values()];
}

/** "<plan_slug> — N tasks block M queued" (operator-specified format,
 * ao_review_queue_mandatory_coverage_2026_09_02.md). M is each entry's own count —
 * these are near-always identical across one plan's entries (a `gate_on_depends`
 * fan-in: every one of the M downstream tasks lists ALL of that plan's currently-
 * unmet predecessor ids in its own `completed_tasks` prereqs, so each predecessor
 * independently shows the SAME M downstream tasks as blocked-behind-it — see
 * server/worker_liveness/_git_alerts.py::fleet_blocked_task_reasons, which emits
 * one "task <tid>" reason per unmet prereq id per queued task, and
 * routes/state.py's top_blockers builder counts how many queued tasks name each
 * tid). Falls back to "up to M" only in the (believed rare) case a plan's entries
 * genuinely disagree, so the line never overstates a smaller true count. */
export function formatGroupedBlockerLine(group: TopBlockerGroup): string {
  const n = group.entries.length;
  const counts = group.entries.map(([, c]) => c);
  const uniform = counts.every((c) => c === counts[0]);
  const m = Math.max(...counts);
  const taskWord = n === 1 ? "task" : "tasks";
  const blockWord = n === 1 ? "blocks" : "block";
  return `${group.planSlug} — ${n} ${taskWord} ${blockWord} ${uniform ? "" : "up to "}${m} queued`;
}

/** Formats the Backlog header's top-blockers tooltip/badge text, grouped by plan
 * (see groupTopBlockersByPlan/formatGroupedBlockerLine). Returns "" when there's
 * nothing to show (field absent/empty) — tolerates the field being missing for one
 * deploy cycle the same way every other new StateResponse field in this file does.
 * Exported for direct unit-testing. */
export function formatTopBlockersGroupedTooltip(topBlockers: Record<string, number> | null | undefined): string {
  const groups = groupTopBlockersByPlan(topBlockers);
  if (groups.length === 0) return "";
  return `blocked behind (N = queued tasks blocked behind that task): ${groups.map(formatGroupedBlockerLine).join("; ")}`;
}

export function BacklogSummary({
  backlog,
  parkedCount,
  parkedNeedsHumanCount,
  topBlockers,
  onOpenDetail,
  onOpenBlockerTask,
  onReloadYaml,
  reloading,
}: {
  backlog: StateResponse["backlog_summary"];
  parkedCount?: number;
  parkedNeedsHumanCount?: number;
  /** IdleFleetBlockersView.top_blockers (ao_fleet_slot_state_reason_tooltips_2026_08_27.md
   * Track H [UI] P2) — task_id -> count of queued tasks blocked behind it. Optional/
   * possibly absent for one deploy cycle while the backend field rolls out; renders
   * nothing extra when undefined/empty. */
  topBlockers?: Record<string, number>;
  onOpenDetail?: () => void;
  /** Opens Backlog -> Detail pre-filtered ("all") and expanded/scrolled to one
   * specific task — used by each top-blocker chip below ("each blocker linking to
   * its task row", same convention as BacklogRelations.tsx's jumpTo/bl-task-${id}).
   * Separate from onOpenDetail (which opens with no target task). */
  onOpenBlockerTask?: (taskId: string) => void;
  onReloadYaml?: () => void;
  reloading?: boolean;
}) {
  const total = backlog.queued + backlog.dispatched + backlog.done + backlog.cancelled || 1;
  const pct = (n: number) => (n / total) * 100;
  // Not memoized: `topBlockers` is a freshly-parsed object on every poll anyway
  // (referentially unequal each render), and grouping <=5 entries is trivial.
  const topBlockerGroups = groupTopBlockersByPlan(topBlockers);
  const topBlockersTooltip = formatTopBlockersGroupedTooltip(topBlockers);
  // Detail is hover/expand-only, never always-on DOM (2026-09-03 operator report:
  // five stacked per-task chips were unreadable and always rendered) — the title=
  // tooltips above give the at-a-glance grouped summary; this toggle reveals the
  // per-task click-through (onOpenBlockerTask) only on demand.
  const [blockersExpanded, setBlockersExpanded] = useState(false);
  return (
    <Panel
      title="Backlog"
      count={`${total} tasks`}
      tone="backlog"
      right={
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {onReloadYaml && (
            <button
              className="btn btn-xs"
              onClick={onReloadYaml}
              disabled={reloading}
              title="Re-read backlog.yaml from disk on the server — use after editing the file"
            >
              <Icon.Bolt /> {reloading ? "Reloading…" : "Reload YAML"}
            </button>
          )}
          {onOpenDetail && (
            <button
              className="btn btn-xs"
              onClick={onOpenDetail}
              title="See every task with its status, brief and blockers"
            >
              <Icon.Eye /> Detail
            </button>
          )}
        </div>
      }
    >
      <div className="backlog">
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--fg-1)" }}>
            <b style={{ color: "var(--fg-0)" }}>{backlog.done}</b> of {total} done
            <span style={{ color: "var(--fg-3)" }}> · {Math.round((backlog.done / total) * 100)}%</span>
          </div>
          <div
            className="stacked"
            title={`done ${backlog.done} · dispatched ${backlog.dispatched} · queued ${backlog.queued} · cancelled ${backlog.cancelled}`}
          >
            <span className="done" style={{ width: pct(backlog.done) + "%" }} />
            <span className="dispatched" style={{ width: pct(backlog.dispatched) + "%" }} />
            <span className="queued" style={{ width: pct(backlog.queued) + "%" }} />
            <span className="cancelled" style={{ width: pct(backlog.cancelled) + "%" }} />
          </div>
        </div>
        <div className="legend">
          <div className="lg">
            <span className="sw" style={{ background: "var(--status-working)" }} />
            Done <span className="num">{backlog.done}</span>
          </div>
          <div className="lg">
            <span className="sw" style={{ background: "var(--accent)" }} />
            Dispatched <span className="num">{backlog.dispatched}</span>
          </div>
          <div className="lg">
            <span className="sw" style={{ background: "var(--fg-3)" }} />
            Queued{" "}
            <span className="num" data-testid="backlog-queued-count" title={topBlockersTooltip || undefined}>
              {backlog.queued}
            </span>
            {backlog.claimable !== undefined && (
              <span
                className="mono"
                style={{ marginLeft: 5, fontSize: 10, color: "var(--status-blocked)" }}
                title={
                  "Subset of queued with NO worker slot able to claim it right now (dispatch.claimable_queued_task_ids " +
                  "— unmet prereq/collision, or every eligible slot busy). Self-resolving for a capacity-wait; see each " +
                  "row's own Blocked column in Backlog → Detail for which kind." +
                  (topBlockersTooltip ? " " + topBlockersTooltip : "")
                }
              >
                · {backlog.queued - backlog.claimable} blocked
                {parkedCount != null && parkedCount > 0 && (
                  <span data-testid="durable-park-badge" style={{ marginLeft: 4 }}>
                    · {parkedCount} durable-park
                  </span>
                )}
                {parkedNeedsHumanCount != null && parkedNeedsHumanCount > 0 && (
                  <span data-testid="durable-park-needs-human-badge" style={{ marginLeft: 4 }}>
                    · {parkedNeedsHumanCount} need human
                  </span>
                )}
                {topBlockerGroups.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    data-testid="top-blockers-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBlockersExpanded((v) => !v);
                    }}
                    title="Show which queued tasks are blocked behind which backlog task, grouped by plan"
                    style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}
                  >
                    {" · blocked behind "}
                    {topBlockerGroups.length} plan{topBlockerGroups.length === 1 ? "" : "s"}
                    {blockersExpanded ? " ▴" : " ▾"}
                  </button>
                )}
              </span>
            )}
          </div>
          {blockersExpanded && topBlockerGroups.length > 0 && (
            <div
              data-testid="top-blockers"
              style={{ marginTop: 4, marginBottom: 4, fontSize: 10.5, color: "var(--fg-2)" }}
            >
              <div style={{ marginBottom: 3, color: "var(--fg-3)" }}>
                Blocked behind — each (N) is how many currently-queued tasks are blocked behind that task.
              </div>
              {topBlockerGroups.map((group) => (
                <div key={group.planSlug} style={{ marginBottom: 3 }}>
                  <div>{formatGroupedBlockerLine(group)}</div>
                  <div style={{ marginLeft: 8 }}>
                    {group.entries.map(([taskId, count], i) => (
                      <React.Fragment key={taskId}>
                        {i > 0 && ", "}
                        <button
                          type="button"
                          className="task-id mono"
                          data-testid={`top-blocker-link-${taskId}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenBlockerTask?.(taskId);
                          }}
                          title={`Open ${taskId} in Backlog detail`}
                          style={{
                            cursor: "pointer",
                            fontSize: 10,
                            padding: "0 4px",
                            marginLeft: 2,
                          }}
                        >
                          {taskId} ({count})
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="lg">
            <span className="sw" style={{ background: "var(--status-stale)" }} />
            Cancelled <span className="num">{backlog.cancelled}</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

// ─── accounts panel ──────────────────────────────────────────────────────

const TIER_DECLARED_TITLE =
  "Subscription tier — operator-declared, NOT verified by the API. " +
  "Anthropic exposes no plan field to this token and its usage headers are normalised, " +
  "so a plan change can only be recorded by hand. Click to correct.";

/**
 * The tier badge.
 *
 * Deliberately styled as *declared*, not measured: it sits next to percentage bars that
 * ARE fetched every 30 min, and rendering it identically implied the refresh had
 * confirmed it. It hadn't — and couldn't (see TIER_DECLARED_TITLE). On 2026-08-08 an
 * account downgraded to Pro kept rendering MAX20 with no way to notice or fix it short
 * of SSHing to the VM to edit accounts.json. Hence: dimmed, captioned as declared, and
 * editable in place.
 */
function TierBadge({
  account,
  onUpdateTier,
}: {
  account: AccountView;
  onUpdateTier: (tier: string, weeklyMsgLimit: number) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState<string>(account.tier);
  const [limit, setLimit] = useState<string>(String(account.weekly_msg_limit));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!editing) {
    return (
      <>
        <button
          type="button"
          className="acc-tier acc-tier-declared mono"
          title={TIER_DECLARED_TITLE}
          onClick={() => {
            setTier(account.tier);
            setLimit(String(account.weekly_msg_limit));
            setError(null);
            setEditing(true);
          }}
        >
          {account.tier}
          <span className="acc-tier-caret" aria-hidden="true">
            ✎
          </span>
        </button>
        {account.tier_contradicted && (
          <span
            className="acc-tier-warn mono"
            title={
              `This account declares "${account.tier}" (a top tier, nothing to upgrade to) but the API ` +
              `offered it an upgrade path (${account.upgrade_paths}). It may have been downgraded. ` +
              "This is a hint, not proof — check the plan and correct the tier if it changed."
            }
          >
            tier?
          </span>
        )}
      </>
    );
  }

  const parsedLimit = Number(limit);
  const limitValid = Number.isInteger(parsedLimit) && parsedLimit >= 1;

  return (
    <span className="acc-tier-edit">
      <select
        className="mono"
        value={tier}
        disabled={saving}
        aria-label="Subscription tier"
        onChange={(e) => setTier(e.target.value)}
      >
        {ACCOUNT_TIERS.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <input
        className="mono"
        type="number"
        min={1}
        value={limit}
        disabled={saving}
        aria-label="Weekly message limit"
        title="Weekly message limit — the denominator the poller uses to turn the API's percentage into a message count. Change it with the tier or the derived count stays wrong."
        onChange={(e) => setLimit(e.target.value)}
      />
      <button
        type="button"
        className="btn btn-xs"
        disabled={saving || !limitValid}
        onClick={() => {
          setSaving(true);
          setError(null);
          onUpdateTier(tier, parsedLimit)
            .then(() => setEditing(false))
            .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setSaving(false));
        }}
      >
        {saving ? "Saving…" : "Save"}
      </button>
      <button type="button" className="btn btn-xs" disabled={saving} onClick={() => setEditing(false)}>
        Cancel
      </button>
      {error && (
        <span className="acc-tier-warn mono" title={error}>
          save failed
        </span>
      )}
    </span>
  );
}

interface AccountsPanelProps {
  accounts: AccountView[];
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  onRefreshDeepseekBalance: (accountId: string) => void;
  onDisableAccount: (accountId: string) => void;
  onEnableAccount: (accountId: string) => void;
  onUpdateTier: (accountId: string, tier: string, weeklyMsgLimit: number) => Promise<void>;
  refreshingId: string | null;
}

export function AccountsPanel({
  accounts,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  onRefreshDeepseekBalance,
  onDisableAccount,
  onEnableAccount,
  onUpdateTier,
  refreshingId,
}: AccountsPanelProps) {
  if (accounts.length === 0) {
    return (
      <Panel title="Accounts" collapsible flush tone="accounts">
        <Empty
          title="No accounts configured"
          sub={
            <>
              Add Claude subscriptions to <span className="mono">accounts.json</span> and restart.
            </>
          }
        />
      </Panel>
    );
  }
  const available = accounts.filter(isAccountAvailableForDisplay).length;
  return (
    <Panel title="Accounts" collapsible count={`${available}/${accounts.length} available`} flush tone="accounts">
      <div className="accounts-list">
        {groupAccountsByProvider(accounts).map(([provider, group]) => (
          <AccountProviderGroup key={provider} provider={provider} accounts={group}>
            {group.map((a) => (
              <AccountRow
                key={a.account_id}
                account={a}
                refreshing={refreshingId === a.account_id}
                onClearRateLimit={() => onClearRateLimit(a.account_id)}
                onReportUsage={() => onReportUsage(a.account_id)}
                onRefreshUsage={() => onRefreshUsage(a.account_id)}
                onRefreshDeepseekBalance={() => onRefreshDeepseekBalance(a.account_id)}
                onDisableAccount={() => onDisableAccount(a.account_id)}
                onEnableAccount={() => onEnableAccount(a.account_id)}
                onUpdateTier={(tier, weeklyMsgLimit) => onUpdateTier(a.account_id, tier, weeklyMsgLimit)}
              />
            ))}
          </AccountProviderGroup>
        ))}
      </div>
    </Panel>
  );
}

/** Group accounts by provider (operator request, 2026-08-16 — "add the other
 * models by provider so grouping google, xai, etc"). `anthropic` sorts first
 * (the primary/largest group); everyone else sorts alphabetically by their
 * `PROVIDER_DISPLAY` label, not the raw provider key, so "Google (Gemini)"
 * lands where an operator would expect it, not under "g" for the internal
 * `gemini` string. A null/unregistered
 * provider groups under "anthropic" -- same fallback `ProviderBadge` uses. */
function groupAccountsByProvider(accounts: AccountView[]): Array<[string, AccountView[]]> {
  const groups = new Map<string, AccountView[]>();
  for (const a of accounts) {
    const key = a.provider ?? "anthropic";
    const existing = groups.get(key);
    if (existing) {
      existing.push(a);
    } else {
      groups.set(key, [a]);
    }
  }
  const entries = [...groups.entries()];
  entries.sort(([providerA], [providerB]) => {
    if (providerA === "anthropic") return -1;
    if (providerB === "anthropic") return 1;
    const labelA = PROVIDER_DISPLAY[providerA]?.label ?? providerA;
    const labelB = PROVIDER_DISPLAY[providerB]?.label ?? providerB;
    return labelA.localeCompare(labelB);
  });
  return entries;
}

const PROVIDER_GROUP_COLLAPSE_KEY_PREFIX = "orch.providerGroupCollapsed.";

function readProviderGroupCollapsed(provider: string): boolean {
  try {
    return localStorage.getItem(PROVIDER_GROUP_COLLAPSE_KEY_PREFIX + provider) === "1";
  } catch {
    return false;
  }
}

/** One provider's sub-section within the Accounts panel — its own collapse
 * toggle (persisted per-provider, same localStorage pattern as `Panel`'s
 * top-level `collapsible`), reusing `ProviderBadge`'s exact label/color via
 * `PROVIDER_DISPLAY` so the group header and each row's badge never drift
 * apart. Deliberately a lighter-weight sub-header, not a nested `Panel` --
 * nesting the full `.panel` card styling inside another panel's body reads as
 * a panel-within-a-panel, not a sub-group. */
function AccountProviderGroup({
  provider,
  accounts,
  children,
}: {
  provider: string;
  accounts: AccountView[];
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readProviderGroupCollapsed(provider));
  const entry = PROVIDER_DISPLAY[provider];
  const available = accounts.filter(isAccountAvailableForDisplay).length;

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PROVIDER_GROUP_COLLAPSE_KEY_PREFIX + provider, next ? "1" : "0");
      } catch {
        // Collapse still works this session, it just won't persist across reloads.
      }
      return next;
    });
  };

  return (
    <div className="account-provider-group">
      <button
        type="button"
        className="account-provider-group-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand" : "Collapse"}
      >
        <span className="panel-collapse-chevron" aria-hidden="true">
          {collapsed ? "▸" : "▾"}
        </span>
        <span className={`provider-badge ${entry?.cssClass ?? "other"}`}>{entry?.label ?? provider}</span>
        <span className="account-provider-group-count">
          {available}/{accounts.length}
        </span>
      </button>
      {!collapsed && <div className="account-provider-group-body">{children}</div>}
    </div>
  );
}

/** Compact plain-text line of the extra unified rate-limit header telemetry
 *  (status / binding window / credits). Not a chart — just the values we already
 *  receive from the Anthropic `anthropic-ratelimit-unified-*` headers. Renders
 *  nothing until an API probe has populated them. */
function AccountRateLimitDetail({ account: a }: { account: AccountView }) {
  const bits: { text: string; warn: boolean; title?: string }[] = [];
  if (a.unified_status && a.unified_status !== "allowed") {
    bits.push({ text: `status: ${a.unified_status}`, warn: true });
  }
  if (a.representative_claim) {
    // Humanize "five_hour" → "5-hour", "seven_day" → "weekly".
    const claim =
      a.representative_claim === "five_hour"
        ? "5-hour"
        : a.representative_claim === "seven_day"
          ? "weekly"
          : a.representative_claim.replace(/_/g, " ");
    bits.push({ text: `binding: ${claim}`, warn: false });
  }
  if (a.overage_status === "rejected") {
    // "Overage" / "extra usage" = optional pay-as-you-go usage BEYOND the plan's rate
    // limits — wholly separate from the flat Max subscription (which is unaffected).
    // Disabled overage is the DEFAULT for an account that hasn't funded extra usage, so
    // it is NOT an alarm on its own — it only bites once a window hits 100% (then the
    // account hard-stops instead of spilling into paid usage). Render it neutral +
    // informative; escalate to a warning ONLY when a window is actually near its limit,
    // i.e. when that hard-stop is genuinely imminent. (The earlier red `overage: out of
    // credits` chip read like "account is broke" on healthy low-usage accounts.)
    const reasonRaw = a.overage_disabled_reason ? a.overage_disabled_reason.replace(/_/g, " ") : "off";
    const friendly =
      a.overage_disabled_reason === "out_of_credits"
        ? "no overage credits"
        : a.overage_disabled_reason === "org_level_disabled"
          ? "org policy"
          : reasonRaw;
    const nearLimit = a.status === "rate_limited" || Math.max(a.five_hour_pct ?? 0, a.weekly_pct ?? 0) >= 80;
    bits.push({
      text: `extra-usage: off (${friendly})`,
      warn: nearLimit,
      title:
        `Pay-as-you-go usage beyond this plan's rate limits is off (${reasonRaw}). ` +
        "Separate from the flat Max subscription, which is unaffected. No effect until a " +
        "window hits 100% — then this account hard-stops instead of billing extra usage.",
    });
  }
  if (bits.length === 0) return null;
  return (
    <div className="acc-rl-detail mono" title="Anthropic unified rate-limit headers">
      {bits.map((b, i) => (
        <span key={i} className={b.warn ? "acc-rl-warn" : "acc-rl-ok"} title={b.title}>
          {b.text}
        </span>
      ))}
    </div>
  );
}

function SetupTokenBadge({ account: a }: { account: AccountView }) {
  // Phase 4c: show setup-token (1-year long-lived) expiry for r3 accounts.
  // Only renders when alert level is warn/crit/expired; silent when ok or unknown.
  const alert = a.setup_token_alert;
  if (!alert || alert === "ok") return null;
  const days = a.setup_token_expires_in_days;
  const label = alert === "expired" ? "Setup token expired" : `Setup token expires in ${days}d`;
  const btnClass = alert === "expired" || alert === "crit" ? "btn-danger" : "btn-warn";
  const title =
    alert === "expired"
      ? "Long-lived setup-token has expired. Agents on this account will 401. Run `claude setup-token` on a browser machine, update ~/.claude-accounts/<id>.env and accounts.json."
      : `Setup token expires ${a.setup_token_expires_at}. Renew: run \`claude setup-token\` 30 days before expiry and update the env file.`;
  return (
    <span
      className={`btn btn-xs ${btnClass}`}
      style={{ marginLeft: 8, fontSize: 10.5, cursor: "default" }}
      title={title}
    >
      {label}
    </span>
  );
}

/** Refresh-button label/tooltip for the account-card footer's non-DeepSeek branch
 *  (everything the ternary at the bottom of `AccountRow` renders below `isDeepseek ? ...`).
 *  `refresh_account_usage` (server/routes/accounts.py) only drives `claude /usage` in a
 *  pty for the DEFAULT ("anthropic") dispatch path -- glm/codex/gemini/ollama each hit
 *  their own real, provider-specific read (`_refresh_glm_usage`/`_refresh_codex_usage`/
 *  `_refresh_gemini_usage`/`_refresh_selfhosted_usage`) before that pty is ever reached,
 *  so the copy must not claim `claude /usage` (or its cost) for them. None of those four
 *  arms document a specific latency, so no timing claim is made for them either -- rather
 *  than guess. Ollama is the one genuine exception with no vendor to query at all
 *  (self-hosted, see `_refresh_selfhosted_usage`'s docstring) and gets copy that says so
 *  rather than a generic "fetches from the provider" claim that would be false for it
 *  specifically. Any provider not special-cased here (i.e. real "anthropic" accounts)
 *  keeps the original claude-/usage-accurate copy, matching the server's own fallback
 *  dispatch. Source: glm_quota_estimate_masked_exhausted_account_2026_08_22 item 2. */
function refreshUsageCopy(provider: string): { label: string; title: string } {
  switch (provider) {
    case "glm":
      return { label: "Refresh usage", title: "Fetch this account's usage directly from Z.ai and update this card" };
    case "codex":
      return {
        label: "Refresh usage",
        title: "Fetch this account's usage directly from OpenAI's rate-limit read and update this card",
      };
    case "gemini":
      return {
        label: "Refresh usage",
        title: "Fetch this account's live Gemini capacity read and update this card",
      };
    case "ollama":
      return {
        label: "Refresh",
        title: "Self-hosted account — no vendor quota exists to query; this just marks the row checked",
      };
    default:
      return {
        label: "Refresh from /usage",
        title: "Drive `claude /usage` on the backend's box, parse, update this card (~12s)",
      };
  }
}

function AccountRow({
  account: a,
  refreshing,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  onRefreshDeepseekBalance,
  onDisableAccount,
  onEnableAccount,
  onUpdateTier,
}: {
  account: AccountView;
  refreshing: boolean;
  onClearRateLimit: () => void;
  onReportUsage: () => void;
  onRefreshUsage: () => void;
  onRefreshDeepseekBalance: () => void;
  onDisableAccount: () => void;
  onEnableAccount: () => void;
  onUpdateTier: (tier: string, weeklyMsgLimit: number) => Promise<void>;
}) {
  // Prefer the authoritative pct from `claude /usage` when present;
  // fall back to msgs/limit ratio so manual-entry accounts still render.
  const weeklyPct = a.weekly_pct ?? (a.weekly_msg_limit ? (a.weekly_msgs_used / a.weekly_msg_limit) * 100 : 0);
  const fiveHourPct =
    a.five_hour_pct ??
    (a.five_hour_msgs_used && a.weekly_msg_limit
      ? (a.five_hour_msgs_used / Math.max(1, Math.round(a.weekly_msg_limit / 14))) * 100
      : 0);
  const sonnetPct =
    a.weekly_sonnet_pct ?? (a.weekly_msg_limit ? (a.weekly_sonnet_msgs_used / a.weekly_msg_limit) * 100 : 0);
  const isDeepseek = a.provider === "deepseek";
  // Gemini has no weekly/5-hour subscription concept at all (raw RPM/RPD/TPM request-
  // rate quota instead, see gemini_headroom.py) — rendering it through the Claude-
  // shaped bars below produced a permanent, meaningless "Weekly 0%" (operator report
  // 2026-08-21, model_provider_badge_mismatch_2026_08_21 [UI] P1). Same isDeepseek
  // branch shape as DeepSeekBalanceLine below.
  const isGemini = a.provider === "gemini";
  const isDisabled = a.status === "disabled";
  const refreshCopy = refreshUsageCopy(a.provider);
  // The Sonnet bar shares the weekly window — same reset time.
  return (
    <div className={`acc-row status-${a.status}`}>
      <div className="acc-head">
        <span className={`acc-dot ${statusToClass(a.status)}`} />
        <span className="acc-label">{a.label}</span>
        <TierBadge account={a} onUpdateTier={onUpdateTier} />
        <ProviderBadge provider={a.provider} />
        <SetupTokenBadge account={a} />
        {isDisabled && (
          <span
            className="btn btn-xs btn-warn"
            style={{ cursor: "default" }}
            title="Paused by an operator — excluded from spawn/rotation until resumed"
          >
            Paused
          </span>
        )}
        {a.health_status === "degraded" && (
          // Generic, provider-agnostic counterpart to NvidiaCapacityPanel.tsx's own
          // .gemma-degraded-banner (gemma_4_31b_it_persistent_timeout_2026_08_19) —
          // fires here regardless of provider or pause state, so an enabled-but-
          // degraded account (health_status: "degraded" with status still "healthy")
          // is visible right on its own row, not only inside a bespoke per-provider
          // panel an operator might not have scrolled to.
          <span
            className="btn btn-xs btn-warn"
            style={{ cursor: "default" }}
            title="Flagged degraded — real, reproduced failure on this specific account/model. See the provider's own capacity/wallet panel (if any) for the evidence."
          >
            ⚠ Degraded
          </span>
        )}
        <span className="spacer" />
        {a.used_by_slots.length > 0 && (
          <span className="acc-used-by mono" title="slots currently using this account">
            in use by {a.used_by_slots.map((s) => `#${s}`).join(", ")}
          </span>
        )}
      </div>
      {isDeepseek ? (
        <DeepSeekBalanceLine account={a} />
      ) : isGemini ? (
        <GeminiCapacityLine account={a} />
      ) : glmQuotaUnavailable(a) ? (
        // No VENDOR-MEASURED reading exists for a Z.ai TEAM seat — its key gets "no coding plan"
        // from the quota endpoint — so this deliberately does NOT feed an estimate into the
        // weekly_pct bar (GeminiCapacityLine precedent: a fabricated 0% reads as "no capacity
        // used" when the truth is "no reading at all"). What it CAN show honestly is AO's own
        // ledger estimate, when the seat declares a weekly_credit_limit, labelled "(est.)" so it
        // is never mistaken for a vendor reading. The vendor's own reason stays underneath.
        <div className="acc-bars">
          {glmEstimateBarPct(a) != null && (
            <BarRow
              label="Weekly (est.)"
              pct={glmEstimateBarPct(a) as number}
              resetsAt={null}
              status={a.status}
              title="AO-ledger ESTIMATE of rolling-7d credits against this seat's declared weekly ceiling — computed from our own task ledger, NOT a Z.ai reading (excludes in-flight and non-AO use)"
            />
          )}
          <span className="acc-quota-unavailable">{glmQuotaUnavailableText(a)}</span>
        </div>
      ) : (
        <div className="acc-bars">
          <BarRow
            label="Weekly"
            pct={weeklyPct}
            resetsAt={a.weekly_resets_at}
            status={a.status}
            title={`${a.weekly_msgs_used} of ${a.weekly_msg_limit} (weekly quota, all models)`}
          />
          {/* Sonnet weekly is only shown when `claude /usage` actually emits the
              "Current week (Sonnet)" bar. The unified rate-limit headers never carry
              it (API path → weekly_sonnet_pct is always null) and the headless /usage
              TUI freezes at 2 bars so the Sonnet bar never renders (verified
              2026-05-25, see usage_tracker.py). Rendering it unconditionally produced
              a permanent, meaningless 0% bar. Gate on a real reading instead — the bar
              auto-reappears if a future claude build ever surfaces the value. */}
          {a.weekly_sonnet_pct != null && (
            <BarRow
              label="Sonnet (wk)"
              pct={sonnetPct}
              resetsAt={a.weekly_resets_at}
              status={a.status}
              title={`Subset of weekly attributed to Sonnet — ${a.weekly_sonnet_msgs_used} msgs`}
              subdued
            />
          )}
          <BarRow
            label="5-hour"
            pct={fiveHourPct}
            resetsAt={a.five_hour_resets_at}
            status={a.status}
            title={`${a.five_hour_msgs_used} messages in the current 5-hour window`}
          />
        </div>
      )}
      <AccountRateLimitDetail account={a} />
      <div className="acc-foot">
        {a.status === "rate_limited" ? (
          <>
            <span className="acc-rate-limit">
              ⏱ rate-limited until {a.rate_limited_until ? fmtIn(a.rate_limited_until) : "—"}
            </span>
            <span className="spacer" />
            <button
              className="btn btn-xs"
              onClick={onRefreshUsage}
              disabled={refreshing}
              title="Re-run /usage via the backend's claude binary; may detect that the window has reset"
            >
              <Icon.Refresh /> {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button className="btn btn-xs" onClick={onClearRateLimit} title="Clear rate-limit flag manually">
              <Icon.Check /> Clear
            </button>
          </>
        ) : (
          <>
            <span className="acc-last-used">
              {isDeepseek
                ? a.balance_checked_at
                  ? `balance checked ${fmtAgo(a.balance_checked_at)}`
                  : "no balance data yet"
                : a.last_used_at
                  ? `last refreshed ${fmtAgo(a.last_used_at)}`
                  : "no usage data yet"}
            </span>
            <span className="spacer" />
            {isDeepseek ? (
              <button
                className="btn btn-xs"
                onClick={onRefreshDeepseekBalance}
                disabled={refreshing}
                title="Fetch DeepSeek GET /user/balance now, instead of waiting for the ~30-min poller (e.g. right after a top-up)"
              >
                <Icon.Refresh /> {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            ) : (
              <>
                <button className="btn btn-xs" onClick={onRefreshUsage} disabled={refreshing} title={refreshCopy.title}>
                  <Icon.Refresh /> {refreshing ? "Refreshing…" : refreshCopy.label}
                </button>
                <button
                  className="btn btn-xs"
                  onClick={onReportUsage}
                  title="Enter numbers manually (fallback if /usage parsing breaks)"
                >
                  Manual…
                </button>
              </>
            )}
          </>
        )}
        {isDisabled ? (
          <button
            className="btn btn-xs"
            onClick={onEnableAccount}
            title="Resume — allow AO to dispatch workers onto this account again"
          >
            <Icon.Check /> Resume
          </button>
        ) : (
          <button
            className="btn btn-xs"
            onClick={onDisableAccount}
            title="Pause — stop AO from dispatching new workers onto this account, and rotate any workers currently on it off"
          >
            Pause
          </button>
        )}
      </div>
    </div>
  );
}

/** DeepSeek accounts are dollar-billed, pay-as-you-go — no weekly/5-hour quota
 *  concept, so they skip the Anthropic-shaped BarRows entirely. Balance is a REAL
 *  API number (GET /user/balance, remaining funds — deepseek_claude_blended_provider_
 *  routing_2026_07_28 follow-up, 2026-08-04). Spend below it is DERIVED (DeepSeek has
 *  no spend-history endpoint at all) from Claude Code's own transcript files —
 *  operator ruling 2026-08-05: show actual $ spent + tokens in/out, not balance-only. */
function DeepSeekBalanceLine({ account: a }: { account: AccountView }) {
  const exhausted = a.balance_is_available === false;
  return (
    <div className="acc-balance-block">
      <div className="acc-balance-row">
        <span className="acc-bar-label">Balance</span>
        {a.balance_usd != null ? (
          <span className={`acc-balance-value mono ${exhausted ? "acc-balance-exhausted" : ""}`}>
            {fmtUsd(a.balance_usd, a.balance_currency)}
          </span>
        ) : (
          <span className="acc-balance-value mono acc-balance-pending">no balance data yet</span>
        )}
        {exhausted && <span className="acc-balance-warn">out of funds</span>}
      </div>
      <DeepSeekUsageTable usage={a.deepseek_usage} />
    </div>
  );
}

/** The real Gemini ceiling shape (RPM/RPD used-vs-ceiling, TPM ceiling-only —
 * see gemini_headroom.py's module docstring) inline in the generic Accounts panel
 * row, in place of the Claude-shaped Weekly/5-hour bars that don't apply to Gemini
 * (model_provider_badge_mismatch_2026_08_21 [UI] P1). Same real numbers as the
 * dedicated GeminiCapacityPanel, sourced from the SAME AccountView object as every
 * other field on this row — refreshes on the identical poll cadence as every other
 * account, not a separate fetch. */
function GeminiCapacityLine({ account: a }: { account: AccountView }) {
  const noCeiling = a.gemini_rpm_ceiling === null && a.gemini_rpd_ceiling === null && a.gemini_tpm_ceiling === null;
  if (noCeiling) {
    return <div className="acc-balance-value mono acc-balance-pending">no capacity data yet</div>;
  }
  return (
    <div className="acc-balance-block">
      <table className="acc-usage-table acc-usage-table-breakdown">
        <tbody>
          <tr>
            <td className="acc-usage-window-label">RPM</td>
            <td className="mono">{fmtCeiling(a.gemini_rpm_used ?? 0, a.gemini_rpm_ceiling)}</td>
          </tr>
          <tr>
            <td className="acc-usage-window-label">RPD</td>
            <td className="mono">{fmtCeiling(a.gemini_rpd_used ?? 0, a.gemini_rpd_ceiling)}</td>
          </tr>
          <tr>
            <td className="acc-usage-window-label">TPM</td>
            <td className="mono">
              {fmtTpm({
                tpm_ceiling: a.gemini_tpm_ceiling,
                tpm_used: a.gemini_tpm_used,
                tpm_peak_recent: a.gemini_tpm_peak_recent,
                tpm_rejected_recent: a.gemini_tpm_rejected_recent,
                tpm_source: a.gemini_tpm_source ?? "none",
              })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const _USAGE_WINDOW_ROWS: { key: keyof Omit<DeepSeekUsageSummary, "updated_at">; label: string }[] = [
  { key: "one_hour", label: "1h" },
  { key: "five_hour", label: "5h" },
  { key: "day", label: "24h" },
  { key: "week", label: "7d" },
  { key: "lifetime", label: "Lifetime" },
];

/** Derived spend + token usage across the five tracked windows. Table, not a
 * second set of bars — there's no cap to fill against (that's the whole point of
 * the 2026-08-05 operator ruling: real numbers, not an invented ceiling). */
function DeepSeekUsageTable({ usage }: { usage: DeepSeekUsageSummary | null }) {
  if (!usage) {
    return <div className="acc-usage-pending">no spend data yet</div>;
  }
  return (
    <>
      <div className="usage-panel-hint">
        Real-time activity on this account, bucketed by when each turn happened — includes tasks still in progress. See
        Task Token Usage for completed-task-only totals instead.
      </div>
      <table className="acc-usage-table acc-usage-table-breakdown">
        <thead>
          <tr>
            <th></th>
            <th>In</th>
            <th>Out</th>
            <th title="DeepSeek native-proxy reasoning-token breakdown — a subset WITHIN Out, not additive spend. — for a window with no native-proxy capture yet">
              Reasoning
            </th>
            <th title="Input, cache miss">$In</th>
            <th title="Input, cache read/hit">$Cache</th>
            <th title="Output">$Out</th>
            <th title="Total spend">Total</th>
          </tr>
        </thead>
        <tbody>
          {_USAGE_WINDOW_ROWS.map(({ key, label }) => {
            const w = usage[key];
            return (
              <tr key={key}>
                <td className="acc-usage-window-label">{label}</td>
                <td className="mono">{fmtTokenCount(w.input_tokens)}</td>
                <td className="mono">{fmtTokenCount(w.output_tokens)}</td>
                <td className="mono">{fmtTokenCountOrDash(w.reasoning_tokens ?? null)}</td>
                <td className="mono">{fmtUsd(w.input_noncache_spend_usd, null)}</td>
                <td className="mono">{fmtUsd(w.input_cache_spend_usd, null)}</td>
                <td className="mono">{fmtUsd(w.output_spend_usd, null)}</td>
                <td className="mono acc-usage-total">{fmtUsd(w.spend_usd, null)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table className="acc-usage-table acc-usage-table-breakdown acc-usage-per-turn-table">
        <thead>
          <tr>
            <th title="Per-turn efficiency — how this account's tasks actually get through work">Per turn</th>
            <th>Turns/task</th>
            <th title="Total tokens (in+out+cache) per completed task">Ctx/task</th>
            <th title="Input, cache miss">In/turn</th>
            <th title="Cache write (billed at the input-miss rate)">CacheWr/turn</th>
            <th title="Cache read (input-hit rate)">CacheRd/turn</th>
            <th>Out/turn</th>
            <th>$/turn</th>
            {/* Appended last, not inserted after Out/turn — this table's columns are
                asserted by nth() index in deepseek-per-turn-metrics.spec.ts; appending
                at the end is the only placement that doesn't shift $/turn (and every
                other already-asserted column) out from under that spec. */}
            <th title="DeepSeek native-proxy reasoning-token breakdown per turn — a subset WITHIN Out/turn, not additive">
              Reasoning/turn
            </th>
          </tr>
        </thead>
        <tbody>
          {_USAGE_WINDOW_ROWS.map(({ key, label }) => {
            const w = usage[key];
            return (
              <tr key={key}>
                <td className="acc-usage-window-label">{label}</td>
                <td className="mono">{fmtRatioOrDash(w.avg_turns_per_task)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.avg_context_tokens_per_task)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.input_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.cache_creation_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.cache_read_tokens_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.output_tokens_per_turn)}</td>
                <td className="mono">{fmtUsdOrDash(w.spend_per_turn)}</td>
                <td className="mono">{fmtTokenAvgOrDash(w.reasoning_tokens_per_turn ?? null)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function fmtUsd(amount: number, currency: string | null): string {
  const symbol = !currency || currency === "USD" ? "$" : `${currency} `;
  return `${symbol}${amount.toFixed(2)}`;
}

function statusToClass(s: AccountStatus): string {
  if (s === "healthy") return "working";
  if (s === "high_usage") return "blocked";
  // "disabled" is a deliberate operator pause, not an error — give it its own
  // muted look rather than reading as rate_limited/auth_failed (both "stale").
  if (s === "disabled") return "paused";
  return "stale";
}

function pctToClass(pct: number, status: AccountStatus): string {
  if (status === "rate_limited") return "stale";
  if (pct >= 80) return "stale";
  if (pct >= 60) return "blocked";
  return "working";
}

function ModelBadge({
  model,
  effort,
  thinking,
}: {
  /** model_provider_badge_mismatch_2026_08_21 naming-alignment follow-up: the
   * backend (server.accounts.effective_model_for_telemetry, joined live off the
   * slot's CURRENT account_id) already resolves this to the real
   * "{provider}-{model-tier-with-version}" label for every provider — a versioned
   * Anthropic snapshot ("claude-sonnet-5") or "{provider}-{variant}"
   * ("deepseek-pro", "codex-luna"). Render it as-is; no provider-specific
   * override needed here anymore (removed 2026-08-21 — the DeepSeek-only
   * "pro"/"flash" special case this badge used to need is now just what `model`
   * already says). */
  model: string | null;
  effort: string | null;
  /** Stored as a free-form string for forward-compat (worker can pass
   *  "on"/"off"/null or richer labels). Treated as boolean here:
   *  truthy non-empty value (other than literal "off"/"no"/"false") →
   *  thinking ON → filled brain. Null/empty/"off" → outline.
   *
   *  ao_satellite_ao_dispatch_batch12_2026_08_09 [UI] P3 finding: this reflects
   *  the harness's OWN `--max-thinking-tokens` CLI flag, not necessarily real
   *  model behavior — live transcript sampling (both Anthropic and DeepSeek
   *  sessions, flag unset) showed genuine `thinking` content blocks regardless,
   *  because the flag is inert on adaptive-reasoning models (sonnet 5 / opus 4.8
   *  / fable 5 — `reasoning` is controlled by `effort` on these, not this flag).
   *  DeepSeek's own Anthropic-compat docs additionally confirm `budget_tokens`
   *  is ignored even when the flag IS honored. Tooltip below states this as
   *  "requested", not a behavior guarantee. */
  thinking: string | null;
}) {
  const thinkingOn = !!thinking && !/^\s*(off|no|false|0)\s*$/i.test(thinking);
  return (
    <span className="model-cell">
      <span className="badge">{model || "—"}</span>
      {effort && (
        <span className="model-aux mono" title={`Effort: ${effort}`}>
          -{effort}
        </span>
      )}
      <span
        className={`model-thinking ${thinkingOn ? "on" : "off"}`}
        title={
          thinkingOn
            ? `Thinking requested: on${thinking && thinking !== "on" ? ` (${thinking})` : ""} — non-Anthropic ` +
              "providers' Anthropic-compat endpoints may ignore the specific token budget"
            : "Thinking requested: off — the harness's own flag; verified inert on adaptive-reasoning models " +
              "(sonnet 5 / opus 4.8 / fable 5, on both Anthropic and DeepSeek), which may reason regardless"
        }
        aria-label={thinkingOn ? "thinking on" : "thinking off"}
      >
        <Icon.Brain filled={thinkingOn} />
      </span>
    </span>
  );
}

/** Fills the Task cell for a slot running typed one-off work (scheduled cron /
 * CI-fix / conflict-resolve / data-pipeline-failure escalation) instead of a
 * backlog task — those deliberately clear `current_task` (see server's
 * claim_slot_for_typed_agent), so without this the Task/Plan pair just read
 * blank, as if the slot were idle or broken, even while it's actively working
 * (operator ask 2026-08-09: "if they're not [dispatched from the backlog]
 * that's fine, but they should show something about the work they're doing"). */
function RoleBadge({ role, work }: { role: string; work?: { task: string; source: string | null } }) {
  // The badge text stays the ROLE — it is what distinguishes an escalation/typed worker
  // from a general planning worker at a glance, and must survive this. The tooltip is
  // where the actual work goes: the old text asserted "no backlog task", which is true of
  // the SLOT but false of the AGENT running in it, so it read as "idle, nothing assigned"
  // for a session demonstrably mid-task (operator report 2026-08-10, cicd on slot 3).
  const title = work
    ? `${role} — ${work.task}${work.source ? ` (${work.source})` : ""}`
    : `Typed one-off role — no backlog task; see Last message for what it's doing`;
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        padding: "1px 6px",
        borderRadius: 3,
        background: "rgba(99,102,241,0.15)",
        color: "var(--fg-1)",
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
      title={title}
      data-testid={`role-badge-${role}`}
    >
      {role}
    </span>
  );
}

/** Slot-routing pin ("none"/"low"/"medium"/"high") for a task — NOT a difficulty
 * rating (see ModelBadge's `effort` for that). Same field + color ladder as the
 * Backlog-detail table's Slot-cell affinity badge, so the two tables read
 * consistently. Renders nothing for "none"/null — the common case, where the
 * task has no slot preference at all. */
function AffinityBadge({ affinity }: { affinity: string | null | undefined }) {
  if (!affinity || affinity === "none") return null;
  const color =
    affinity === "high" ? "var(--status-blocked)" : affinity === "medium" ? "var(--status-paused)" : "var(--fg-3)";
  return (
    <span
      className="mono"
      style={{ fontSize: 10, color }}
      title={`Slot affinity: ${affinity} (routing pin, not a difficulty rating)`}
    >
      ({affinity})
    </span>
  );
}

/** Rounded + capped at 100 for DISPLAY — Anthropic's own utilization headers can
 * read slightly over 100% right after a rate-limit trips (the sample landing a
 * beat after the account crossed the cap), and BarRow's bar-width fill was
 * already clamped while the printed number next to it was not, so the two
 * visibly disagreed (e.g. a full bar reading "130%"). The account's real
 * over-cap state is still fully conveyed via status/rate_limited_until — this
 * only bounds the number a viewer reads off the bar. */
export function displayPct(pct: number): number {
  return Math.min(100, Math.round(pct));
}

/** True when a GLM account's quota is genuinely UNREADABLE — BOTH pct readings null — so the card
 * must not render a numeric bar. Without this the row printed a false "0%", which reads as "no
 * capacity used" when the truth is "we have no reading at all". Observed live on a Z.ai team-plan
 * seat: the vendor answers HTTP 200 with a refusal body, so the poller correctly writes nothing
 * and both percentages stay null indefinitely.
 *
 * Provider-gated deliberately: for a NON-glm account both-null means "not polled yet", a
 * transient condition with its own existing treatment. The vendor's actual REASON also lands in
 * the Activity feed (`account_usage_refresh_parse_failed`) and, since the poller persists it,
 * on the card via `quota_unavailable_reason` (see `glmQuotaUnavailableText`). */
export function glmQuotaUnavailable(a: {
  provider: string;
  weekly_pct: number | null;
  five_hour_pct: number | null;
}): boolean {
  return a.provider === "glm" && a.weekly_pct === null && a.five_hour_pct === null;
}

/** Card text for a GLM account with no readable quota: the vendor's own reason when the poller has
 * recorded one, else the neutral "no reading yet" (never polled). Never speculates about a cause. */
export function glmQuotaUnavailableText(a: { quota_unavailable_reason: string | null }): string {
  return a.quota_unavailable_reason
    ? `Z.ai quota unavailable — vendor says: ${a.quota_unavailable_reason}`
    : "Z.ai quota unavailable — no reading yet";
}

/** The AO-ledger estimate pct a GLM card may draw as a bar, or null when it must not.
 *
 * Both conditions matter. `glmQuotaUnavailable(a)` keeps this OFF any account that HAS a
 * vendor reading — a real bar already exists there, and an estimate beside it is exactly the
 * "unlabelled, confidently-wrong estimate next to real numbers" that glm_team_quota.py names as
 * the 2026-08-21 429-storm cause. `weekly_estimate_pct` is null for every account that declares
 * no `weekly_credit_limit`, so this is a no-op for the rest of the fleet.
 *
 * Kept a pure exported predicate (not inlined into JSX) so the rule is unit-testable in this
 * suite's own idiom, alongside glmQuotaUnavailable/glmQuotaUnavailableText. */
export function glmEstimateBarPct(a: {
  provider: string;
  weekly_pct: number | null;
  five_hour_pct: number | null;
  weekly_estimate_pct?: number | null;
}): number | null {
  if (!glmQuotaUnavailable(a)) return null;
  return a.weekly_estimate_pct ?? null;
}

function BarRow({
  label,
  pct,
  resetsAt,
  status,
  title,
  subdued,
}: {
  label: string;
  pct: number;
  resetsAt: string | null;
  status: AccountStatus;
  title: string;
  subdued?: boolean;
}) {
  const rounded = displayPct(pct);
  return (
    <div className="acc-bar-row" style={subdued ? { opacity: 0.7 } : undefined}>
      <span className="acc-bar-label">{label}</span>
      <div className="acc-bar" title={title}>
        <div className={`acc-bar-fill ${pctToClass(pct, status)}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="acc-bar-value mono">{rounded}%</span>
      <span className="acc-bar-resets" title={resetsAt ? new Date(resetsAt).toString() : ""}>
        {formatResetsIn(resetsAt)}
      </span>
    </div>
  );
}

function formatResetsIn(isoUtc: string | null): string {
  if (!isoUtc) return "—";
  const target = Date.parse(isoUtc);
  if (!Number.isFinite(target)) return "—";
  const diffSec = Math.round((target - Date.now()) / 1000);
  if (diffSec <= 0) return "now";
  if (diffSec < 60) return `${diffSec}s`;
  const days = Math.floor(diffSec / 86400);
  const hours = Math.floor((diffSec % 86400) / 3600);
  const mins = Math.floor((diffSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ─── agents panel (chat with main / review / plan_reconciler, by ROLE) ───
// Role-split (ao_dashboard_fleet_ui_2026_06_26): main, review, and plan_reconciler
// each get their own chat tab; the collapsed `custom` catch-all tab is retired —
// other kind-agents (cicd, plan_health, data_pipeline_failure, workers) surface in
// the Fleet / agent-types panel instead, not as a chat tab.
const ROLES_ORDER: AgentRole[] = ["main", "review", "plan_reconciler"];

interface AgentsPanelProps {
  agents: AgentView[];
  /** Full slot roster (unfiltered — includes kind="review"/"main" rows), reused
   * from the same `state.slots` the Fleet table filters down via `fleetSlots()`.
   * Powers `ReservedRoleBadge`'s live tmux_session -> slot `kind` cross-check. */
  slots: SlotView[];
  activeRole: AgentRole | null;
  setActiveRole: (r: AgentRole | null) => void;
  history: ChatMessage[];
  historyLoading: boolean;
  onSendToRole: (role: string, text: string) => Promise<void>;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchive: (agentId: string) => Promise<void>;
  onForceArchive: (agentId: string) => Promise<void>;
  onRestore: (agentId: string) => Promise<void>;
  onDelete: (agent: AgentView) => void;
  onEdit: (agent: AgentView) => void;
  onLog: (agent: AgentView) => void;
  onSwitchAccount: (agent: AgentView) => void;
  onSwitchModel: (agent: AgentView) => void;
  onSpawnAgent: () => void;
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
}

export function AgentsPanel({
  agents,
  slots,
  activeRole,
  setActiveRole,
  history,
  historyLoading,
  onSendToRole,
  onPromote,
  onArchive,
  onForceArchive,
  onRestore,
  onDelete,
  onEdit,
  onLog,
  onSwitchAccount,
  onSwitchModel,
  onSpawnAgent,
  showArchived,
  setShowArchived,
}: AgentsPanelProps) {
  const visible = showArchived ? agents : agents.filter((a) => a.status !== "archived");
  const rolesPresent = ROLES_ORDER.filter((r) => visible.some((a) => a.role === r));

  // Auto-select first role with an agent.
  React.useEffect(() => {
    if (activeRole === null && rolesPresent.length > 0) {
      setActiveRole(rolesPresent[0]!);
    } else if (activeRole && !rolesPresent.includes(activeRole) && rolesPresent.length > 0) {
      setActiveRole(rolesPresent[0]!);
    }
  }, [activeRole, rolesPresent, setActiveRole]);

  if (agents.length === 0) {
    return (
      <Panel
        title="Agents"
        flush
        tone="agents"
        right={
          <button className="btn btn-sm" onClick={onSpawnAgent}>
            <Icon.Bolt /> Spawn agent
          </button>
        }
      >
        <Empty
          title="No agents connected"
          sub={
            <>
              Click <b>Spawn agent</b> for a boot prompt; paste it into a fresh Claude Code session (after running{" "}
              <span className="mono">/remote-control</span> in that session) and the agent will register itself.
            </>
          }
        />
      </Panel>
    );
  }

  const onlineCount = visible.filter((a) => a.online).length;

  return (
    <Panel
      title="Agents"
      count={`${onlineCount}/${visible.length} online`}
      flush
      tone="agents"
      right={
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            className="btn btn-xs btn-ghost"
            onClick={() => setShowArchived(!showArchived)}
            title="Toggle showing archived agents"
          >
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <button className="btn btn-sm" onClick={onSpawnAgent}>
            <Icon.Bolt /> Spawn agent
          </button>
        </div>
      }
    >
      <div className="agents-tabs">
        {rolesPresent.map((r) => {
          const holders = visible.filter((a) => a.role === r);
          const anyOnline = holders.some((a) => a.online);
          return (
            <button
              key={r}
              className={`agents-tab ${r === activeRole ? "active" : ""}`}
              onClick={() => setActiveRole(r)}
              data-testid={`agents-tab-${r}`}
            >
              <span className={`status-dot ${anyOnline ? "working" : "stale"}`} />
              {r === "plan_reconciler" ? "plan-reconciler" : r}{" "}
              <span style={{ color: "var(--fg-3)" }}>· {holders.length}</span>
            </button>
          );
        })}
      </div>

      {activeRole && (
        <RoleChat
          role={activeRole}
          holders={visible.filter((a) => a.role === activeRole)}
          slots={slots}
          history={history}
          loading={historyLoading}
          onSend={(text) => onSendToRole(activeRole, text)}
          onPromote={onPromote}
          onArchive={onArchive}
          onForceArchive={onForceArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          onEdit={onEdit}
          onLog={onLog}
          onSwitchAccount={onSwitchAccount}
          onSwitchModel={onSwitchModel}
        />
      )}
    </Panel>
  );
}

// Message-delivery visibility chip (Plan B, audit Ask 4 — frontend-only, NO
// messaging-layer rewrite). Surfaces the already-served `pending_count` so the
// operator can SEE whether a sent message landed: "queued N" while undelivered,
// "delivered" once the agent has polled and the outbox is empty.
export interface DeliveryChip {
  label: string;
  tone: "queued" | "delivered" | "needs-operator";
  title: string;
}

export function deliveryChip(pendingCount: number, hasHolder: boolean, needsOperatorCount = 0): DeliveryChip | null {
  if (!hasHolder) return null;
  // `needs-operator` OUTRANKS `queued` (ao_dispatch_hardening Phase 2b, 2026-07-16).
  // The server has computed needs_operator_count since 2026-07-08 and NOTHING rendered it —
  // a message redelivered to the cap without a reply means the agent is stuck or dead, and
  // that was invisible unless someone curled the API by hand. It is strictly more urgent
  // than "queued" (which is the healthy waiting-to-poll state), so it wins the chip.
  if (needsOperatorCount > 0) {
    return {
      label: `needs operator ${needsOperatorCount}`,
      tone: "needs-operator",
      title:
        `${needsOperatorCount} message${needsOperatorCount === 1 ? "" : "s"} redelivered to the cap with no reply — ` +
        `this agent is not answering. Check its tmux session; it may be wedged, dead, or out of context.`,
    };
  }
  if (pendingCount > 0) {
    return {
      label: `queued ${pendingCount}`,
      tone: "queued",
      title: `${pendingCount} message${pendingCount === 1 ? "" : "s"} queued — waiting for the agent to /poll`,
    };
  }
  return { label: "delivered", tone: "delivered", title: "Outbox empty — the agent has received all messages" };
}

interface RoleChatProps {
  role: AgentRole;
  holders: AgentView[];
  /** Full slot roster — see AgentsPanelProps.slots. Powers ReservedRoleBadge. */
  slots: SlotView[];
  history: ChatMessage[];
  loading: boolean;
  onSend: (text: string) => Promise<void>;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchive: (agentId: string) => Promise<void>;
  onForceArchive: (agentId: string) => Promise<void>;
  onRestore: (agentId: string) => Promise<void>;
  onDelete: (agent: AgentView) => void;
  onEdit: (agent: AgentView) => void;
  onLog: (agent: AgentView) => void;
  onSwitchAccount: (agent: AgentView) => void;
  onSwitchModel: (agent: AgentView) => void;
}

function RoleChat({
  role,
  holders,
  slots,
  history,
  loading,
  onSend,
  onPromote,
  onArchive,
  onForceArchive,
  onRestore,
  onDelete,
  onEdit,
  onLog,
  onSwitchAccount,
  onSwitchModel,
}: RoleChatProps) {
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length]);

  // ao_dashboard_escalations_and_pending_filter_2026_08_07: a to_agent message
  // with answered_at===null hasn't been /reply-ack'd yet — this is the same
  // "awaiting a reply" signal already computed server-side for the header's
  // deliveryChip (count_pending_to_agent), just applied per-message so the
  // history list itself can be filtered down to it instead of scrolling a
  // 200-message thread to find what's actually still open.
  const displayedHistory = pendingOnly ? history.filter(isPendingToAgentMessage) : history;

  const currentHolder = holders.find((a) => a.status === "active") ?? holders[0];
  // Stranded holders (ao_review_agent_stale_pileup_2026_08_14): currentHolder picks
  // ONE record (active, or an arbitrary holders[0] fallback) — a role with several
  // concurrently-stale records (a crash-looping persistent role respawning repeatedly)
  // leaves the rest invisible here with no way to individually inspect or clean them up
  // short of waiting out reap_orphan_agents' own grace window.
  const otherHolders = holders.filter((h) => h.agent_id !== currentHolder?.agent_id && h.status !== "archived");

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const pendingCount = currentHolder?.pending_count ?? 0;
  const needsOperatorCount = currentHolder?.needs_operator_count ?? 0;

  return (
    <div className="agent-chat">
      <div className="agent-chat-header">
        <span className="agent-chat-identity">
          <span className={`status-dot ${currentHolder?.online ? "working" : "stale"}`} />
          <b style={{ color: "var(--fg-0)" }}>{role}</b>
          {currentHolder && (
            <>
              <span
                className="mono"
                style={{ color: "var(--fg-2)" }}
                title={
                  currentHolder.claude_session_id
                    ? `Claude session: ${currentHolder.claude_session_id}`
                    : currentHolder.agent_id
                }
              >
                {currentHolder.agent_id}
              </span>
              {currentHolder.claude_session_id && (
                <span
                  className="mono"
                  style={{ color: "var(--fg-3)", fontSize: 10 }}
                  title={`claude --resume ${currentHolder.claude_session_id}`}
                >
                  {currentHolder.claude_session_id.slice(0, 8)}…
                </span>
              )}
              {currentHolder.lifecycle && currentHolder.lifecycle !== "persistent" && (
                <span
                  className="status-pill"
                  style={{ background: "var(--bg-3)", color: "var(--fg-3)", fontSize: 10 }}
                  title={
                    currentHolder.lifecycle === "one_shot"
                      ? "lifecycle: one_shot — runs one task then its session exits (expected)"
                      : "lifecycle: scheduled — cron-driven; its session ending is expected"
                  }
                >
                  {currentHolder.lifecycle === "one_shot" ? "1-shot" : "scheduled"}
                </span>
              )}
              <span className={`status-pill status-${currentHolder.status}`}>{currentHolder.status}</span>
              <ReservedRoleBadge role={role} tmuxSession={currentHolder.tmux_session} slots={slots} />
              {currentHolder.last_ping && (
                <span style={{ color: "var(--fg-3)", fontSize: 11 }} title={`last ping ${currentHolder.last_ping}`}>
                  {fmtAgo(currentHolder.last_ping)}
                </span>
              )}
              {(() => {
                const chip = deliveryChip(pendingCount, !!currentHolder, needsOperatorCount);
                if (!chip) return null;
                return (
                  <span
                    className="pending-badge"
                    title={chip.title}
                    style={{
                      background:
                        chip.tone === "needs-operator"
                          ? "var(--status-failed, #b91c1c)"
                          : chip.tone === "queued"
                            ? "var(--status-blocked, #b45309)"
                            : "var(--status-working, #15803d)",
                      color: "#fff",
                      padding: "1px 6px",
                      borderRadius: 8,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {chip.label}
                  </span>
                );
              })()}
            </>
          )}
        </span>
        {currentHolder && (
          <span className="agent-chat-actions">
            {currentHolder.rc_url && (
              <a
                href={currentHolder.rc_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-xs"
                title="Open this agent's session in claude.ai/code"
              >
                ↗ claude.ai/code
              </a>
            )}
            <button
              className="btn btn-xs"
              onClick={() => onEdit(currentHolder)}
              title="Edit label / RC URL / machine / model"
            >
              Edit
            </button>
            <button
              className="btn btn-xs"
              onClick={() => onLog(currentHolder)}
              title="Tail this agent's tmux capture-pane (auto-refreshes every 5s)"
            >
              Log
            </button>
            <button
              className="btn btn-xs"
              onClick={() => onSwitchAccount(currentHolder)}
              title="Emergency lever: force this agent onto a specific account (kill + resume, context intact) — usable from a phone with no laptop/SSH access"
            >
              Switch Account
            </button>
            <button
              className="btn btn-xs"
              onClick={() => onSwitchModel(currentHolder)}
              title="Switch this agent onto a specific model (kill + resume, context intact) — the real lever; Edit's Model field is display-only"
            >
              Switch Model
            </button>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => setPendingOnly((v) => !v)}
              title="Only messages still awaiting this agent's reply — hides the full history's already-answered turns."
              data-testid="role-chat-pending-toggle"
            >
              {pendingOnly ? "Show all" : "Pending only"}
            </button>
            <PromoteMenu agent={currentHolder} currentRole={role} onPromote={onPromote} />
            {currentHolder.status === "archived" ? (
              <button className="btn btn-xs" onClick={() => onRestore(currentHolder.agent_id)}>
                Restore
              </button>
            ) : (
              <button className="btn btn-xs" onClick={() => onArchive(currentHolder.agent_id)}>
                Archive
              </button>
            )}
            <button className="btn btn-xs btn-danger" onClick={() => onDelete(currentHolder)}>
              Delete
            </button>
          </span>
        )}
      </div>

      {otherHolders.length > 0 && (
        <div className="role-chat-stranded" data-testid="role-chat-stranded-holders">
          <span className="role-chat-stranded-label">
            {otherHolders.length} other stranded {otherHolders.length === 1 ? "record" : "records"} for {role}:
          </span>
          {otherHolders.map((h) => (
            <span key={h.agent_id} className="role-chat-stranded-row">
              <span className={`status-pill status-${h.status}`}>{h.status}</span>
              <span className="mono" title={h.agent_id}>
                {h.agent_id}
              </span>
              {h.last_ping && (
                <span style={{ color: "var(--fg-3)", fontSize: 11 }} title={`last ping ${h.last_ping}`}>
                  {fmtAgo(h.last_ping)}
                </span>
              )}
              <button
                className="btn btn-xs"
                onClick={() => onForceArchive(h.agent_id)}
                title="Archive this record now instead of waiting out the auto-heal grace window — refused if its session is still genuinely alive"
              >
                Clean up
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="agent-chat-scroll" ref={scrollRef} data-testid="role-chat-history">
        {loading && history.length === 0 ? (
          <div className="agent-chat-empty">loading…</div>
        ) : history.length === 0 ? (
          <div className="agent-chat-empty">
            No conversation yet for role <b>{role}</b>. Type below to start.
          </div>
        ) : displayedHistory.length === 0 ? (
          <div className="agent-chat-empty">Nothing pending — every message in this thread has been answered.</div>
        ) : (
          displayedHistory.map((m) => <ChatBubble key={m.id} msg={m} />)
        )}
      </div>

      {!currentHolder?.online && (
        <div className="agent-offline-notice">
          {currentHolder
            ? `${currentHolder.label} is offline. Message will queue until it polls again.`
            : `No active ${role} agent. Message will queue for the next one.`}
        </div>
      )}

      <div className="agent-chat-compose">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Message ${role}…`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          disabled={sending}
          data-testid="role-chat-input"
        />
        <button
          className="btn btn-primary btn-sm"
          disabled={sending || !text.trim()}
          onClick={() => void submit()}
          title="Cmd/Ctrl + Enter to send"
          data-testid="role-chat-send"
        >
          <Icon.Send /> {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}

// Short display labels per agent_kind (orchestrator_agent_type_oversight_coverage_
// 2026-06-17). Task-agents register under role="custom", so the kind chip is what
// distinguishes a cicd worker from a plan-reconciler in the custom tab.
const AGENT_KIND_LABEL: Record<string, string> = {
  orchestrator: "orchestrator",
  worker: "worker",
  review: "review",
  cicd: "cicd",
  conflict_resolver: "conflict-resolver",
  quality_gate_resolution: "quality-gate-resolution",
  plan_health: "plan-health",
  plan_reconciler: "plan-reconciler",
  data_pipeline_failure: "data-pipeline-failure",
  ag_closeout_auditor: "ag-closeout-auditor",
  na_eligibility_auditor: "na-eligibility-auditor",
  context_scout_auditor: "context-scout-auditor",
  monitor: "monitor",
  human: "human",
  "planning-human": "human (planning)",
  custom: "custom",
};

// ─── agent types panel (by agent_kind — running + past) ───────────────────
//
// Plan B (audit Ask 1): the role chat (main/review/plan_reconciler) collapses
// persistent agents. This panel is the cross-cut by what the agent DOES —
// cicd / conflict-resolver / plan-health / plan-reconciler / monitor are each
// visible here, current AND past (show-finished toggle pulls retained rows).

// Display order for the kind sections (matches AGENT_KIND_LABEL coverage).
export const KINDS_ORDER: AgentKind[] = [
  "orchestrator",
  "review",
  "cicd",
  "conflict_resolver",
  "quality_gate_resolution",
  "plan_health",
  "plan_reconciler",
  "data_pipeline_failure",
  "ag_closeout_auditor",
  "na_eligibility_auditor",
  "context_scout_auditor",
  "monitor",
  "human",
  "planning-human",
  "worker",
  "custom",
];

export interface AgentKindGroup {
  kind: AgentKind;
  label: string;
  agents: AgentView[];
  onlineCount: number;
}

/** Group agents by `agent_kind` in KINDS_ORDER, newest-registered first within a
 * kind, online rows ahead of offline. Rows with a null kind (pre-migration) fold
 * into "custom". Empty kinds are dropped. Pure → vitest. */
export function groupAgentsByKind(agents: AgentView[]): AgentKindGroup[] {
  const byKind = new Map<AgentKind, AgentView[]>();
  for (const a of agents) {
    const kind: AgentKind = a.agent_kind ?? "custom";
    const bucket = byKind.get(kind);
    if (bucket) bucket.push(a);
    else byKind.set(kind, [a]);
  }
  const groups: AgentKindGroup[] = [];
  for (const kind of KINDS_ORDER) {
    const list = byKind.get(kind);
    if (!list || list.length === 0) continue;
    const sorted = [...list].sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1; // online first
      return b.registered_at.localeCompare(a.registered_at); // newest first
    });
    groups.push({
      kind,
      label: AGENT_KIND_LABEL[kind] ?? kind,
      agents: sorted,
      onlineCount: sorted.filter((a) => a.online).length,
    });
  }
  return groups;
}

/** Derive a ContextPressure band from context% for the agent-types CtxBar. Agents (unlike
 * slots) don't persist context_pressure or compactions/hr, so mirror the server's
 * derive_context_pressure %-bands (high ≥80, medium ≥60, else low). */
function agentCtxPressure(pct: number): ContextPressure {
  if (pct >= 80) return "high";
  if (pct >= 60) return "medium";
  return "low";
}

interface AgentTypesPanelProps {
  agents: AgentView[];
  showFinished: boolean;
  setShowFinished: (v: boolean) => void;
}

export function AgentTypesPanel({ agents, showFinished, setShowFinished }: AgentTypesPanelProps) {
  // Flat table: Type is a COLUMN, not a section head (operator 2026-06-19 — a
  // tabular view: Type · ID · Account · Tmux · Last heartbeat/message · Status).
  // Rows still cluster by kind (KINDS_ORDER) with online-first + newest-first
  // within a kind, by flattening the tested grouping.
  const rows = groupAgentsByKind(agents).flatMap((g) => g.agents);
  const totalOnline = agents.filter((a) => a.online).length;

  return (
    <Panel
      title="Agent types"
      count={`${totalOnline} online`}
      flush
      tone="agents"
      collapsible
      right={
        <button
          className="btn btn-xs btn-ghost"
          onClick={() => setShowFinished(!showFinished)}
          title="Include finished one-shot / scheduled agents (past runs)"
        >
          {showFinished ? "Hide finished" : "Show finished"}
        </button>
      }
    >
      {rows.length === 0 ? (
        <Empty
          title="No agents by type yet"
          sub={
            showFinished
              ? "No running or finished agents recorded."
              : "Escalate / plan-health / conflict-resolver runs appear here. Toggle “Show finished” for past runs."
          }
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="slot-table agent-types-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>ID</th>
                <th>Source</th>
                <th>Task</th>
                <th>Model</th>
                <th style={{ width: 160 }}>Context</th>
                <th>Account</th>
                <th>Tmux</th>
                <th>Last heartbeat / message</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <AgentTypeRow key={a.agent_id} a={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** Type-column text for one AgentTypesPanel row. human/planning-human rows: the
 * generic bucket label ("human"/"human (planning)", correct at the section-grouping
 * level via AGENT_KIND_LABEL) tells the operator nothing about WHO this individual
 * row is — swap in the registered operator's actual name (Ikenna/Harsh, the only two
 * registered — see humanOperatorDisplayName) instead, appending "(planning)" for the
 * planning-time role_group so that distinction isn't lost.
 *
 * ALSO appends "human-fleet slot NNNN" (ao_fleet_regression_triad_2026_08_16
 * Finding 3): a bare operator name here reads as the operator's overall presence
 * on the fleet, but this row is really just ONE opt-in self-registration
 * (config.human_slot_ids()) — a completely separate, older mechanism
 * (per-tab-worktrees.md) covers the operator's ordinary interactive sessions with
 * no dashboard presence at all. Live confirmed 2026-08-16: the operator read a
 * STALE badge on this row as "I've gone quiet," when several other interactive
 * sessions were live the whole time — this row was never designed to represent
 * them. Pure → vitest-covered. */
export function agentTypeRowLabel(kind: string, label: string): string {
  if (kind === "human" || kind === "planning-human") {
    const slotId = humanOperatorSlotId(label);
    const scope = slotId !== null ? `human-fleet slot ${slotId}` : "human-fleet";
    const planning = kind === "planning-human" ? "planning, " : "";
    return `${humanOperatorDisplayName(label)} (${planning}${scope})`;
  }
  return AGENT_KIND_LABEL[kind] ?? kind;
}

/** One agent row in the AgentTypesPanel table. Columns: Type (kind + lifecycle pill) ·
 * ID (agent_id; label as tooltip) · Account (account_id) · Tmux (attach command, click
 * to copy) · Last heartbeat + last message (finished rows show finished_at + exit_reason
 * — audit Ask 1: "past escalate/plan-health runs") · Status. */
function AgentTypeRow({ a }: { a: AgentView }) {
  const finished = a.status === "finished";
  const kind = a.agent_kind ?? "custom";
  const typeLabel = agentTypeRowLabel(kind, a.label);
  return (
    <tr className={`status-${a.status}`} data-testid={`agent-type-row-${a.agent_id}`}>
      {/* Type */}
      <td>
        <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>{typeLabel}</span>
        {a.lifecycle && a.lifecycle !== "persistent" && (
          <span
            className="status-pill"
            style={{ background: "var(--bg-3)", color: "var(--fg-3)", fontSize: 10, marginLeft: 6 }}
            title={`lifecycle: ${a.lifecycle}`}
          >
            {a.lifecycle === "one_shot" ? "1-shot" : "scheduled"}
          </span>
        )}
      </td>
      {/* ID — agent_id (label as tooltip) + claude_session_id for --resume */}
      <td>
        <span className="mono" style={{ color: "var(--fg-1)" }} title={a.label}>
          {a.agent_id}
        </span>
        {a.claude_session_id && (
          <div
            className="mono"
            style={{ color: "var(--fg-3)", fontSize: 10, marginTop: 2 }}
            title={`Claude session: ${a.claude_session_id}`}
          >
            {a.claude_session_id.slice(0, 8)}…
          </div>
        )}
      </td>
      {/* Source — kind-specific context (repo, alert-id, etc.) */}
      <td>
        {a.source ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.source}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Task — what the agent is currently doing */}
      <td>
        {a.current_task ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.current_task}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Model — the claude model this agent runs (parity with the fleet slot view).
          ProviderBadge alongside it, same convention as the Fleet table's model-cell
          above — a bare tier like "sonnet" gave no hint the agent was actually
          running on DeepSeek (deepseek_agent_types_model_column_provider_gap_2026_08_05). */}
      <td>
        {a.model ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.model}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}{" "}
        <ProviderBadge provider={a.provider} />
        <TokenUsageBadge
          inputTokens={a.session_input_tokens}
          outputTokens={a.session_output_tokens}
          cacheCreationTokens={a.session_cache_creation_input_tokens}
          cacheReadTokens={a.session_cache_read_input_tokens}
          spendUsd={a.session_spend_usd}
          reasoningTokens={a.session_reasoning_tokens}
        />
      </td>
      {/* Context — used %, same CtxBar as the fleet. Agents don't persist context_pressure/
          compactions, so the pressure band is derived from the pct (agentCtxPressure). */}
      <td>
        {finished || !agentContextIsReported(a.context_used_pct) ? (
          <span
            style={{ color: "var(--fg-3)" }}
            title={
              finished
                ? undefined
                : "No context measurement reported for this agent — not the same as an empty context. " +
                  "The column shows a reading only when there is one."
            }
          >
            —
          </span>
        ) : (
          <CtxBar
            pct={a.context_used_pct}
            pressure={agentCtxPressure(a.context_used_pct)}
            tokens={a.context_tokens}
            windowTokens={a.context_window_tokens}
          />
        )}
      </td>
      {/* Account */}
      <td>
        {a.account_id ? (
          <span className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>
            {a.account_id}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Tmux attach command — click to copy */}
      <td>
        {a.tmux_session ? (
          <span
            className="mono"
            style={{
              fontSize: 11,
              padding: "1px 5px",
              borderRadius: 3,
              background: "var(--bg-3)",
              color: "var(--fg-2)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            title={`Click to copy. Attach: tmux attach -t ${a.tmux_session}`}
            onClick={() => {
              void navigator.clipboard?.writeText(`tmux attach -t ${a.tmux_session}`);
            }}
          >
            tmux attach -t {a.tmux_session}
          </span>
        ) : (
          <span style={{ color: "var(--fg-3)" }}>—</span>
        )}
      </td>
      {/* Last heartbeat / message */}
      <td>
        <div style={{ color: "var(--fg-3)", fontSize: 11 }}>
          {finished
            ? a.finished_at
              ? `finished ${fmtAgo(a.finished_at)}`
              : "finished"
            : a.last_ping
              ? `${a.online ? "active" : "last seen"} ${fmtAgo(a.last_ping)}`
              : "—"}
        </div>
        {finished && a.exit_reason ? (
          <div className="mono" style={{ color: "var(--fg-2)", fontSize: 11 }} title={a.exit_reason}>
            {a.exit_reason}
          </div>
        ) : (
          a.last_msg && (
            <div
              style={{
                color: "var(--fg-2)",
                fontSize: 12,
                maxWidth: 360,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={a.last_msg}
            >
              {a.last_msg}
            </div>
          )
        )}
      </td>
      {/* Status. human/planning-human rows get a scope-clarifying tooltip on the
          badge (ao_fleet_regression_triad_2026_08_16 Finding 3) — STALE here means
          ONLY that this one opt-in self-registration hasn't heartbeat, never a
          read on the operator's overall activity. */}
      <td>
        <span className="status-cell">
          <span className={`status-dot ${a.online ? "working" : finished ? "idle" : "stale"}`} />
          <span
            className={`status-pill status-${a.status}`}
            title={
              kind === "human" || kind === "planning-human"
                ? "This specific registered session's heartbeat — not a read on the operator's " +
                  "overall activity. The operator may have other live interactive sessions this " +
                  "row has no visibility into."
                : undefined
            }
          >
            {a.status}
          </span>
        </span>
      </td>
    </tr>
  );
}

function PromoteMenu({
  agent,
  currentRole,
  onPromote,
}: {
  agent: AgentView;
  currentRole: AgentRole;
  onPromote: (agentId: string, targetRole: AgentRole) => Promise<void>;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", click);
    return () => window.removeEventListener("mousedown", click);
  }, [open]);
  const targets = ROLES_ORDER.filter((r) => r !== currentRole);
  return (
    <div className="anchor" ref={ref}>
      <button className="btn btn-xs" onClick={() => setOpen((o) => !o)}>
        Promote ▾
      </button>
      {open && (
        <div className="menu-pop" style={{ minWidth: 140 }} role="menu">
          {targets.map((r) => (
            <button
              key={r}
              className="m-row"
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                padding: "6px 10px",
              }}
              onClick={() => {
                setOpen(false);
                void onPromote(agent.agent_id, r);
              }}
            >
              → {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatBubble({ msg }: { msg: ChatMessage }) {
  const isFromAgent = msg.direction === "from_agent";
  const senderShort = msg.from_agent_id ? msg.from_agent_id.split("-").slice(-1)[0].slice(0, 6) : null;
  return (
    <div className={`chat-bubble ${isFromAgent ? "from-agent" : "to-agent"}`}>
      <div className="chat-bubble-head">
        <span className="chat-bubble-role">
          {msg.from_role}
          {senderShort ? ` (${senderShort})` : ""}
        </span>
        <span className="chat-bubble-ts">{fmtAgo(msg.created_at)}</span>
      </div>
      <div className="chat-bubble-body">{msg.content}</div>
    </div>
  );
}

// ─── role registry (read-only) ───────────────────────────────────────────────

/** Read-only catalogue of the orchestrator's agent roles (GET /api/roles). A
 * thin renderer over the pure helpers in utils.ts — no mutation, no actions. */
export function RolesPanel({ roles }: { roles: RoleView[] }) {
  const rows = sortRolesForDisplay(roles);
  const s = summarizeRegistry(roles);
  const subtitle = `${s.total} role${s.total === 1 ? "" : "s"}`;
  return (
    <Panel
      title="Role registry"
      count={subtitle}
      flush
      tone="agents"
      right={
        <span className="meta" style={{ color: "var(--fg-3)", fontSize: 11 }}>
          {s.byModel.opus} opus · {s.byModel.sonnet} sonnet · {s.byModel.haiku} haiku
          {s.unschematized > 0 ? ` · ${s.unschematized} unschematized` : ""}
        </span>
      }
    >
      {rows.length === 0 ? (
        <Empty
          title="No roles registered"
          sub="The backend exposes the agent role catalogue at /api/roles. Nothing returned yet."
        />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="slot-table roles-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Model</th>
                <th>Thinking</th>
                <th>Lifecycle</th>
                <th>Escalates&nbsp;→</th>
                <th>Reports&nbsp;→</th>
                <th>Skills</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <RoleRow key={r.role} r={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <span style={{ color: "var(--fg-3)" }}>{children}</span>;
}

/** One registry row. Model + lifecycle render as small badges; skills as chips;
 * an unschematized role (has_frontmatter=false) gets a muted marker by its name. */
function RoleRow({ r }: { r: RoleView }) {
  return (
    <tr>
      {/* Role name (+ unschematized marker) */}
      <td>
        <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>{r.role}</span>
        {!r.has_frontmatter && (
          <span
            className="meta"
            style={{ color: "var(--fg-3)", fontSize: 10.5, marginLeft: 6 }}
            title="No parsed schema block in this role's prompt file."
          >
            (unschematized)
          </span>
        )}
        {r.temperament_base && (
          <div className="meta" style={{ color: "var(--fg-3)", fontSize: 10.5, marginTop: 2 }}>
            {r.temperament_base}
          </div>
        )}
      </td>
      {/* Model badge */}
      <td>
        <span className={modelBadgeClass(r.model)}>{r.model}</span>
      </td>
      {/* Thinking budget */}
      <td>
        {r.thinking ? (
          <span className="mono" style={{ fontSize: 11.5 }}>
            {r.thinking}
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </td>
      {/* Lifecycle badge */}
      <td>
        <span className="status-pill" title={`lifecycle: ${r.lifecycle}`}>
          {r.lifecycle === "one_shot" ? "1-shot" : r.lifecycle}
        </span>
      </td>
      {/* Escalation target */}
      <td>
        {r.escalation_to ? (
          <span className="mono" style={{ fontSize: 11.5 }}>
            {r.escalation_to}
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </td>
      {/* Reports-to */}
      <td>
        {r.reports_to ? (
          <span className="mono" style={{ fontSize: 11.5 }}>
            {r.reports_to}
          </span>
        ) : (
          <Muted>—</Muted>
        )}
      </td>
      {/* Skills as chips */}
      <td>
        {r.skills.length === 0 ? (
          <Muted>—</Muted>
        ) : (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {r.skills.map((skill) => (
              <span key={skill} className="role-skill-chip mono">
                {skill}
              </span>
            ))}
          </span>
        )}
      </td>
    </tr>
  );
}
